import { macroCondition, getOwnConfig, isDevelopingApp, dependencySatisfies, importSync } from '@embroider/macros';
import { dasherize } from '@ember/string';
import { getOwner, setOwner } from '@ember/application';
import { _backburner } from '@ember/runloop';
import Service from '@ember/service';
import { registerWaiter, unregisterWaiter } from '@ember/test';
import RSVP, { resolve, Promise as Promise$1, reject } from 'rsvp';
import { tracked } from '@glimmer/tracking';
import { subscribe, addToTransaction, addTransactionCB } from '@ember-data/tracking/-private';
import { tagForProperty } from '@ember/-internals/metal';
import { get, set } from '@ember/object';
import { dependentKeyCompat } from '@ember/object/compat';
import { compare } from '@ember/utils';
import { dirtyTag } from '@glimmer/validator';
import Ember from 'ember';
import { reads } from '@ember/object/computed';
import ArrayProxy from '@ember/array/proxy';
import PromiseProxyMixin from '@ember/object/promise-proxy-mixin';
import ObjectProxy from '@ember/object/proxy';

/**
  @module @ember-data/store
*/

/**
 This method normalizes a modelName into the format Ember Data uses
 internally by dasherizing it.

  @method normalizeModelName
  @static
  @public
  @deprecated
  @for @ember-data/store
  @param {String} modelName
  @return {String} normalizedModelName
*/
function normalizeModelName$1(modelName) {
  return dasherize(modelName);
}

/**
  @module @ember-data/store
*/

// Used by the store to normalize IDs entering the store.  Despite the fact
// that developers may provide IDs as numbers (e.g., `store.findRecord('person', 1)`),
// it is important that internally we use strings, since IDs may be serialized
// and lose type information.  For example, Ember's router may put a record's
// ID into the URL, and if we later try to deserialize that URL and find the
// corresponding record, we will not know if it is a string or a number.

function coerceId(id) {
  if (id === null || id === undefined || id === '') {
    return null;
  }
  if (typeof id === 'string') {
    return id;
  }
  if (typeof id === 'symbol') {
    return id.toString();
  }
  return '' + id;
}
function ensureStringId(id) {
  let normalized = null;
  if (typeof id === 'string') {
    normalized = id.length > 0 ? id : null;
  } else if (typeof id === 'number' && !isNaN(id)) {
    normalized = '' + id;
  }
  if (normalized === null) {
    throw new Error(`Expected id to be a string or number, received ${String(id)}`);
  }
  return normalized;
}

// provided for additional debuggability
const DEBUG_CLIENT_ORIGINATED = Symbol('record-originated-on-client');
const DEBUG_IDENTIFIER_BUCKET = Symbol('identifier-bucket');
function isNonEmptyString(str) {
  return str && typeof str === 'string';
}

/**
  @module @ember-data/store
*/

function installPolyfill() {
  const isFastBoot = typeof FastBoot !== 'undefined';
  const CRYPTO = isFastBoot ? FastBoot.require('crypto') : window.crypto;
  if (!CRYPTO.randomUUID) {
    // we might be able to optimize this by requesting more bytes than we need at a time
    const rng = function () {
      // WHATWG crypto RNG - http://wiki.whatwg.org/wiki/Crypto
      let rnds8 = new Uint8Array(16);
      if (!CRYPTO.getRandomValues && !isFastBoot) {
        throw new Error(`Unable to generate bytes for UUID`);
      }
      return CRYPTO.getRandomValues ? CRYPTO.getRandomValues(rnds8) : CRYPTO.randomFillSync(rnds8);
    };

    /*
     * Convert array of 16 byte values to UUID string format of the form:
     * XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX
     */
    const byteToHex = [];
    for (let i = 0; i < 256; ++i) {
      byteToHex[i] = (i + 0x100).toString(16).substr(1);
    }
    const bytesToUuid = function (buf) {
      let bth = byteToHex;
      // join used to fix memory issue caused by concatenation: https://bugs.chromium.org/p/v8/issues/detail?id=3175#c4
      return [bth[buf[0]], bth[buf[1]], bth[buf[2]], bth[buf[3]], '-', bth[buf[4]], bth[buf[5]], '-', bth[buf[6]], bth[buf[7]], '-', bth[buf[8]], bth[buf[9]], '-', bth[buf[10]], bth[buf[11]], bth[buf[12]], bth[buf[13]], bth[buf[14]], bth[buf[15]]].join('');
    };
    CRYPTO.randomUUID = function uuidv4() {
      let rnds = rng();

      // Per 4.4, set bits for version and `clock_seq_hi_and_reserved`
      rnds[6] = rnds[6] & 0x0f | 0x40;
      rnds[8] = rnds[8] & 0x3f | 0x80;
      return bytesToUuid(rnds);
    };
  }
}

/**
  @module @ember-data/store
*/
const IDENTIFIERS = new Set();
function isStableIdentifier(identifier) {
  return IDENTIFIERS.has(identifier);
}
const isFastBoot = typeof FastBoot !== 'undefined';
const _crypto = isFastBoot ? FastBoot.require('crypto') : window.crypto;
if (macroCondition(getOwnConfig().polyfillUUID)) {
  installPolyfill();
}
function uuidv4() {
  return _crypto.randomUUID();
}
function freeze$1(obj) {
  if (typeof Object.freeze === 'function') {
    return Object.freeze(obj);
  }
  return obj;
}
let configuredForgetMethod;
let configuredGenerationMethod;
let configuredResetMethod;
let configuredUpdateMethod;
function setIdentifierGenerationMethod(method) {
  configuredGenerationMethod = method;
}
function setIdentifierUpdateMethod(method) {
  configuredUpdateMethod = method;
}
function setIdentifierForgetMethod(method) {
  configuredForgetMethod = method;
}
function setIdentifierResetMethod(method) {
  configuredResetMethod = method;
}
function defaultGenerationMethod(data, bucket) {
  if (isNonEmptyString(data.lid)) {
    return data.lid;
  }
  if (data.id !== undefined) {
    let {
      type,
      id
    } = data;
    // TODO: add test for id not a string
    if (isNonEmptyString(coerceId(id))) {
      return `@lid:${normalizeModelName$1(type)}-${id}`;
    }
  }
  return uuidv4();
}
function defaultEmptyCallback() {}
let DEBUG_MAP;
if (macroCondition(isDevelopingApp())) {
  DEBUG_MAP = new WeakMap();
}

/**
 * Each instance of {Store} receives a unique instance of a IdentifierCache.
 *
 * This cache is responsible for assigning or retrieving the unique identify
 * for arbitrary resource data encountered by the store. Data representing
 * a unique resource or record should always be represented by the same
 * identifier.
 *
 * It can be configured by consuming applications.
 *
 * @class IdentifierCache
   @public
 */
class IdentifierCache {
  constructor() {
    this._cache = {
      lids: new Map(),
      types: Object.create(null)
    };
    // we cache the user configuredGenerationMethod at init because it must
    // be configured prior and is not allowed to be changed
    this._generate = configuredGenerationMethod || defaultGenerationMethod;
    this._update = configuredUpdateMethod || defaultEmptyCallback;
    this._forget = configuredForgetMethod || defaultEmptyCallback;
    this._reset = configuredResetMethod || defaultEmptyCallback;
    this._merge = defaultEmptyCallback;
    this._isDefaultConfig = !configuredGenerationMethod;
  }

  /**
   * Internal hook to allow management of merge conflicts with identifiers.
   *
   * we allow late binding of this private internal merge so that
   * the cache can insert itself here to handle elimination of duplicates
   *
   * @method __configureMerge
   * @private
   */
  __configureMerge(method) {
    this._merge = method || defaultEmptyCallback;
  }

  /**
   * @method _getRecordIdentifier
   * @private
   */

  _getRecordIdentifier(resource, shouldGenerate) {
    if (shouldGenerate === void 0) {
      shouldGenerate = false;
    }
    // short circuit if we're already the stable version
    if (isStableIdentifier(resource)) {
      if (macroCondition(isDevelopingApp())) {
        // TODO should we instead just treat this case as a new generation skipping the short circuit?
        if (!this._cache.lids.has(resource.lid) || this._cache.lids.get(resource.lid) !== resource) {
          throw new Error(`The supplied identifier ${resource} does not belong to this store instance`);
        }
      }
      if (macroCondition(getOwnConfig().debug.LOG_IDENTIFIERS)) {
        // eslint-disable-next-line no-console
        console.log(`Identifiers: Peeked Identifier was already Stable ${String(resource)}`);
      }
      return resource;
    }
    let lid = coerceId(resource.lid);
    let identifier = lid !== null ? this._cache.lids.get(lid) : undefined;
    if (identifier !== undefined) {
      if (macroCondition(getOwnConfig().debug.LOG_IDENTIFIERS)) {
        // eslint-disable-next-line no-console
        console.log(`Identifiers: cache HIT ${identifier}`, resource);
      }
      return identifier;
    }
    if (macroCondition(getOwnConfig().debug.LOG_IDENTIFIERS)) {
      // eslint-disable-next-line no-console
      console.groupCollapsed(`Identifiers: ${shouldGenerate ? 'Generating' : 'Peeking'} Identifier`, resource);
    }
    if (shouldGenerate === false) {
      if (!resource.type || !resource.id) {
        return;
      }
    }
    let type = resource.type && normalizeModelName$1(resource.type);
    let id = coerceId(resource.id);
    let keyOptions = getTypeIndex(this._cache.types, type);

    // go straight for the stable RecordIdentifier key'd to `lid`
    if (lid !== null) {
      identifier = keyOptions.lid.get(lid);
    }

    // we may have not seen this resource before
    // but just in case we check our own secondary lookup (`id`)
    if (identifier === undefined && id !== null) {
      identifier = keyOptions.id.get(id);
    }
    if (identifier === undefined) {
      // we have definitely not seen this resource before
      // so we allow the user configured `GenerationMethod` to tell us
      let newLid = this._generate(resource, 'record');
      if (macroCondition(getOwnConfig().debug.LOG_IDENTIFIERS)) {
        // eslint-disable-next-line no-console
        console.log(`Identifiers: lid ${newLid} determined for resource`, resource);
      }

      // we do this _even_ when `lid` is present because secondary lookups
      // may need to be populated, but we enforce not giving us something
      // different than expected
      if (lid !== null && newLid !== lid) {
        throw new Error(`You should not change the <lid> of a RecordIdentifier`);
      } else if (lid === null && !this._isDefaultConfig) {
        // allow configuration to tell us that we have
        // seen this `lid` before. E.g. a secondary lookup
        // connects this resource to a previously seen
        // resource.
        identifier = keyOptions.lid.get(newLid);
      }
      if (shouldGenerate === true) {
        if (identifier === undefined) {
          // if we still don't have an identifier, time to generate one
          identifier = makeStableRecordIdentifier(id, type, newLid, 'record', false);

          // populate our unique table
          if (macroCondition(isDevelopingApp())) {
            // realistically if you hit this it means you changed `type` :/
            // TODO consider how to handle type change assertions more gracefully
            if (this._cache.lids.has(identifier.lid)) {
              throw new Error(`You should not change the <type> of a RecordIdentifier`);
            }
          }
          this._cache.lids.set(identifier.lid, identifier);

          // populate our primary lookup table
          // TODO consider having the `lid` cache be
          // one level up
          keyOptions.lid.set(identifier.lid, identifier);
          if (macroCondition(getOwnConfig().debug.LOG_IDENTIFIERS)) {
            if (shouldGenerate) {
              // eslint-disable-next-line no-console
              console.log(`Identifiers: generated ${String(identifier)} for`, resource);
              if (resource[DEBUG_IDENTIFIER_BUCKET]) {
                // eslint-disable-next-line no-console
                console.trace(`[WARNING] Identifiers: generated a new identifier from a previously used identifier. This is likely a bug.`);
              }
            }
          }
        }

        // populate our own secondary lookup table
        // even for the "successful" secondary lookup
        // by `_generate()`, since we missed the cache
        // previously
        // we use identifier.id instead of id here
        // because they may not match and we prefer
        // what we've set via resource data
        if (identifier.id !== null) {
          keyOptions.id.set(identifier.id, identifier);

          // TODO allow filling out of `id` here
          // for the `username` non-client created
          // case.
        }
      }
    }

    if (macroCondition(getOwnConfig().debug.LOG_IDENTIFIERS)) {
      if (!identifier && !shouldGenerate) {
        // eslint-disable-next-line no-console
        console.log(`Identifiers: cache MISS`, resource);
      }
      // eslint-disable-next-line no-console
      console.groupEnd();
    }
    return identifier;
  }

  /**
   * allows us to peek without generating when needed
   * useful for the "create" case when we need to see if
   * we are accidentally overwritting something
   *
   * @method peekRecordIdentifier
   * @param resource
   * @returns {StableRecordIdentifier | undefined}
   * @private
   */
  peekRecordIdentifier(resource) {
    return this._getRecordIdentifier(resource, false);
  }

  /**
    Returns the Identifier for the given Resource, creates one if it does not yet exist.
     Specifically this means that we:
     - validate the `id` `type` and `lid` combo against known identifiers
    - return an object with an `lid` that is stable (repeated calls with the same
      `id` + `type` or `lid` will return the same `lid` value)
    - this referential stability of the object itself is guaranteed
     @method getOrCreateRecordIdentifier
    @param resource
    @returns {StableRecordIdentifier}
    @public
  */
  getOrCreateRecordIdentifier(resource) {
    return this._getRecordIdentifier(resource, true);
  }

  /**
   Returns a new Identifier for the supplied data. Call this method to generate
   an identifier when a new resource is being created local to the client and
   potentially does not have an `id`.
    Delegates generation to the user supplied `GenerateMethod` if one has been provided
   with the signature `generateMethod({ type }, 'record')`.
    @method createIdentifierForNewRecord
   @param data
   @returns {StableRecordIdentifier}
   @public
  */
  createIdentifierForNewRecord(data) {
    let newLid = this._generate(data, 'record');
    let identifier = makeStableRecordIdentifier(data.id || null, data.type, newLid, 'record', true);
    let keyOptions = getTypeIndex(this._cache.types, data.type);

    // populate our unique table
    if (macroCondition(isDevelopingApp())) {
      if (this._cache.lids.has(identifier.lid)) {
        throw new Error(`The lid generated for the new record is not unique as it matches an existing identifier`);
      }
    }
    this._cache.lids.set(identifier.lid, identifier);

    // populate the type+lid cache
    keyOptions.lid.set(newLid, identifier);
    if (data.id) {
      keyOptions.id.set(data.id, identifier);
    }
    if (macroCondition(getOwnConfig().debug.LOG_IDENTIFIERS)) {
      // eslint-disable-next-line no-console
      console.log(`Identifiers: createded identifier ${String(identifier)} for newly generated resource`, data);
    }
    return identifier;
  }

  /**
   Provides the opportunity to update secondary lookup tables for existing identifiers
   Called after an identifier created with `createIdentifierForNewRecord` has been
   committed.
    Assigned `id` to an `Identifier` if `id` has not previously existed; however,
   attempting to change the `id` or calling update without providing an `id` when
   one is missing will throw an error.
     - sets `id` (if `id` was previously `null`)
    - `lid` and `type` MUST NOT be altered post creation
     If a merge occurs, it is possible the returned identifier does not match the originally
    provided identifier. In this case the abandoned identifier will go through the usual
    `forgetRecordIdentifier` codepaths.
     @method updateRecordIdentifier
    @param identifierObject
    @param data
    @returns {StableRecordIdentifier}
    @public
  */
  updateRecordIdentifier(identifierObject, data) {
    let identifier = this.getOrCreateRecordIdentifier(identifierObject);
    let newId = data.id !== undefined ? coerceId(data.id) : null;
    let existingIdentifier = detectMerge(this._cache.types, identifier, data, newId, this._cache.lids);
    if (!existingIdentifier) {
      // If the incoming type does not match the identifier type, we need to create an identifier for the incoming
      // data so we can merge the incoming data with the existing identifier, see #7325 and #7363
      if (data.type && identifier.type !== normalizeModelName$1(data.type)) {
        let incomingDataResource = {
          ...data
        };
        // Need to strip the lid from the incomingData in order force a new identifier creation
        delete incomingDataResource.lid;
        existingIdentifier = this.getOrCreateRecordIdentifier(incomingDataResource);
      }
    }
    if (existingIdentifier) {
      let keyOptions = getTypeIndex(this._cache.types, identifier.type);
      let generatedIdentifier = identifier;
      identifier = this._mergeRecordIdentifiers(keyOptions, generatedIdentifier, existingIdentifier, data, newId);
      if (macroCondition(getOwnConfig().debug.LOG_IDENTIFIERS)) {
        // eslint-disable-next-line no-console
        console.log(`Identifiers: merged identifiers ${generatedIdentifier.lid} and ${existingIdentifier.lid} for resource into ${identifier.lid}`, data);
      }
    }
    let id = identifier.id;
    performRecordIdentifierUpdate(identifier, data, this._update);
    newId = identifier.id;

    // add to our own secondary lookup table
    if (id !== newId && newId !== null) {
      if (macroCondition(getOwnConfig().debug.LOG_IDENTIFIERS)) {
        // eslint-disable-next-line no-console
        console.log(`Identifiers: updated id for identifier ${identifier.lid} from '${id}' to '${newId}' for resource`, data);
      }
      let keyOptions = getTypeIndex(this._cache.types, identifier.type);
      keyOptions.id.set(newId, identifier);
      if (id !== null) {
        keyOptions.id.delete(id);
      }
    } else if (macroCondition(getOwnConfig().debug.LOG_IDENTIFIERS)) {
      // eslint-disable-next-line no-console
      console.log(`Identifiers: updated identifier ${identifier.lid} resource`, data);
    }
    return identifier;
  }

  /**
   * @method _mergeRecordIdentifiers
   * @private
   */
  _mergeRecordIdentifiers(keyOptions, identifier, existingIdentifier, data, newId) {
    // delegate determining which identifier to keep to the configured MergeMethod
    let kept = this._merge(identifier, existingIdentifier, data);
    let abandoned = kept === identifier ? existingIdentifier : identifier;

    // cleanup the identifier we no longer need
    this.forgetRecordIdentifier(abandoned);

    // ensure a secondary cache entry for this id for the identifier we do keep
    keyOptions.id.set(newId, kept);
    // ensure a secondary cache entry for this id for the abandoned identifier's type we do keep
    let baseKeyOptions = getTypeIndex(this._cache.types, existingIdentifier.type);
    baseKeyOptions.id.set(newId, kept);

    // make sure that the `lid` on the data we are processing matches the lid we kept
    data.lid = kept.lid;
    return kept;
  }

  /**
   Provides the opportunity to eliminate an identifier from secondary lookup tables
   as well as eliminates it from ember-data's own lookup tables and book keeping.
    Useful when a record has been deleted and the deletion has been persisted and
   we do not care about the record anymore. Especially useful when an `id` of a
   deleted record might be reused later for a new record.
    @method forgetRecordIdentifier
   @param identifierObject
   @public
  */
  forgetRecordIdentifier(identifierObject) {
    let identifier = this.getOrCreateRecordIdentifier(identifierObject);
    let keyOptions = getTypeIndex(this._cache.types, identifier.type);
    if (identifier.id !== null) {
      keyOptions.id.delete(identifier.id);
    }
    this._cache.lids.delete(identifier.lid);
    keyOptions.lid.delete(identifier.lid);
    IDENTIFIERS.delete(identifierObject);
    this._forget(identifier, 'record');
    if (macroCondition(getOwnConfig().debug.LOG_IDENTIFIERS)) {
      // eslint-disable-next-line no-console
      console.log(`Identifiers: released identifier ${identifierObject.lid}`);
    }
  }
  destroy() {
    this._reset();
  }
}
function getTypeIndex(typeMap, type) {
  let typeIndex = typeMap[type];
  if (typeIndex === undefined) {
    typeIndex = {
      lid: new Map(),
      id: new Map()
    };
    typeMap[type] = typeIndex;
  }
  return typeIndex;
}
function makeStableRecordIdentifier(id, type, lid, bucket, clientOriginated) {
  if (clientOriginated === void 0) {
    clientOriginated = false;
  }
  let recordIdentifier = {
    lid,
    id,
    type
  };
  IDENTIFIERS.add(recordIdentifier);
  if (macroCondition(isDevelopingApp())) {
    // we enforce immutability in dev
    //  but preserve our ability to do controlled updates to the reference
    let wrapper = {
      get lid() {
        return recordIdentifier.lid;
      },
      get id() {
        return recordIdentifier.id;
      },
      get type() {
        return recordIdentifier.type;
      },
      toString() {
        let {
          type,
          id,
          lid
        } = recordIdentifier;
        return `${clientOriginated ? '[CLIENT_ORIGINATED] ' : ''}${type}:${id} (${lid})`;
      },
      toJSON() {
        let {
          type,
          id,
          lid
        } = recordIdentifier;
        return {
          type,
          id,
          lid
        };
      }
    };
    wrapper[DEBUG_CLIENT_ORIGINATED] = clientOriginated;
    wrapper[DEBUG_IDENTIFIER_BUCKET] = bucket;
    IDENTIFIERS.add(wrapper);
    DEBUG_MAP.set(wrapper, recordIdentifier);
    wrapper = freeze$1(wrapper);
    return wrapper;
  }
  return recordIdentifier;
}
function performRecordIdentifierUpdate(identifier, data, updateFn) {
  if (macroCondition(isDevelopingApp())) {
    let {
      lid
    } = data;
    let id = 'id' in data ? data.id : undefined;
    let type = 'type' in data && data.type && normalizeModelName$1(data.type);

    // get the mutable instance behind our proxy wrapper
    let wrapper = identifier;
    identifier = DEBUG_MAP.get(wrapper);
    if (lid !== undefined) {
      let newLid = coerceId(lid);
      if (newLid !== identifier.lid) {
        throw new Error(`The 'lid' for a RecordIdentifier cannot be updated once it has been created. Attempted to set lid for '${wrapper}' to '${lid}'.`);
      }
    }
    if (id !== undefined) {
      let newId = coerceId(id);
      if (identifier.id !== null && identifier.id !== newId) ;
    }

    // TODO consider just ignoring here to allow flexible polymorphic support
    if (type && type !== identifier.type) {
      throw new Error(`The 'type' for a RecordIdentifier cannot be updated once it has been set. Attempted to set type for '${wrapper}' to '${type}'.`);
    }
    updateFn(wrapper, data, 'record');
  } else {
    updateFn(identifier, data, 'record');
  }

  // upgrade the ID, this is a "one time only" ability
  // for the multiple-cache-key scenario we "could"
  // use a heuristic to guess the best id for display
  // (usually when `data.id` is available and `data.attributes` is not)
  if (data.id !== undefined) {
    identifier.id = coerceId(data.id);
  }
}
function detectMerge(typesCache, identifier, data, newId, lids) {
  const {
    id,
    type,
    lid
  } = identifier;
  if (id !== null && id !== newId && newId !== null) {
    let keyOptions = getTypeIndex(typesCache, identifier.type);
    let existingIdentifier = keyOptions.id.get(newId);
    return existingIdentifier !== undefined ? existingIdentifier : false;
  } else {
    let newType = data.type && normalizeModelName$1(data.type);

    // If the ids and type are the same but lid is not the same, we should trigger a merge of the identifiers
    if (id !== null && id === newId && newType === type && data.lid && data.lid !== lid) {
      let existingIdentifier = lids.get(data.lid);
      return existingIdentifier !== undefined ? existingIdentifier : false;
      // If the lids are the same, and ids are the same, but types are different we should trigger a merge of the identifiers
    } else if (id !== null && id === newId && newType && newType !== type && data.lid && data.lid === lid) {
      let keyOptions = getTypeIndex(typesCache, newType);
      let existingIdentifier = keyOptions.id.get(id);
      return existingIdentifier !== undefined ? existingIdentifier : false;
    }
  }
  return false;
}
let tokenId = 0;
const Cache = new Map();
const Tokens = new Map();
// TODO this isn't importable anyway, remove and use a map on the manager?
function unsubscribe(token) {
  let identifier = Tokens.get(token);
  if (macroCondition(getOwnConfig().debug.LOG_NOTIFICATIONS)) {
    if (!identifier) {
      // eslint-disable-next-line no-console
      console.log('Passed unknown unsubscribe token to unsubscribe', identifier);
    }
  }
  if (identifier) {
    Tokens.delete(token);
    const map = Cache.get(identifier);
    map?.delete(token);
  }
}
/*
  Currently only support a single callback per identifier
*/
class NotificationManager {
  constructor(store) {
    this.store = store;
    this.isDestroyed = false;
  }
  subscribe(identifier, callback) {
    let map = Cache.get(identifier);
    if (!map) {
      map = new Map();
      Cache.set(identifier, map);
    }
    let unsubToken = macroCondition(isDevelopingApp()) ? {
      _tokenRef: tokenId++
    } : {};
    map.set(unsubToken, callback);
    Tokens.set(unsubToken, identifier);
    return unsubToken;
  }
  unsubscribe(token) {
    if (!this.isDestroyed) {
      unsubscribe(token);
    }
  }

  // deactivated type signature overloads because pass-through was failing to match any. Bring back if possible.
  // notify(identifier: StableRecordIdentifier, value: 'attributes' | 'relationships', key?: string): boolean;
  // notify(identifier: StableRecordIdentifier, value: 'errors' | 'meta' | 'identity' | 'state'): boolean;
  notify(identifier, value, key) {
    if (!isStableIdentifier(identifier)) {
      if (macroCondition(getOwnConfig().debug.LOG_NOTIFICATIONS)) {
        // eslint-disable-next-line no-console
        console.log(`Notifying: Expected to receive a stable Identifier to notify '${value}' '${key}' with, but ${String(identifier)} is not in the cache`, identifier);
      }
      return false;
    }
    if (macroCondition(getOwnConfig().debug.LOG_NOTIFICATIONS)) {
      // eslint-disable-next-line no-console
      console.log(`Notifying: ${String(identifier)}\t${value}\t${key}`);
    }
    let callbackMap = Cache.get(identifier);
    if (!callbackMap || !callbackMap.size) {
      return false;
    }
    callbackMap.forEach(cb => {
      cb(identifier, value, key);
    });
    return true;
  }
  destroy() {
    this.isDestroyed = true;
    Tokens.clear();
    Cache.clear();
  }
}
var _class$2, _descriptor$2;
function _initializerDefineProperty$2(target, property, descriptor, context) {
  if (!descriptor) return;
  Object.defineProperty(target, property, {
    enumerable: descriptor.enumerable,
    configurable: descriptor.configurable,
    writable: descriptor.writable,
    value: descriptor.initializer ? descriptor.initializer.call(context) : void 0
  });
}
function _applyDecoratedDescriptor$2(target, property, decorators, descriptor, context) {
  var desc = {};
  Object.keys(descriptor).forEach(function (key) {
    desc[key] = descriptor[key];
  });
  desc.enumerable = !!desc.enumerable;
  desc.configurable = !!desc.configurable;
  if ('value' in desc || desc.initializer) {
    desc.writable = true;
  }
  desc = decorators.slice().reverse().reduce(function (desc, decorator) {
    return decorator(target, property, desc) || desc;
  }, desc);
  if (context && desc.initializer !== void 0) {
    desc.value = desc.initializer ? desc.initializer.call(context) : void 0;
    desc.initializer = undefined;
  }
  if (desc.initializer === void 0) {
    Object.defineProperty(target, property, desc);
    desc = null;
  }
  return desc;
}
/**
  @module @ember-data/store
*/
/**
   A `RecordReference` is a low-level API that allows users and
   addon authors to perform meta-operations on a record.

   @class RecordReference
   @public
   @extends Reference
*/
let RecordReference = (_class$2 = class RecordReference {
  constructor(store, identifier) {
    // unsubscribe token given to us by the notification manager
    this.___token = void 0;
    this.___identifier = void 0;
    _initializerDefineProperty$2(this, "_ref", _descriptor$2, this);
    this.store = store;
    this.___identifier = identifier;
    this.___token = store._notificationManager.subscribe(identifier, (_, bucket, notifiedKey) => {
      if (bucket === 'identity' || bucket === 'attributes' && notifiedKey === 'id') {
        this._ref++;
      }
    });
  }
  destroy() {
    unsubscribe(this.___token);
  }
  get type() {
    return this.identifier().type;
  }

  /**
     The `id` of the record that this reference refers to.
      Together, the `type` and `id` properties form a composite key for
     the identity map.
      Example
      ```javascript
     let userRef = store.getReference('user', 1);
      userRef.id(); // '1'
     ```
      @method id
    @public
     @return {String} The id of the record.
  */
  id() {
    this._ref; // consume the tracked prop
    return this.___identifier.id;
  }

  /**
     The `identifier` of the record that this reference refers to.
      Together, the `type` and `id` properties form a composite key for
     the identity map.
      Example
      ```javascript
     let userRef = store.getReference('user', 1);
      userRef.identifier(); // '1'
     ```
      @method identifier
    @public
     @return {String} The identifier of the record.
  */
  identifier() {
    return this.___identifier;
  }

  /**
     How the reference will be looked up when it is loaded. Currently
     this always returns `identity` to signify that a record will be
     loaded by its `type` and `id`.
      Example
      ```javascript
     const userRef = store.getReference('user', 1);
      userRef.remoteType(); // 'identity'
     ```
      @method remoteType
     @public
     @return {String} 'identity'
  */
  remoteType() {
    return 'identity';
  }

  /**
    This API allows you to provide a reference with new data. The
    simplest usage of this API is similar to `store.push`: you provide a
    normalized hash of data and the object represented by the reference
    will update.
     If you pass a promise to `push`, Ember Data will not ask the adapter
    for the data if another attempt to fetch it is made in the
    interim. When the promise resolves, the underlying object is updated
    with the new data, and the promise returned by *this function* is resolved
    with that object.
     For example, `recordReference.push(promise)` will be resolved with a
    record.
      Example
      ```javascript
     let userRef = store.getReference('user', 1);
      // provide data for reference
     userRef.push({
       data: {
         id: "1",
         type: "user",
         attributes: {
           username: "@user"
         }
       }
     }).then(function(user) {
       userRef.value() === user;
     });
     ```
     @method push
    @public
    @param objectOrPromise a JSON:API ResourceDocument or a promise resolving to one
    @return a promise for the value (record or relationship)
  */
  push(objectOrPromise) {
    // TODO @deprecate pushing unresolved payloads
    return resolve(objectOrPromise).then(data => {
      return this.store.push(data);
    });
  }

  /**
    If the entity referred to by the reference is already loaded, it is
    present as `reference.value`. Otherwise the value returned by this function
    is `null`.
      Example
      ```javascript
     let userRef = store.getReference('user', 1);
      userRef.value(); // user
     ```
      @method value
    @public
     @return {Model} the record for this RecordReference
  */
  value() {
    return this.store.peekRecord(this.___identifier);
  }

  /**
     Triggers a fetch for the backing entity based on its `remoteType`
     (see `remoteType` definitions per reference type).
      Example
      ```javascript
     let userRef = store.getReference('user', 1);
      // load user (via store.find)
     userRef.load().then(...)
     ```
      @method load
    @public
     @return {Promise<record>} the record for this RecordReference
  */
  load() {
    const id = this.id();
    if (id !== null) {
      return this.store.findRecord(this.type, id);
    }
  }

  /**
     Reloads the record if it is already loaded. If the record is not
     loaded it will load the record via `store.findRecord`
      Example
      ```javascript
     let userRef = store.getReference('user', 1);
      // or trigger a reload
     userRef.reload().then(...)
     ```
      @method reload
    @public
     @return {Promise<record>} the record for this RecordReference
  */
  reload() {
    const id = this.id();
    if (id !== null) {
      return this.store.findRecord(this.type, id, {
        reload: true
      });
    }
  }
}, _descriptor$2 = _applyDecoratedDescriptor$2(_class$2.prototype, "_ref", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 0;
  }
}), _class$2);
function _classPrivateFieldLooseBase(receiver, privateKey) {
  if (!Object.prototype.hasOwnProperty.call(receiver, privateKey)) {
    throw new TypeError("attempted to use private field on non-instance");
  }
  return receiver;
}
var id = 0;
function _classPrivateFieldLooseKey(name) {
  return "__private_" + id++ + "_" + name;
}
var _store = /*#__PURE__*/_classPrivateFieldLooseKey("store");
var _recordData = /*#__PURE__*/_classPrivateFieldLooseKey("recordData");
var _identifier = /*#__PURE__*/_classPrivateFieldLooseKey("identifier");
var _isDeprecated = /*#__PURE__*/_classPrivateFieldLooseKey("isDeprecated");
/**
 * The RecordDataManager wraps a RecordData cache
 * enforcing that only the public API surface area
 * is exposed.
 *
 * This class is the the return value of both the
 * `recordDataFor` function supplied to the store
 * hook `instantiateRecord`, and the `recordDataFor`
 * method on the `RecordDataStoreWrapper`. It is not
 * directly instantiatable.
 *
 * It handles translating between cache versions when
 * necessary, for instance when a Store is configured
 * to use both a v1 and a v2 cache depending on some
 * heuristic.
 *
 * Starting with the v2 spec, the cache is designed such
 * that it may be implemented as a singleton. However,
 * because the v1 spec was not designed for this whenever
 * we encounter any v1 cache we must wrap all caches, even
 * singletons, in non-singleton managers to preserve v1
 * compatibility.
 *
 * To avoid this performance penalty being paid by all
 * applications, singleton behavior may be opted-in via
 * the configuration supplied to your Ember application
 * at build time. This effectively removes support for
 * v1 caches.
 *
 * ```js
 * let app = new EmberApp(defaults, {
 *   emberData: {
 *     useSingletonManager: true
 *   },
 * });
 * ```
 *
 * @class RecordDataManager
 * @public
 */
class NonSingletonRecordDataManager {
  get managedVersion() {
    return _classPrivateFieldLooseBase(this, _recordData)[_recordData].version || '1';
  }
  constructor(store, _recordData2, identifier) {
    Object.defineProperty(this, _isDeprecated, {
      value: _isDeprecated2
    });
    this.version = '2';
    Object.defineProperty(this, _store, {
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, _recordData, {
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, _identifier, {
      writable: true,
      value: void 0
    });
    _classPrivateFieldLooseBase(this, _store)[_store] = store;
    _classPrivateFieldLooseBase(this, _recordData)[_recordData] = _recordData2;
    _classPrivateFieldLooseBase(this, _identifier)[_identifier] = identifier;
    if (_classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](_recordData2)) ;
  }
  // Cache
  // =====
  /**
   * Retrieve the identifier for this v1 recordData
   *
   * DEPRECATED Caches should not be assumed to be 1:1 with resources
   *
   * @method getResourceIdentifier
   * @public
   * @deprecated
   */
  getResourceIdentifier() {
    return _classPrivateFieldLooseBase(this, _identifier)[_identifier];
  }

  /**
   * Push resource data from a remote source into the cache for this identifier
   *
   * @method pushData
   * @public
   * @param identifier
   * @param data
   * @param hasRecord
   * @returns {void | string[]} if `hasRecord` is true then calculated key changes should be returned
   */
  pushData(identifier, data, hasRecord) {
    const recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    // called by something V1
    if (!isStableIdentifier(identifier)) {
      data = identifier;
      hasRecord = data;
      identifier = _classPrivateFieldLooseBase(this, _identifier)[_identifier];
    }
    if (_classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData)) {
      return recordData.pushData(data, hasRecord);
    }
    return recordData.pushData(identifier, data, hasRecord);
  }

  /**
   * Perform an operation on the cache to update the remote state.
   *
   * Note: currently the only valid operation is a MergeOperation
   * which occurs when a collision of identifiers is detected.
   *
   * @method sync
   * @public
   * @param op the operation to perform
   * @returns {void}
   */
  sync(op) {
    const recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    if (_classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData)) {
      return;
    }
    recordData.sync(op);
  }

  /**
   * Update resource data with a local mutation. Currently supports operations
   * on relationships only.
   *
   * @method update
   * @public
   * @param operation
   */
  // isCollection is only needed for interop with v1 cache
  update(operation, isResource) {
    if (_classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](_classPrivateFieldLooseBase(this, _recordData)[_recordData])) {
      const cache = _classPrivateFieldLooseBase(this, _store)[_store]._instanceCache;
      switch (operation.op) {
        case 'addToRelatedRecords':
          _classPrivateFieldLooseBase(this, _recordData)[_recordData].addToHasMany(operation.field, operation.value.map(i => cache.getRecordData(i)), operation.index);
          return;
        case 'removeFromRelatedRecords':
          _classPrivateFieldLooseBase(this, _recordData)[_recordData].removeFromHasMany(operation.field, operation.value.map(i => cache.getRecordData(i)));
          return;
        case 'replaceRelatedRecords':
          _classPrivateFieldLooseBase(this, _recordData)[_recordData].setDirtyHasMany(operation.field, operation.value.map(i => cache.getRecordData(i)));
          return;
        case 'replaceRelatedRecord':
          if (isResource) {
            _classPrivateFieldLooseBase(this, _recordData)[_recordData].setDirtyBelongsTo(operation.field, operation.value ? cache.getRecordData(operation.value) : null);
            return;
          }
          _classPrivateFieldLooseBase(this, _recordData)[_recordData].removeFromHasMany(operation.field, [cache.getRecordData(operation.prior)]);
          _classPrivateFieldLooseBase(this, _recordData)[_recordData].addToHasMany(operation.field, [cache.getRecordData(operation.value)], operation.index);
          return;
        case 'sortRelatedRecords':
          return;
        default:
          return;
      }
    } else {
      _classPrivateFieldLooseBase(this, _recordData)[_recordData].update(operation);
    }
  }

  /**
   * [LIFECYLCE] Signal to the cache that a new record has been instantiated on the client
   *
   * It returns properties from options that should be set on the record during the create
   * process. This return value behavior is deprecated.
   *
   * @method clientDidCreate
   * @public
   * @param identifier
   * @param options
   */
  clientDidCreate(identifier, options) {
    // called by something V1
    if (!isStableIdentifier(identifier)) {
      options = identifier;
      identifier = _classPrivateFieldLooseBase(this, _identifier)[_identifier];
    }
    let recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];

    // TODO deprecate return value
    if (_classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData)) {
      recordData.clientDidCreate();
      // if a V2 is calling a V1 we need to call both methods
      return recordData._initRecordCreateOptions(options);
    } else {
      return recordData.clientDidCreate(identifier, options);
    }
  }

  /**
   * Pass options to the cache that were supplied to a new record
   * instantiated on the client.
   *
   * DEPRECATED: options are now passed via `clientDidCreate`
   *
   * @method clientDidCreate
   * @public
   * @deprecated
   * @param options
   */
  _initRecordCreateOptions(options) {
    let recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    if (_classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData)) {
      return recordData._initRecordCreateOptions(options);
    }
  }

  /**
   * [LIFECYCLE] Signals to the cache that a resource
   * will be part of a save transaction.
   *
   * @method willCommit
   * @public
   * @param identifier
   */
  willCommit(identifier) {
    _classPrivateFieldLooseBase(this, _recordData)[_recordData].willCommit(identifier || _classPrivateFieldLooseBase(this, _identifier)[_identifier]);
  }

  /**
   * [LIFECYCLE] Signals to the cache that a resource
   * was successfully updated as part of a save transaction.
   *
   * @method didCommit
   * @public
   * @param identifier
   * @param data
   */
  didCommit(identifier, data) {
    // called by something V1
    if (!isStableIdentifier(identifier)) {
      data = identifier;
      identifier = _classPrivateFieldLooseBase(this, _identifier)[_identifier];
    }
    let recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    _classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData) ? recordData.didCommit(data) : recordData.didCommit(identifier, data);
  }

  /**
   * [LIFECYCLE] Signals to the cache that a resource
   * was update via a save transaction failed.
   *
   * @method commitWasRejected
   * @public
   * @param identifier
   * @param errors
   */
  commitWasRejected(identifier, errors) {
    _classPrivateFieldLooseBase(this, _recordData)[_recordData].commitWasRejected(identifier || _classPrivateFieldLooseBase(this, _identifier)[_identifier], errors);
  }

  /**
   * [LIFECYCLE] Signals to the cache that all data for a resource
   * should be cleared.
   *
   * @method unloadRecord
   * @public
   * @param identifier
   */
  unloadRecord(identifier) {
    const recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    if (_classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData)) {
      recordData.unloadRecord();
    } else {
      recordData.unloadRecord(identifier || _classPrivateFieldLooseBase(this, _identifier)[_identifier]);
    }
  }

  // Attrs
  // =====

  /**
   * Retrieve the data for an attribute from the cache
   *
   * @method getAttr
   * @public
   * @param identifier
   * @param propertyName
   * @returns {unknown}
   */
  getAttr(identifier, propertyName) {
    // called by something V1
    if (!isStableIdentifier(identifier)) {
      propertyName = identifier;
      identifier = _classPrivateFieldLooseBase(this, _identifier)[_identifier];
    }
    let recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    return _classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData) ? recordData.getAttr(propertyName) : recordData.getAttr(identifier, propertyName);
  }

  /**
   * Mutate the data for an attribute in the cache
   *
   * @method setAttr
   * @public
   * @param identifier
   * @param propertyName
   * @param value
   */
  setAttr(identifier, propertyName, value) {
    let recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    _classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData) ? recordData.setDirtyAttribute(propertyName, value) : recordData.setAttr(identifier, propertyName, value);
  }

  /**
   * Mutate the data for an attribute in the cache
   *
   * DEPRECATED use setAttr
   *
   * @method setDirtyAttribute
   * @public
   * @deprecated
   * @param identifier
   * @param propertyName
   * @param value
   */
  setDirtyAttribute(propertyName, value) {
    let recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    _classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData) ? recordData.setDirtyAttribute(propertyName, value) : recordData.setAttr(_classPrivateFieldLooseBase(this, _identifier)[_identifier], propertyName, value);
  }

  /**
   * Query the cache for the changed attributes of a resource.
   *
   * DEPRECATED use changedAttrs
   *
   * @method changedAttributes
   * @public
   * @deprecated
   * @param identifier
   * @returns
   */
  changedAttributes() {
    const recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    if (_classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData)) {
      return recordData.changedAttributes();
    }
    return recordData.changedAttrs(_classPrivateFieldLooseBase(this, _identifier)[_identifier]);
  }

  /**
   * Query the cache for the changed attributes of a resource.
   *
   * @method changedAttrs
   * @public
   * @deprecated
   * @param identifier
   * @returns
   */
  changedAttrs(identifier) {
    const recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    if (_classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData)) {
      return recordData.changedAttributes();
    }
    return recordData.changedAttrs(identifier);
  }

  /**
   * Query the cache for whether any mutated attributes exist
   *
   * DEPRECATED use hasChangedAttrs
   *
   * @method hasChangedAttributes
   * @public
   * @deprecated
   * @returns {boolean}
   */
  hasChangedAttributes() {
    const recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    return _classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData) ? recordData.hasChangedAttributes() : recordData.hasChangedAttrs(_classPrivateFieldLooseBase(this, _identifier)[_identifier]);
  }

  /**
   * Query the cache for whether any mutated attributes exist
   *
   * @method hasChangedAttrs
   * @public
   * @param identifier
   * @returns {boolean}
   */
  hasChangedAttrs(identifier) {
    const recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    return _classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData) ? recordData.hasChangedAttributes() : recordData.hasChangedAttrs(identifier);
  }

  /**
   * Tell the cache to discard any uncommitted mutations to attributes
   *
   * DEPRECATED use rollbackAttrs
   *
   * @method rollbackAttributes
   * @public
   * @deprecated
   * @returns
   */
  rollbackAttributes() {
    const recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    return _classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData) ? recordData.rollbackAttributes() : recordData.rollbackAttrs(_classPrivateFieldLooseBase(this, _identifier)[_identifier]);
  }

  /**
   * Tell the cache to discard any uncommitted mutations to attributes
   *
   * @method rollbackAttrs
   * @public
   * @param identifier
   * @returns the names of attributes that were restored
   */
  rollbackAttrs(identifier) {
    const recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    return _classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData) ? recordData.rollbackAttributes() : recordData.rollbackAttrs(identifier);
  }

  // Relationships
  // =============

  // the third arg here is "private". In a world with only V2 it is not necessary
  // but in one in which we must convert a call from V2 -> V1 it is required to do this
  // or else to do nasty schema lookup things
  // @runspired has implemented this concept in relationships spikes and is confident
  // we do not need any signal about whether a relationship is a collection or not at this
  // boundary
  /**
   * Query the cache for the current state of a relationship property
   *
   * @method getRelationship
   * @public
   * @param identifier
   * @param propertyName
   * @returns resource relationship object
   */
  getRelationship(identifier, propertyName, isCollection) {
    if (isCollection === void 0) {
      isCollection = false;
    }
    let recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    if (_classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData)) {
      let isBelongsTo = !isCollection;
      return isBelongsTo ? recordData.getBelongsTo(propertyName) : recordData.getHasMany(propertyName);
    }
    return recordData.getRelationship(identifier, propertyName);
  }

  /**
   * Query the cache for the current state of a belongsTo field
   *
   * DEPRECATED use `getRelationship`
   *
   * @method getBelongsTo
   * @public
   * @deprecated
   * @param propertyName
   * @returns single resource relationship object
   */
  getBelongsTo(propertyName) {
    let recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    if (_classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData)) {
      return recordData.getBelongsTo(propertyName);
    } else {
      let identifier = _classPrivateFieldLooseBase(this, _identifier)[_identifier];
      return recordData.getRelationship(identifier, propertyName);
    }
  }

  /**
   * Query the cache for the current state of a hasMany field
   *
   * DEPRECATED use `getRelationship`
   *
   * @method getHasMany
   * @public
   * @deprecated
   * @param propertyName
   * @returns single resource relationship object
   */
  getHasMany(propertyName) {
    let recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    if (_classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData)) {
      return recordData.getHasMany(propertyName);
    } else {
      let identifier = _classPrivateFieldLooseBase(this, _identifier)[_identifier];
      return recordData.getRelationship(identifier, propertyName);
    }
  }

  /**
   * Mutate the current state of a belongsTo relationship
   *
   * DEPRECATED use update
   *
   * @method setDirtyBelongsTo
   * @public
   * @deprecated
   * @param propertyName
   * @param value
   */
  setDirtyBelongsTo(propertyName, value) {
    const recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    _classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData) ? recordData.setDirtyBelongsTo(propertyName, value) : recordData.update({
      op: 'replaceRelatedRecord',
      record: _classPrivateFieldLooseBase(this, _identifier)[_identifier],
      field: propertyName,
      value: value ? value.getResourceIdentifier() : null
    });
  }

  /**
   * Mutate the current state of a hasMany relationship by adding values
   * An index may optionally be specified which the cache should use for
   * where in the list to insert the records
   *
   * DEPRECATED use update
   *
   * @method addToHasMany
   * @deprecated
   * @public
   * @param propertyName
   * @param value
   * @param idx
   */
  addToHasMany(propertyName, value, idx) {
    const identifier = _classPrivateFieldLooseBase(this, _identifier)[_identifier];
    const recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    _classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData) ? recordData.addToHasMany(propertyName, value, idx) : recordData.update({
      op: 'addToRelatedRecords',
      field: propertyName,
      record: identifier,
      value: value.map(v => v.getResourceIdentifier())
    });
  }

  /**
   * Mutate the current state of a hasMany relationship by removing values.
   *
   * DEPRECATED use update
   *
   * @method removeFromHasMany
   * @deprecated
   * @public
   * @param propertyName
   * @param value
   */
  removeFromHasMany(propertyName, value) {
    const identifier = _classPrivateFieldLooseBase(this, _identifier)[_identifier];
    const recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    _classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData) ? recordData.removeFromHasMany(propertyName, value) : recordData.update({
      op: 'removeFromRelatedRecords',
      record: identifier,
      field: propertyName,
      value: value.map(v => v.getResourceIdentifier())
    });
  }

  /**
   * Mutate the current state of a hasMany relationship by replacing it entirely
   *
   * DEPRECATED use `setHasMany`
   *
   * @method setDirtyHasMany
   * @public
   * @deprecated
   * @param propertyName
   * @param value
   */
  setDirtyHasMany(propertyName, value) {
    let recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    _classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData) ? recordData.setDirtyHasMany(propertyName, value) : recordData.update({
      op: 'replaceRelatedRecords',
      record: _classPrivateFieldLooseBase(this, _identifier)[_identifier],
      field: propertyName,
      value: value.map(rd => rd.getResourceIdentifier())
    });
  }

  // State
  // =============

  /**
   * Update the cache state for the given resource to be marked as locally deleted,
   * or remove such a mark.
   *
   * @method setIsDeleted
   * @public
   * @param identifier
   * @param isDeleted
   */
  setIsDeleted(identifier, isDeleted) {
    if (!isStableIdentifier(identifier)) {
      isDeleted = identifier;
      identifier = _classPrivateFieldLooseBase(this, _identifier)[_identifier];
    }
    const recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    _classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData) ? recordData.setIsDeleted(isDeleted) : recordData.setIsDeleted(identifier, isDeleted);
  }

  /**
   * Query the cache for any validation errors applicable to the given resource.
   *
   * @method getErrors
   * @public
   * @param identifier
   * @returns
   */
  getErrors(identifier) {
    return _classPrivateFieldLooseBase(this, _recordData)[_recordData].getErrors(identifier || _classPrivateFieldLooseBase(this, _identifier)[_identifier]);
  }

  /**
   * Query the cache for whether a given resource has any available data
   *
   * @method isEmpty
   * @public
   * @param identifier
   * @returns {boolean}
   */
  isEmpty(identifier) {
    const recordData = _classPrivateFieldLooseBase(this, _recordData)[_recordData];
    return _classPrivateFieldLooseBase(this, _isDeprecated)[_isDeprecated](recordData) ? recordData.isEmpty?.(identifier || _classPrivateFieldLooseBase(this, _identifier)[_identifier]) || false : recordData.isEmpty(identifier || _classPrivateFieldLooseBase(this, _identifier)[_identifier]);
  }

  /**
   * Query the cache for whether a given resource was created locally and not
   * yet persisted.
   *
   * @method isNew
   * @public
   * @param identifier
   * @returns {boolean}
   */
  isNew(identifier) {
    return _classPrivateFieldLooseBase(this, _recordData)[_recordData].isNew(identifier || _classPrivateFieldLooseBase(this, _identifier)[_identifier]);
  }

  /**
   * Query the cache for whether a given resource is marked as deleted (but not
   * necessarily persisted yet).
   *
   * @method isDeleted
   * @public
   * @param identifier
   * @returns {boolean}
   */
  isDeleted(identifier) {
    return _classPrivateFieldLooseBase(this, _recordData)[_recordData].isDeleted(identifier || _classPrivateFieldLooseBase(this, _identifier)[_identifier]);
  }

  /**
   * Query the cache for whether a given resource has been deleted and that deletion
   * has also been persisted.
   *
   * @method isDeletionCommitted
   * @public
   * @param identifier
   * @returns {boolean}
   */
  isDeletionCommitted(identifier) {
    return _classPrivateFieldLooseBase(this, _recordData)[_recordData].isDeletionCommitted(identifier || _classPrivateFieldLooseBase(this, _identifier)[_identifier]);
  }
}
function _isDeprecated2(recordData) {
  let version = recordData.version || '1';
  return version !== this.version;
}
var _recordDatas = /*#__PURE__*/_classPrivateFieldLooseKey("recordDatas");
var _recordData3 = /*#__PURE__*/_classPrivateFieldLooseKey("recordData");
class SingletonRecordDataManager {
  constructor() {
    Object.defineProperty(this, _recordData3, {
      value: _recordData4
    });
    this.version = '2';
    Object.defineProperty(this, _recordDatas, {
      writable: true,
      value: void 0
    });
    _classPrivateFieldLooseBase(this, _recordDatas)[_recordDatas] = new Map();
  }
  _addRecordData(identifier, recordData) {
    _classPrivateFieldLooseBase(this, _recordDatas)[_recordDatas].set(identifier, recordData);
  }
  // Cache
  // =====
  pushData(identifier, data, hasRecord) {
    return _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](identifier).pushData(identifier, data, hasRecord);
  }
  sync(op) {
    _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](op.record).sync(op);
  }
  clientDidCreate(identifier, options) {
    return _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](identifier).clientDidCreate(identifier, options);
  }
  willCommit(identifier) {
    _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](identifier).willCommit(identifier);
  }
  didCommit(identifier, data) {
    _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](identifier).didCommit(identifier, data);
  }
  commitWasRejected(identifier, errors) {
    _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](identifier).commitWasRejected(identifier, errors);
  }
  unloadRecord(identifier) {
    _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](identifier).unloadRecord(identifier);
  }

  // Attrs
  // =====

  getAttr(identifier, propertyName) {
    return _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](identifier).getAttr(identifier, propertyName);
  }
  setAttr(identifier, propertyName, value) {
    _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](identifier).setAttr(identifier, propertyName, value);
  }
  changedAttrs(identifier) {
    return _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](identifier).changedAttrs(identifier);
  }
  hasChangedAttrs(identifier) {
    return _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](identifier).hasChangedAttrs(identifier);
  }
  rollbackAttrs(identifier) {
    return _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](identifier).rollbackAttrs(identifier);
  }
  getRelationship(identifier, propertyName) {
    return _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](identifier).getRelationship(identifier, propertyName);
  }
  update(operation) {
    _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](operation.record).update(operation);
  }

  // State
  // =============

  setIsDeleted(identifier, isDeleted) {
    _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](identifier).setIsDeleted(identifier, isDeleted);
  }
  getErrors(identifier) {
    return _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](identifier).getErrors(identifier);
  }
  isEmpty(identifier) {
    return _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](identifier).isEmpty(identifier);
  }
  isNew(identifier) {
    return _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](identifier).isNew(identifier);
  }
  isDeleted(identifier) {
    return _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](identifier).isDeleted(identifier);
  }
  isDeletionCommitted(identifier) {
    return _classPrivateFieldLooseBase(this, _recordData3)[_recordData3](identifier).isDeletionCommitted(identifier);
  }
}
function _recordData4(identifier) {
  return _classPrivateFieldLooseBase(this, _recordDatas)[_recordDatas].get(identifier);
}
function constructResource(type, id, lid) {
  if (typeof type === 'object' && type !== null) {
    let resource = type;
    if (isStableIdentifier(resource)) {
      return resource;
    }
    if ('id' in resource) {
      resource.id = coerceId(resource.id);
    }
    return resource;
  } else {
    const trueId = coerceId(id);
    if (!isNonEmptyString(trueId)) {
      if (isNonEmptyString(lid)) {
        return {
          lid
        };
      }
      throw new Error('Expected either id or lid to be a valid string');
    }
    if (isNonEmptyString(lid)) {
      return {
        type,
        id: trueId,
        lid
      };
    }
    return {
      type,
      id: trueId
    };
  }
}

/**
  @module @ember-data/store
*/

class LegacyWrapper {
  constructor(_store) {
    this._store = _store;
    this._willNotify = false;
    this._pendingNotifies = new Map();
  }
  get identifierCache() {
    return this._store.identifierCache;
  }
  _scheduleNotification(identifier, key) {
    let pending = this._pendingNotifies.get(identifier);
    if (!pending) {
      pending = new Set();
      this._pendingNotifies.set(identifier, pending);
    }
    pending.add(key);
    if (this._willNotify === true) {
      return;
    }
    this._willNotify = true;
    // it's possible a RecordData adhoc notifies us,
    // in which case we sync flush
    if (this._store._cbs) {
      this._store._schedule('notify', () => this._flushNotifications());
    } else {
      this._flushNotifications();
    }
  }
  _flushNotifications() {
    if (this._willNotify === false) {
      return;
    }
    let pending = this._pendingNotifies;
    this._pendingNotifies = new Map();
    this._willNotify = false;
    pending.forEach((set, identifier) => {
      set.forEach(key => {
        this._store._notificationManager.notify(identifier, 'relationships', key);
      });
    });
  }
  notifyChange(identifier, namespace, key) {
    if (namespace === 'relationships' && key) {
      this._scheduleNotification(identifier, key);
      return;
    }
    this._store._notificationManager.notify(identifier, namespace, key);
    if (namespace === 'state') {
      this._store.recordArrayManager.identifierChanged(identifier);
    }
  }
  notifyErrorsChange(type, id, lid) {
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_V1CACHE_STORE_APIS)) ;
    const resource = constructResource(type, id, lid);
    const identifier = this.identifierCache.getOrCreateRecordIdentifier(resource);
    this._store._notificationManager.notify(identifier, 'errors');
  }
  attributesDefinitionFor(type) {
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_V1CACHE_STORE_APIS)) ;
    return this._store.getSchemaDefinitionService().attributesDefinitionFor({
      type
    });
  }
  relationshipsDefinitionFor(type) {
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_V1CACHE_STORE_APIS)) ;
    return this._store.getSchemaDefinitionService().relationshipsDefinitionFor({
      type
    });
  }
  getSchemaDefinitionService() {
    return this._store.getSchemaDefinitionService();
  }
  notifyPropertyChange(type, id, lid, key) {
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_V1CACHE_STORE_APIS)) ;
    const resource = constructResource(type, id, lid);
    const identifier = this.identifierCache.getOrCreateRecordIdentifier(resource);
    this._store._notificationManager.notify(identifier, 'attributes', key);
  }
  notifyHasManyChange(type, id, lid, key) {
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_V1CACHE_STORE_APIS)) ;
    const resource = constructResource(type, id, lid);
    const identifier = this.identifierCache.getOrCreateRecordIdentifier(resource);
    this._scheduleNotification(identifier, key);
  }
  notifyBelongsToChange(type, id, lid, key) {
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_V1CACHE_STORE_APIS)) ;
    const resource = constructResource(type, id, lid);
    const identifier = this.identifierCache.getOrCreateRecordIdentifier(resource);
    this._scheduleNotification(identifier, key);
  }
  notifyStateChange(type, id, lid, key) {
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_V1CACHE_STORE_APIS)) ;
    const resource = constructResource(type, id, lid);
    const identifier = this.identifierCache.getOrCreateRecordIdentifier(resource);
    this._store._notificationManager.notify(identifier, 'state');
    this._store.recordArrayManager.identifierChanged(identifier);
  }
  recordDataFor(type, id, lid) {
    let identifier;
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_V1CACHE_STORE_APIS)) {
      if (!isStableIdentifier(type)) {
        // we also deprecate create capability. This behavior was problematic because
        // there's no outside association between this RecordData and an Identifier.
        // It's likely a mistake when we hit this codepath, but we said in an early
        // RFC we'd allow this.
        // With V2 we are enforcing someone to use the record-data and identifier-cache APIs to
        // create a new identifier and then call clientDidCreate on the RecordData
        // instead.
        identifier = !id && !lid ? this.identifierCache.createIdentifierForNewRecord({
          type: type
        }) : this.identifierCache.getOrCreateRecordIdentifier(constructResource(type, id, lid));
      } else {
        identifier = type;
      }
    } else {
      identifier = type;
    }
    const recordData = this._store._instanceCache.getRecordData(identifier);
    if (!id && !lid) {
      recordData.clientDidCreate(identifier);
      this._store.recordArrayManager.identifierAdded(identifier);
    }
    return recordData;
  }
  setRecordId(type, id, lid) {
    let identifier;
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_V1CACHE_STORE_APIS)) {
      if (!isStableIdentifier(type)) {
        const modelName = normalizeModelName$1(type);
        const resource = constructResource(modelName, null, coerceId(lid));
        identifier = this.identifierCache.peekRecordIdentifier(resource);
      } else {
        identifier = type;
      }
    } else {
      identifier = type;
    }
    this._store._instanceCache.setRecordId(identifier, id);
  }
  isRecordInUse(type, id, lid) {
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_V1CACHE_STORE_APIS)) ;
    const resource = constructResource(type, id, lid);
    const identifier = this.identifierCache.peekRecordIdentifier(resource);
    const record = identifier && this._store._instanceCache.peek({
      identifier,
      bucket: 'record'
    });
    return record ? !(record.isDestroyed || record.isDestroying) : false;
  }
  hasRecord(identifier) {
    return Boolean(this._store._instanceCache.peek({
      identifier,
      bucket: 'record'
    }));
  }
  disconnectRecord(type, id, lid) {
    let identifier;
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_V1CACHE_STORE_APIS)) {
      if (typeof type === 'string') {
        let resource = constructResource(type, id, lid);
        identifier = this.identifierCache.peekRecordIdentifier(resource);
      } else {
        identifier = type;
      }
    } else {
      identifier = type;
    }
    this._store._instanceCache.disconnect(identifier);
    this._pendingNotifies.delete(identifier);
  }
}
class V2RecordDataStoreWrapper {
  constructor(_store) {
    this._store = _store;
    this._willNotify = false;
    this._pendingNotifies = new Map();
  }
  get identifierCache() {
    return this._store.identifierCache;
  }
  _scheduleNotification(identifier, key) {
    let pending = this._pendingNotifies.get(identifier);
    if (!pending) {
      pending = new Set();
      this._pendingNotifies.set(identifier, pending);
    }
    pending.add(key);
    if (this._willNotify === true) {
      return;
    }
    this._willNotify = true;
    // it's possible a RecordData adhoc notifies us,
    // in which case we sync flush
    if (this._store._cbs) {
      this._store._schedule('notify', () => this._flushNotifications());
    } else {
      this._flushNotifications();
    }
  }
  _flushNotifications() {
    if (this._willNotify === false) {
      return;
    }
    let pending = this._pendingNotifies;
    this._pendingNotifies = new Map();
    this._willNotify = false;
    pending.forEach((set, identifier) => {
      set.forEach(key => {
        this._store._notificationManager.notify(identifier, 'relationships', key);
      });
    });
  }
  notifyChange(identifier, namespace, key) {
    if (namespace === 'relationships' && key) {
      this._scheduleNotification(identifier, key);
      return;
    }
    this._store._notificationManager.notify(identifier, namespace, key);
    if (namespace === 'state') {
      this._store.recordArrayManager.identifierChanged(identifier);
    }
  }
  getSchemaDefinitionService() {
    return this._store.getSchemaDefinitionService();
  }
  recordDataFor(identifier) {
    return this._store._instanceCache.getRecordData(identifier);
  }
  setRecordId(identifier, id) {
    this._store._instanceCache.setRecordId(identifier, id);
  }
  hasRecord(identifier) {
    return Boolean(this._store._instanceCache.peek({
      identifier,
      bucket: 'record'
    }));
  }
  disconnectRecord(identifier) {
    this._store._instanceCache.disconnect(identifier);
    this._pendingNotifies.delete(identifier);
  }
}
const RecordDataStoreWrapper = macroCondition(getOwnConfig().deprecations.DEPRECATE_V1CACHE_STORE_APIS) ? LegacyWrapper : V2RecordDataStoreWrapper;

/**
  @module @ember-data/store
*/
/**
  Snapshot is not directly instantiable.
  Instances are provided to a consuming application's
  adapters and serializers for certain requests.

  @class Snapshot
  @public
*/
class Snapshot {
  /**
   * @method constructor
   * @constructor
   * @private
   * @param options
   * @param identifier
   * @param _store
   */
  constructor(options, identifier, store) {
    this._store = store;
    this.__attributes = null;
    this._belongsToRelationships = Object.create(null);
    this._belongsToIds = Object.create(null);
    this._hasManyRelationships = Object.create(null);
    this._hasManyIds = Object.create(null);
    const hasRecord = !!store._instanceCache.peek({
      identifier,
      bucket: 'record'
    });
    this.modelName = identifier.type;

    /**
      The unique RecordIdentifier associated with this Snapshot.
       @property identifier
      @public
      @type {StableRecordIdentifier}
    */
    this.identifier = identifier;

    /*
      If the we do not yet have a record, then we are
      likely a snapshot being provided to a find request, so we
      populate __attributes lazily. Else, to preserve the "moment
      in time" in which a snapshot is created, we greedily grab
      the values.
     */
    if (hasRecord) {
      this._attributes;
    }

    /**
     The id of the snapshot's underlying record
      Example
      ```javascript
     // store.push('post', { id: 1, author: 'Tomster', title: 'Ember.js rocks' });
     postSnapshot.id; // => '1'
     ```
      @property id
     @type {String}
     @public
     */
    this.id = identifier.id;

    /**
     A hash of adapter options
     @property adapterOptions
     @type {Object}
     @public
     */
    this.adapterOptions = options.adapterOptions;

    /**
     If `include` was passed to the options hash for the request, the value
     would be available here.
      @property include
     @type {String|Array}
     @public
     */
    this.include = options.include;

    /**
     The name of the type of the underlying record for this snapshot, as a string.
      @property modelName
     @type {String}
     @public
     */
    this.modelName = identifier.type;
    if (hasRecord) {
      this._changedAttributes = this._store._instanceCache.getRecordData(identifier).changedAttrs(identifier);
    }
  }

  /**
   The underlying record for this snapshot. Can be used to access methods and
   properties defined on the record.
    Example
    ```javascript
   let json = snapshot.record.toJSON();
   ```
    @property record
   @type {Model}
   @public
   */
  get record() {
    return this._store._instanceCache.getRecord(this.identifier);
  }
  get _attributes() {
    if (this.__attributes !== null) {
      return this.__attributes;
    }
    let attributes = this.__attributes = Object.create(null);
    const {
      identifier
    } = this;
    let attrs = Object.keys(this._store.getSchemaDefinitionService().attributesDefinitionFor(identifier));
    let recordData = this._store._instanceCache.getRecordData(identifier);
    attrs.forEach(keyName => {
      attributes[keyName] = recordData.getAttr(identifier, keyName);
    });
    return attributes;
  }

  /**
   The type of the underlying record for this snapshot, as a Model.
    @property type
    @public
    @deprecated
   @type {Model}
   */

  get isNew() {
    const recordData = this._store._instanceCache.peek({
      identifier: this.identifier,
      bucket: 'recordData'
    });
    return recordData?.isNew(this.identifier) || false;
  }

  /**
   Returns the value of an attribute.
    Example
    ```javascript
   // store.push('post', { id: 1, author: 'Tomster', title: 'Ember.js rocks' });
   postSnapshot.attr('author'); // => 'Tomster'
   postSnapshot.attr('title'); // => 'Ember.js rocks'
   ```
    Note: Values are loaded eagerly and cached when the snapshot is created.
    @method attr
   @param {String} keyName
   @return {Object} The attribute value or undefined
   @public
   */
  attr(keyName) {
    if (keyName in this._attributes) {
      return this._attributes[keyName];
    }
  }

  /**
   Returns all attributes and their corresponding values.
    Example
    ```javascript
   // store.push('post', { id: 1, author: 'Tomster', title: 'Ember.js rocks' });
   postSnapshot.attributes(); // => { author: 'Tomster', title: 'Ember.js rocks' }
   ```
    @method attributes
   @return {Object} All attributes of the current snapshot
   @public
   */
  attributes() {
    return {
      ...this._attributes
    };
  }

  /**
   Returns all changed attributes and their old and new values.
    Example
    ```javascript
   // store.push('post', { id: 1, author: 'Tomster', title: 'Ember.js rocks' });
   postModel.set('title', 'Ember.js rocks!');
   postSnapshot.changedAttributes(); // => { title: ['Ember.js rocks', 'Ember.js rocks!'] }
   ```
    @method changedAttributes
   @return {Object} All changed attributes of the current snapshot
   @public
   */
  changedAttributes() {
    let changedAttributes = Object.create(null);
    if (!this._changedAttributes) {
      return changedAttributes;
    }
    let changedAttributeKeys = Object.keys(this._changedAttributes);
    for (let i = 0, length = changedAttributeKeys.length; i < length; i++) {
      let key = changedAttributeKeys[i];
      changedAttributes[key] = this._changedAttributes[key].slice();
    }
    return changedAttributes;
  }

  /**
   Returns the current value of a belongsTo relationship.
    `belongsTo` takes an optional hash of options as a second parameter,
   currently supported options are:
    - `id`: set to `true` if you only want the ID of the related record to be
   returned.
    Example
    ```javascript
   // store.push('post', { id: 1, title: 'Hello World' });
   // store.createRecord('comment', { body: 'Lorem ipsum', post: post });
   commentSnapshot.belongsTo('post'); // => Snapshot
   commentSnapshot.belongsTo('post', { id: true }); // => '1'
    // store.push('comment', { id: 1, body: 'Lorem ipsum' });
   commentSnapshot.belongsTo('post'); // => undefined
   ```
    Calling `belongsTo` will return a new Snapshot as long as there's any known
   data for the relationship available, such as an ID. If the relationship is
   known but unset, `belongsTo` will return `null`. If the contents of the
   relationship is unknown `belongsTo` will return `undefined`.
    Note: Relationships are loaded lazily and cached upon first access.
    @method belongsTo
   @param {String} keyName
   @param {Object} [options]
   @public
   @return {(Snapshot|String|null|undefined)} A snapshot or ID of a known
   relationship or null if the relationship is known but unset. undefined
   will be returned if the contents of the relationship is unknown.
   */
  belongsTo(keyName, options) {
    let returnModeIsId = !!(options && options.id);
    let result;
    let store = this._store;
    if (returnModeIsId === true && keyName in this._belongsToIds) {
      return this._belongsToIds[keyName];
    }
    if (returnModeIsId === false && keyName in this._belongsToRelationships) {
      return this._belongsToRelationships[keyName];
    }
    store.getSchemaDefinitionService().relationshipsDefinitionFor({
      type: this.modelName
    })[keyName];
    // this check is not a regression in behavior because relationships don't currently
    // function without access to intimate API contracts between RecordData and Model.
    // This is a requirement we should fix as soon as the relationship layer does not require
    // this intimate API usage.
    if (macroCondition(!dependencySatisfies("@ember-data/record-data", "*"))) ;
    const graphFor = importSync('@ember-data/record-data/-private').graphFor;
    const {
      identifier
    } = this;
    const relationship = graphFor(this._store).get(identifier, keyName);
    let value = relationship.getData();
    let data = value && value.data;
    let inverseIdentifier = data ? store.identifierCache.getOrCreateRecordIdentifier(data) : null;
    if (value && value.data !== undefined) {
      if (inverseIdentifier && !store._instanceCache.getRecordData(inverseIdentifier).isDeleted(inverseIdentifier)) {
        if (returnModeIsId) {
          result = inverseIdentifier.id;
        } else {
          result = store._instanceCache.createSnapshot(inverseIdentifier);
        }
      } else {
        result = null;
      }
    }
    if (returnModeIsId) {
      this._belongsToIds[keyName] = result;
    } else {
      this._belongsToRelationships[keyName] = result;
    }
    return result;
  }

  /**
   Returns the current value of a hasMany relationship.
    `hasMany` takes an optional hash of options as a second parameter,
   currently supported options are:
    - `ids`: set to `true` if you only want the IDs of the related records to be
   returned.
    Example
    ```javascript
   // store.push('post', { id: 1, title: 'Hello World', comments: [2, 3] });
   postSnapshot.hasMany('comments'); // => [Snapshot, Snapshot]
   postSnapshot.hasMany('comments', { ids: true }); // => ['2', '3']
    // store.push('post', { id: 1, title: 'Hello World' });
   postSnapshot.hasMany('comments'); // => undefined
   ```
    Note: Relationships are loaded lazily and cached upon first access.
    @method hasMany
   @param {String} keyName
   @param {Object} [options]
   @public
   @return {(Array|undefined)} An array of snapshots or IDs of a known
   relationship or an empty array if the relationship is known but unset.
   undefined will be returned if the contents of the relationship is unknown.
   */
  hasMany(keyName, options) {
    let returnModeIsIds = !!(options && options.ids);
    let results;
    let cachedIds = this._hasManyIds[keyName];
    let cachedSnapshots = this._hasManyRelationships[keyName];
    if (returnModeIsIds === true && keyName in this._hasManyIds) {
      return cachedIds;
    }
    if (returnModeIsIds === false && keyName in this._hasManyRelationships) {
      return cachedSnapshots;
    }
    let store = this._store;
    store.getSchemaDefinitionService().relationshipsDefinitionFor({
      type: this.modelName
    })[keyName];
    // this check is not a regression in behavior because relationships don't currently
    // function without access to intimate API contracts between RecordData and Model.
    // This is a requirement we should fix as soon as the relationship layer does not require
    // this intimate API usage.
    if (macroCondition(!dependencySatisfies("@ember-data/record-data", "*"))) ;
    const graphFor = importSync('@ember-data/record-data/-private').graphFor;
    const {
      identifier
    } = this;
    const relationship = graphFor(this._store).get(identifier, keyName);
    let value = relationship.getData();
    if (value.data) {
      results = [];
      value.data.forEach(member => {
        let inverseIdentifier = store.identifierCache.getOrCreateRecordIdentifier(member);
        if (!store._instanceCache.getRecordData(inverseIdentifier).isDeleted(inverseIdentifier)) {
          if (returnModeIsIds) {
            results.push(inverseIdentifier.id);
          } else {
            results.push(store._instanceCache.createSnapshot(inverseIdentifier));
          }
        }
      });
    }

    // we assign even if `undefined` so that we don't reprocess the relationship
    // on next access. This works with the `keyName in` checks above.
    if (returnModeIsIds) {
      this._hasManyIds[keyName] = results;
    } else {
      this._hasManyRelationships[keyName] = results;
    }
    return results;
  }

  /**
    Iterates through all the attributes of the model, calling the passed
    function on each attribute.
     Example
     ```javascript
    snapshot.eachAttribute(function(name, meta) {
      // ...
    });
    ```
     @method eachAttribute
    @param {Function} callback the callback to execute
    @param {Object} [binding] the value to which the callback's `this` should be bound
    @public
  */
  eachAttribute(callback, binding) {
    let attrDefs = this._store.getSchemaDefinitionService().attributesDefinitionFor(this.identifier);
    Object.keys(attrDefs).forEach(key => {
      callback.call(binding, key, attrDefs[key]);
    });
  }

  /**
    Iterates through all the relationships of the model, calling the passed
    function on each relationship.
     Example
     ```javascript
    snapshot.eachRelationship(function(name, relationship) {
      // ...
    });
    ```
     @method eachRelationship
    @param {Function} callback the callback to execute
    @param {Object} [binding] the value to which the callback's `this` should be bound
    @public
  */
  eachRelationship(callback, binding) {
    let relationshipDefs = this._store.getSchemaDefinitionService().relationshipsDefinitionFor(this.identifier);
    Object.keys(relationshipDefs).forEach(key => {
      callback.call(binding, key, relationshipDefs[key]);
    });
  }

  /**
    Serializes the snapshot using the serializer for the model.
     Example
     ```app/adapters/application.js
    import Adapter from '@ember-data/adapter';
     export default Adapter.extend({
      createRecord(store, type, snapshot) {
        let data = snapshot.serialize({ includeId: true });
        let url = `/${type.modelName}`;
         return fetch(url, {
          method: 'POST',
          body: data,
        }).then((response) => response.json())
      }
    });
    ```
     @method serialize
    @param {Object} options
    @return {Object} an object whose values are primitive JSON values only
    @public
   */
  serialize(options) {
    const serializer = this._store.serializerFor(this.modelName);
    return serializer.serialize(this, options);
  }
}
if (macroCondition(getOwnConfig().deprecations.DEPRECATE_SNAPSHOT_MODEL_CLASS_ACCESS)) {
  Object.defineProperty(Snapshot.prototype, 'type', {
    get() {
      return this._store.modelFor(this.identifier.type);
    }
  });
}

/*
 * Returns the RecordData instance associated with a given
 * Model or Identifier
 */

const RecordDataForIdentifierCache = new Map();
function setRecordDataFor(identifier, recordData) {
  RecordDataForIdentifierCache.set(identifier, recordData);
}
function removeRecordDataFor(identifier) {
  RecordDataForIdentifierCache.delete(identifier);
}
function recordDataFor(instance) {
  if (RecordDataForIdentifierCache.has(instance)) {
    return RecordDataForIdentifierCache.get(instance);
  }
  return null;
}
let _peekGraph;
if (macroCondition(dependencySatisfies("@ember-data/record-data", "*"))) {
  let __peekGraph;
  _peekGraph = wrapper => {
    let a = importSync('@ember-data/record-data/-private').peekGraph;
    __peekGraph = __peekGraph || a;
    return __peekGraph(wrapper);
  };
}

/**
  @module @ember-data/store
*/

const RecordCache = new Map();
function peekRecordIdentifier(record) {
  return RecordCache.get(record);
}

/**
  Retrieves the unique referentially-stable [RecordIdentifier](/ember-data/release/classes/StableRecordIdentifier)
  assigned to the given record instance.
  ```js
  import { recordIdentifierFor } from "@ember-data/store";
  // ... gain access to a record, for instance with peekRecord or findRecord
  const record = store.peekRecord("user", "1");
  // get the identifier for the record (see docs for StableRecordIdentifier)
  const identifier = recordIdentifierFor(record);
  // access the identifier's properties.
  const { id, type, lid } = identifier;
  ```
  @method recordIdentifierFor
  @public
  @static
  @for @ember-data/store
  @param {Object} record a record instance previously obstained from the store.
  @returns {StableRecordIdentifier}
 */
function recordIdentifierFor(record) {
  return RecordCache.get(record);
}
function setRecordIdentifier(record, identifier) {
  if (macroCondition(isDevelopingApp())) {
    if (RecordCache.has(record) && RecordCache.get(record) !== identifier) {
      throw new Error(`${String(record)} was already assigned an identifier`);
    }
  }

  /*
  It would be nice to do a reverse check here that an identifier has not
  previously been assigned a record; however, unload + rematerialization
  prevents us from having a great way of doing so when CustomRecordClasses
  don't necessarily give us access to a `isDestroyed` for dematerialized
  instance.
  */

  RecordCache.set(record, identifier);
}
const StoreMap = new Map();
function storeFor(record) {
  const store = StoreMap.get(record);
  return store;
}
class InstanceCache {
  constructor(store) {
    this.__instances = {
      record: new Map(),
      recordData: new Map(),
      reference: new WeakMap()
    };
    this.store = store;
    this._storeWrapper = new RecordDataStoreWrapper(this.store);
    this.__recordDataFor = resource => {
      // TODO enforce strict
      const identifier = this.store.identifierCache.getOrCreateRecordIdentifier(resource);
      return this.getRecordData(identifier);
    };
    store.identifierCache.__configureMerge((identifier, matchedIdentifier, resourceData) => {
      let keptIdentifier = identifier;
      if (identifier.id !== matchedIdentifier.id) {
        keptIdentifier = 'id' in resourceData && identifier.id === resourceData.id ? identifier : matchedIdentifier;
      } else if (identifier.type !== matchedIdentifier.type) {
        keptIdentifier = 'type' in resourceData && identifier.type === resourceData.type ? identifier : matchedIdentifier;
      }
      let staleIdentifier = identifier === keptIdentifier ? matchedIdentifier : identifier;

      // check for duplicate entities
      let keptHasRecord = this.__instances.record.has(keptIdentifier);
      let staleHasRecord = this.__instances.record.has(staleIdentifier);
      let keptRecordData = this.__instances.recordData.get(keptIdentifier) || null;
      let staleRecordData = this.__instances.recordData.get(staleIdentifier) || null;

      // we cannot merge entities when both have records
      // (this may not be strictly true, we could probably swap the recordData the record points at)
      if (keptHasRecord && staleHasRecord) {
        // TODO we probably don't need to throw these errors anymore
        // we can probably just "swap" what data source the abandoned
        // record points at so long as
        // it itself is not retained by the store in any way.
        if ('id' in resourceData) {
          throw new Error(`Failed to update the 'id' for the RecordIdentifier '${identifier.type}:${String(identifier.id)} (${identifier.lid})' to '${String(resourceData.id)}', because that id is already in use by '${matchedIdentifier.type}:${String(matchedIdentifier.id)} (${matchedIdentifier.lid})'`);
        }
      }
      let recordData = keptRecordData || staleRecordData;
      if (recordData) {
        recordData.sync({
          op: 'mergeIdentifiers',
          record: staleIdentifier,
          value: keptIdentifier
        });
      } else if (macroCondition(dependencySatisfies("@ember-data/record-data", "*"))) {
        // TODO notify cache always, this requires it always being a singleton
        // and not ever specific to one record-data
        this.store.__private_singleton_recordData?.sync({
          op: 'mergeIdentifiers',
          record: staleIdentifier,
          value: keptIdentifier
        });
      }
      if (staleRecordData === null) {
        return keptIdentifier;
      }

      /*
      TODO @runspired consider adding this to make polymorphism even nicer
      if (HAS_RECORD_DATA_PACKAGE) {
      if (identifier.type !== matchedIdentifier.type) {
        const graphFor = importSync('@ember-data/record-data/-private').graphFor;
        graphFor(this).registerPolymorphicType(identifier.type, matchedIdentifier.type);
      }
      }
      */

      this.unloadRecord(staleIdentifier);
      return keptIdentifier;
    });
  }
  peek(_ref) {
    let {
      identifier,
      bucket
    } = _ref;
    return this.__instances[bucket]?.get(identifier);
  }
  getRecord(identifier, properties) {
    let record = this.__instances.record.get(identifier);
    if (!record) {
      const recordData = this.getRecordData(identifier);
      record = this.store.instantiateRecord(identifier, properties || {}, this.__recordDataFor, this.store._notificationManager);
      setRecordIdentifier(record, identifier);
      setRecordDataFor(record, recordData);
      StoreMap.set(record, this.store);
      this.__instances.record.set(identifier, record);
      if (macroCondition(getOwnConfig().debug.LOG_INSTANCE_CACHE)) {
        // eslint-disable-next-line no-console
        console.log(`InstanceCache: created Record for ${String(identifier)}`, properties);
      }
    }
    return record;
  }
  getRecordData(identifier) {
    let recordData = this.__instances.recordData.get(identifier);
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_V1CACHE_STORE_APIS)) {
      if (!recordData && this.store.createRecordDataFor.length > 2) {
        let recordDataInstance = this.store.createRecordDataFor(identifier.type, identifier.id,
        // @ts-expect-error
        identifier.lid, this._storeWrapper);
        if (macroCondition(getOwnConfig().deprecations.DEPRECATE_V1_RECORD_DATA)) {
          recordData = new NonSingletonRecordDataManager(this.store, recordDataInstance, identifier);
        } else {
          recordData = this.__cacheManager = this.__cacheManager || new NonSingletonRecordDataManager(this.store, recordDataInstance, identifier);
        }
      }
    }
    if (!recordData) {
      let recordDataInstance = this.store.createRecordDataFor(identifier, this._storeWrapper);
      if (macroCondition(getOwnConfig().deprecations.DEPRECATE_V1_RECORD_DATA)) {
        recordData = new NonSingletonRecordDataManager(this.store, recordDataInstance, identifier);
      } else {
        if (macroCondition(isDevelopingApp())) {
          recordData = this.__cacheManager = this.__cacheManager || new SingletonRecordDataManager();
          recordData._addRecordData(identifier, recordDataInstance);
        } else {
          recordData = recordDataInstance;
        }
      }
      setRecordDataFor(identifier, recordData);
      this.__instances.recordData.set(identifier, recordData);
      if (macroCondition(getOwnConfig().debug.LOG_INSTANCE_CACHE)) {
        // eslint-disable-next-line no-console
        console.log(`InstanceCache: created RecordData for ${String(identifier)}`);
      }
    }
    return recordData;
  }
  getReference(identifier) {
    let cache = this.__instances.reference;
    let reference = cache.get(identifier);
    if (!reference) {
      reference = new RecordReference(this.store, identifier);
      cache.set(identifier, reference);
    }
    return reference;
  }
  recordIsLoaded(identifier, filterDeleted) {
    if (filterDeleted === void 0) {
      filterDeleted = false;
    }
    const recordData = this.__instances.recordData.get(identifier);
    if (!recordData) {
      return false;
    }
    const isNew = recordData.isNew(identifier);
    const isEmpty = recordData.isEmpty(identifier);

    // if we are new we must consider ourselves loaded
    if (isNew) {
      return !recordData.isDeleted(identifier);
    }
    // even if we have a past request, if we are now empty we are not loaded
    // typically this is true after an unloadRecord call

    // if we are not empty, not new && we have a fulfilled request then we are loaded
    // we should consider allowing for something to be loaded that is simply "not empty".
    // which is how RecordState currently handles this case; however, RecordState is buggy
    // in that it does not account for unloading.
    return filterDeleted && recordData.isDeletionCommitted(identifier) ? false : !isEmpty;

    /*
    const req = this.store.getRequestStateService();
    const fulfilled = req.getLastRequestForRecord(identifier);
    const isLocallyLoaded = !isEmpty;
    const isLoading =
      !isLocallyLoaded &&
      fulfilled === null &&
      req.getPendingRequestsForRecord(identifier).some((req) => req.type === 'query');
     if (isEmpty || (filterDeleted && recordData.isDeletionCommitted(identifier)) || isLoading) {
      return false;
    }
     return true;
    */
  }

  createSnapshot(identifier, options) {
    if (options === void 0) {
      options = {};
    }
    return new Snapshot(options, identifier, this.store);
  }
  disconnect(identifier) {
    this.__instances.record.get(identifier);
    if (macroCondition(dependencySatisfies("@ember-data/record-data", "*"))) {
      let graph = _peekGraph(this.store);
      if (graph) {
        graph.remove(identifier);
      }
    }
    this.store.identifierCache.forgetRecordIdentifier(identifier);
    if (macroCondition(getOwnConfig().debug.LOG_INSTANCE_CACHE)) {
      // eslint-disable-next-line no-console
      console.log(`InstanceCache: disconnected ${String(identifier)}`);
    }
  }
  unloadRecord(identifier) {
    if (macroCondition(isDevelopingApp())) {
      const requests = this.store.getRequestStateService().getPendingRequestsForRecord(identifier);
      if (requests.some(req => {
        return req.type === 'mutation';
      })) ;
    }
    if (macroCondition(getOwnConfig().debug.LOG_INSTANCE_CACHE)) {
      // eslint-disable-next-line no-console
      console.groupCollapsed(`InstanceCache: unloading record for ${String(identifier)}`);
    }

    // TODO is this join still necessary?
    this.store._join(() => {
      const record = this.__instances.record.get(identifier);
      const recordData = this.__instances.recordData.get(identifier);
      if (record) {
        this.store.teardownRecord(record);
        this.__instances.record.delete(identifier);
        StoreMap.delete(record);
        RecordCache.delete(record);
        removeRecordDataFor(record);
        if (macroCondition(getOwnConfig().debug.LOG_INSTANCE_CACHE)) {
          // eslint-disable-next-line no-console
          console.log(`InstanceCache: destroyed record for ${String(identifier)}`);
        }
      }
      if (recordData) {
        recordData.unloadRecord(identifier);
        this.__instances.recordData.delete(identifier);
        removeRecordDataFor(identifier);
      } else {
        this.disconnect(identifier);
      }
      this.store._fetchManager.clearEntries(identifier);
      this.store.recordArrayManager.identifierRemoved(identifier);
      if (macroCondition(getOwnConfig().debug.LOG_INSTANCE_CACHE)) {
        // eslint-disable-next-line no-console
        console.log(`InstanceCache: unloaded RecordData for ${String(identifier)}`);
        // eslint-disable-next-line no-console
        console.groupEnd();
      }
    });
  }
  clear(type) {
    const typeCache = this.store.identifierCache._cache.types;
    if (type === undefined) {
      this.__instances.recordData.forEach((value, identifier) => {
        this.unloadRecord(identifier);
      });
    } else {
      let identifiers = typeCache[type]?.lid;
      const rds = this.__instances.recordData;
      if (identifiers) {
        identifiers.forEach(identifier => {
          if (rds.has(identifier)) {
            this.unloadRecord(identifier);
          }
          // TODO we don't remove the identifier, should we?
        });
      }
    }
  }

  // TODO this should move into the network layer
  _fetchDataIfNeededForIdentifier(identifier, options) {
    if (options === void 0) {
      options = {};
    }
    // pre-loading will change the isEmpty value
    const isEmpty = _isEmpty(this, identifier);
    const isLoading = _isLoading(this, identifier);
    if (options.preload) {
      this.store._join(() => {
        preloadData(this.store, identifier, options.preload);
      });
    }
    let promise;
    if (isEmpty) {
      promise = this.store._fetchManager.scheduleFetch(identifier, options);
    } else if (isLoading) {
      promise = this.store._fetchManager.getPendingFetch(identifier, options);
    } else {
      promise = resolve(identifier);
    }
    return promise;
  }

  // TODO this should move into something coordinating operations
  setRecordId(identifier, id) {
    const {
      type,
      lid
    } = identifier;
    let oldId = identifier.id;
    // however, this is more than likely a developer error.
    if (oldId !== null && id === null) {
      return;
    }
    if (macroCondition(getOwnConfig().debug.LOG_INSTANCE_CACHE)) {
      // eslint-disable-next-line no-console
      console.log(`InstanceCache: updating id to '${id}' for record ${String(identifier)}`);
    }
    this.store.identifierCache.peekRecordIdentifier({
      type,
      id
    });
    if (identifier.id === null) {
      // TODO potentially this needs to handle merged result
      this.store.identifierCache.updateRecordIdentifier(identifier, {
        type,
        id
      });
    }

    // TODO update recordData if needed ?
    // TODO handle consequences of identifier merge for notifications
    this.store._notificationManager.notify(identifier, 'identity');
  }

  // TODO this should move into something coordinating operations
  loadData(data) {
    data.type;
    const resource = constructResource(normalizeModelName$1(data.type), ensureStringId(data.id), coerceId(data.lid));
    let identifier = this.store.identifierCache.peekRecordIdentifier(resource);
    let isUpdate = false;

    // store.push will be from empty
    // findRecord will be from root.loading
    // this cannot be loading state if we do not already have an identifier
    // all else will be updates
    if (identifier) {
      const isLoading = _isLoading(this, identifier) || !this.recordIsLoaded(identifier);
      isUpdate = !_isEmpty(this, identifier) && !isLoading;

      // exclude store.push (root.empty) case
      if (isUpdate || isLoading) {
        identifier = this.store.identifierCache.updateRecordIdentifier(identifier, data);
      }
    } else {
      identifier = this.store.identifierCache.getOrCreateRecordIdentifier(data);
    }
    const recordData = this.getRecordData(identifier);
    if (recordData.isNew(identifier)) {
      this.store._notificationManager.notify(identifier, 'identity');
    }
    const hasRecord = this.__instances.record.has(identifier);
    recordData.pushData(identifier, data, hasRecord);
    if (!isUpdate) {
      this.store.recordArrayManager.identifierAdded(identifier);
    }
    return identifier;
  }
}
function _recordDataIsFullDeleted(identifier, recordData) {
  return recordData.isDeletionCommitted(identifier) || recordData.isNew(identifier) && recordData.isDeleted(identifier);
}
function recordDataIsFullyDeleted(cache, identifier) {
  let recordData = cache.__instances.recordData.get(identifier);
  return !recordData || _recordDataIsFullDeleted(identifier, recordData);
}

/*
    When a find request is triggered on the store, the user can optionally pass in
    attributes and relationships to be preloaded. These are meant to behave as if they
    came back from the server, except the user obtained them out of band and is informing
    the store of their existence. The most common use case is for supporting client side
    nested URLs, such as `/posts/1/comments/2` so the user can do
    `store.findRecord('comment', 2, { preload: { post: 1 } })` without having to fetch the post.

    Preloaded data can be attributes and relationships passed in either as IDs or as actual
    models.
  */

function preloadData(store, identifier, preload) {
  let jsonPayload = {};
  //TODO(Igor) consider the polymorphic case
  const schemas = store.getSchemaDefinitionService();
  const relationships = schemas.relationshipsDefinitionFor(identifier);
  Object.keys(preload).forEach(key => {
    let preloadValue = preload[key];
    let relationshipMeta = relationships[key];
    if (relationshipMeta) {
      if (!jsonPayload.relationships) {
        jsonPayload.relationships = {};
      }
      jsonPayload.relationships[key] = preloadRelationship(relationshipMeta, preloadValue);
    } else {
      if (!jsonPayload.attributes) {
        jsonPayload.attributes = {};
      }
      jsonPayload.attributes[key] = preloadValue;
    }
  });
  store._instanceCache.getRecordData(identifier).pushData(identifier, jsonPayload);
}
function preloadRelationship(schema, preloadValue) {
  const relatedType = schema.type;
  if (schema.kind === 'hasMany') {
    return {
      data: preloadValue.map(value => _convertPreloadRelationshipToJSON(value, relatedType))
    };
  }
  return {
    data: preloadValue ? _convertPreloadRelationshipToJSON(preloadValue, relatedType) : null
  };
}

/*
  findRecord('user', '1', { preload: { friends: ['1'] }});
  findRecord('user', '1', { preload: { friends: [record] }});
*/
function _convertPreloadRelationshipToJSON(value, type) {
  if (typeof value === 'string' || typeof value === 'number') {
    return {
      type,
      id: value
    };
  }
  // TODO if not a record instance assert it's an identifier
  // and allow identifiers to be used
  return recordIdentifierFor(value);
}
function _isEmpty(cache, identifier) {
  const recordData = cache.__instances.recordData.get(identifier);
  if (!recordData) {
    return true;
  }
  const isNew = recordData.isNew(identifier);
  const isDeleted = recordData.isDeleted(identifier);
  const isEmpty = recordData.isEmpty(identifier);
  return (!isNew || isDeleted) && isEmpty;
}
function _isLoading(cache, identifier) {
  const req = cache.store.getRequestStateService();
  // const fulfilled = req.getLastRequestForRecord(identifier);
  const isLoaded = cache.recordIsLoaded(identifier);
  return !isLoaded &&
  // fulfilled === null &&
  req.getPendingRequestsForRecord(identifier).some(req => req.type === 'query');
}
function _clearCaches() {
  RecordCache.clear();
  StoreMap.clear();
  RecordDataForIdentifierCache.clear();
}
let _modelForMixin;
if (macroCondition(dependencySatisfies("@ember-data/model", "*"))) {
  let _found;
  _modelForMixin = function () {
    if (!_found) {
      _found = importSync('@ember-data/model/-private')._modelForMixin;
    }
    return _found(...arguments);
  };
}
class DSModelSchemaDefinitionService {
  constructor(store) {
    this.store = store;
    this._relationshipsDefCache = Object.create(null);
    this._attributesDefCache = Object.create(null);
  }

  // Following the existing RD implementation
  attributesDefinitionFor(identifier) {
    let modelName, attributes;
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_STRING_ARG_SCHEMAS)) {
      if (typeof identifier === 'string') {
        modelName = identifier;
      } else {
        modelName = identifier.type;
      }
    } else {
      modelName = identifier.type;
    }
    attributes = this._attributesDefCache[modelName];
    if (attributes === undefined) {
      let modelClass = this.store.modelFor(modelName);
      let attributeMap = modelClass.attributes;
      attributes = Object.create(null);
      attributeMap.forEach((meta, name) => attributes[name] = meta);
      this._attributesDefCache[modelName] = attributes;
    }
    return attributes;
  }

  // Following the existing RD implementation
  relationshipsDefinitionFor(identifier) {
    let modelName, relationships;
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_STRING_ARG_SCHEMAS)) {
      if (typeof identifier === 'string') {
        modelName = identifier;
      } else {
        modelName = identifier.type;
      }
    } else {
      modelName = identifier.type;
    }
    relationships = this._relationshipsDefCache[modelName];
    if (relationships === undefined) {
      let modelClass = this.store.modelFor(modelName);
      relationships = modelClass.relationshipsObject || null;
      this._relationshipsDefCache[modelName] = relationships;
    }
    return relationships;
  }
  doesTypeExist(modelName) {
    let normalizedModelName = normalizeModelName$1(modelName);
    let factory = getModelFactory(this.store, this.store._modelFactoryCache, normalizedModelName);
    return factory !== null;
  }
}
function getModelFactory(store, cache, normalizedModelName) {
  let factory = cache[normalizedModelName];
  if (!factory) {
    let owner = getOwner(store);
    factory = owner.factoryFor(`model:${normalizedModelName}`);
    if (macroCondition(dependencySatisfies("@ember-data/model", "*"))) {
      if (!factory) {
        //Support looking up mixins as base types for polymorphic relationships
        factory = _modelForMixin(store, normalizedModelName);
      }
    }
    if (!factory) {
      // we don't cache misses in case someone wants to register a missing model
      return null;
    }
    let klass = factory.class;
    if (klass.isModel) {
      let hasOwnModelNameSet = klass.modelName && Object.prototype.hasOwnProperty.call(klass, 'modelName');
      if (!hasOwnModelNameSet) {
        Object.defineProperty(klass, 'modelName', {
          value: normalizedModelName
        });
      }
    }
    cache[normalizedModelName] = factory;
  }
  return factory;
}

// if modelFor turns out to be a bottleneck we should replace with a Map
// and clear it during store teardown.
const AvailableShims = new WeakMap();
function getShimClass(store, modelName) {
  let shims = AvailableShims.get(store);
  if (!shims) {
    shims = Object.create(null);
    AvailableShims.set(store, shims);
  }
  let shim = shims[modelName];
  if (shim === undefined) {
    shim = shims[modelName] = new ShimModelClass(store, modelName);
  }
  return shim;
}
function mapFromHash(hash) {
  let map = new Map();
  for (let i in hash) {
    if (Object.prototype.hasOwnProperty.call(hash, i)) {
      map.set(i, hash[i]);
    }
  }
  return map;
}

// Mimics the static apis of DSModel
class ShimModelClass {
  constructor(store, modelName) {
    this.__store = store;
    this.modelName = modelName;
  }
  get fields() {
    let attrs = this.__store.getSchemaDefinitionService().attributesDefinitionFor({
      type: this.modelName
    });
    let relationships = this.__store.getSchemaDefinitionService().relationshipsDefinitionFor({
      type: this.modelName
    });
    let fields = new Map();
    Object.keys(attrs).forEach(key => fields.set(key, 'attribute'));
    Object.keys(relationships).forEach(key => fields.set(key, relationships[key].kind));
    return fields;
  }
  get attributes() {
    let attrs = this.__store.getSchemaDefinitionService().attributesDefinitionFor({
      type: this.modelName
    });
    return mapFromHash(attrs);
  }
  get relationshipsByName() {
    let relationships = this.__store.getSchemaDefinitionService().relationshipsDefinitionFor({
      type: this.modelName
    });
    return mapFromHash(relationships);
  }
  eachAttribute(callback, binding) {
    let attrDefs = this.__store.getSchemaDefinitionService().attributesDefinitionFor({
      type: this.modelName
    });
    Object.keys(attrDefs).forEach(key => {
      callback.call(binding, key, attrDefs[key]);
    });
  }
  eachRelationship(callback, binding) {
    let relationshipDefs = this.__store.getSchemaDefinitionService().relationshipsDefinitionFor({
      type: this.modelName
    });
    Object.keys(relationshipDefs).forEach(key => {
      callback.call(binding, key, relationshipDefs[key]);
    });
  }
  eachTransformedAttribute(callback, binding) {
    let relationshipDefs = this.__store.getSchemaDefinitionService().relationshipsDefinitionFor({
      type: this.modelName
    });
    Object.keys(relationshipDefs).forEach(key => {
      if (relationshipDefs[key].type) {
        callback.call(binding, key, relationshipDefs[key]);
      }
    });
  }
}
const PromiseArrayProxy = ArrayProxy.extend(PromiseProxyMixin);
const PromiseObjectProxy = ObjectProxy.extend(PromiseProxyMixin);
var _dec, _class$1, _descriptor$1;
function _initializerDefineProperty$1(target, property, descriptor, context) {
  if (!descriptor) return;
  Object.defineProperty(target, property, {
    enumerable: descriptor.enumerable,
    configurable: descriptor.configurable,
    writable: descriptor.writable,
    value: descriptor.initializer ? descriptor.initializer.call(context) : void 0
  });
}
function _applyDecoratedDescriptor$1(target, property, decorators, descriptor, context) {
  var desc = {};
  Object.keys(descriptor).forEach(function (key) {
    desc[key] = descriptor[key];
  });
  desc.enumerable = !!desc.enumerable;
  desc.configurable = !!desc.configurable;
  if ('value' in desc || desc.initializer) {
    desc.writable = true;
  }
  desc = decorators.slice().reverse().reduce(function (desc, decorator) {
    return decorator(target, property, desc) || desc;
  }, desc);
  if (context && desc.initializer !== void 0) {
    desc.value = desc.initializer ? desc.initializer.call(context) : void 0;
    desc.initializer = undefined;
  }
  if (desc.initializer === void 0) {
    Object.defineProperty(target, property, desc);
    desc = null;
  }
  return desc;
}

/**
  @module @ember-data/store
*/

/**
  A `PromiseArray` is an object that acts like both an `Ember.Array`
  and a promise. When the promise is resolved the resulting value
  will be set to the `PromiseArray`'s `content` property. This makes
  it easy to create data bindings with the `PromiseArray` that will be
  updated when the promise resolves.

  This class should not be imported and instantiated directly.

  For more information see the [Ember.PromiseProxyMixin
  documentation](/ember/release/classes/PromiseProxyMixin).

  Example

  ```javascript
  let promiseArray = PromiseArray.create({
    promise: $.getJSON('/some/remote/data.json')
  });

  promiseArray.length; // 0

  promiseArray.then(function() {
    promiseArray.length; // 100
  });
  ```

  @class PromiseArray
  @public
  @extends Ember.ArrayProxy
  @uses Ember.PromiseProxyMixin
*/
let PromiseArray = (_dec = reads('content.meta'), (_class$1 = class PromiseArray extends PromiseArrayProxy {
  constructor() {
    super(...arguments);
    _initializerDefineProperty$1(this, "meta", _descriptor$1, this);
  }
}, _descriptor$1 = _applyDecoratedDescriptor$1(_class$1.prototype, "meta", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _class$1));
function _promiseObject(promise, label) {
  return PromiseObjectProxy.create({
    promise: resolve(promise, label)
  });
}
function _promiseArray(promise, label) {
  return PromiseArray.create({
    promise: resolve(promise, label)
  });
}

// constructor is accessed in some internals but not including it in the copyright for the deprecation
const ALLOWABLE_METHODS = ['constructor', 'then', 'catch', 'finally'];
const ALLOWABLE_PROPS = ['__ec_yieldable__', '__ec_cancel__'];
const PROXIED_ARRAY_PROPS = ['length', '[]', 'firstObject', 'lastObject', 'meta', 'content', 'isPending', 'isSettled', 'isRejected', 'isFulfilled', 'promise', 'reason'];
const PROXIED_OBJECT_PROPS = ['content', 'isPending', 'isSettled', 'isRejected', 'isFulfilled', 'promise', 'reason'];
function promiseArray(promise) {
  const promiseObjectProxy = _promiseArray(promise);
  if (macroCondition(!isDevelopingApp())) {
    return promiseObjectProxy;
  }
  const handler = {
    get(target, prop, receiver) {
      if (typeof prop === 'symbol') {
        return Reflect.get(target, prop, receiver);
      }
      if (ALLOWABLE_PROPS.includes(prop)) {
        return target[prop];
      }
      const value = target[prop];
      if (value && typeof value === 'function' && typeof value.bind === 'function') {
        return value.bind(target);
      }
      if (PROXIED_ARRAY_PROPS.includes(prop)) {
        return value;
      }
      return undefined;
    }
  };
  return new Proxy(promiseObjectProxy, handler);
}
const ProxySymbolString = String(Symbol.for('PROXY_CONTENT'));
function promiseObject(promise) {
  const promiseObjectProxy = _promiseObject(promise);
  if (macroCondition(!isDevelopingApp())) {
    return promiseObjectProxy;
  }
  const handler = {
    get(target, prop, receiver) {
      if (typeof prop === 'symbol') {
        if (String(prop) === ProxySymbolString) {
          return;
        }
        return Reflect.get(target, prop, receiver);
      }
      if (prop === 'constructor') {
        return target.constructor;
      }
      if (ALLOWABLE_PROPS.includes(prop)) {
        return target[prop];
      }
      if (!ALLOWABLE_METHODS.includes(prop)) ;else {
        return target[prop].bind(target);
      }
      if (PROXIED_OBJECT_PROPS.includes(prop)) {
        return target[prop];
      }
      const value = get(target, prop);
      if (value && typeof value === 'function' && typeof value.bind === 'function') {
        return value.bind(receiver);
      }
      return undefined;
    }
  };
  return new Proxy(promiseObjectProxy, handler);
}
var _class, _descriptor, _class3, _descriptor2;
function _initializerDefineProperty(target, property, descriptor, context) {
  if (!descriptor) return;
  Object.defineProperty(target, property, {
    enumerable: descriptor.enumerable,
    configurable: descriptor.configurable,
    writable: descriptor.writable,
    value: descriptor.initializer ? descriptor.initializer.call(context) : void 0
  });
}
function _applyDecoratedDescriptor(target, property, decorators, descriptor, context) {
  var desc = {};
  Object.keys(descriptor).forEach(function (key) {
    desc[key] = descriptor[key];
  });
  desc.enumerable = !!desc.enumerable;
  desc.configurable = !!desc.configurable;
  if ('value' in desc || desc.initializer) {
    desc.writable = true;
  }
  desc = decorators.slice().reverse().reduce(function (desc, decorator) {
    return decorator(target, property, desc) || desc;
  }, desc);
  if (context && desc.initializer !== void 0) {
    desc.value = desc.initializer ? desc.initializer.call(context) : void 0;
    desc.initializer = undefined;
  }
  if (desc.initializer === void 0) {
    Object.defineProperty(target, property, desc);
    desc = null;
  }
  return desc;
}
const ARRAY_GETTER_METHODS = new Set([Symbol.iterator, 'concat', 'entries', 'every', 'fill', 'filter', 'find', 'findIndex', 'flat', 'flatMap', 'forEach', 'includes', 'indexOf', 'join', 'keys', 'lastIndexOf', 'map', 'reduce', 'reduceRight', 'slice', 'some', 'values']);
const ARRAY_SETTER_METHODS = new Set(['push', 'pop', 'unshift', 'shift', 'splice', 'sort']);
const SYNC_PROPS = new Set(['[]', 'length', 'links', 'meta']);
function isArrayGetter(prop) {
  return ARRAY_GETTER_METHODS.has(prop);
}
function isArraySetter(prop) {
  return ARRAY_SETTER_METHODS.has(prop);
}
const IDENTIFIER_ARRAY_TAG = Symbol('#tag');
const SOURCE = Symbol('#source');
const MUTATE = Symbol('#update');
const NOTIFY = Symbol('#notify');
function notifyArray(arr) {
  arr[IDENTIFIER_ARRAY_TAG].ref = null;
  if (macroCondition(getOwnConfig().deprecations.DEPRECATE_COMPUTED_CHAINS)) {
    // eslint-disable-next-line
    dirtyTag(tagForProperty(arr, 'length'));
    // eslint-disable-next-line
    dirtyTag(tagForProperty(arr, '[]'));
  }
}
function convertToInt(prop) {
  if (typeof prop === 'symbol') return null;
  const num = Number(prop);
  if (isNaN(num)) return null;
  return num % 1 === 0 ? num : null;
}
let Tag = (_class = class Tag {
  /*
   * whether this was part of a transaction when last mutated
   */

  constructor() {
    _initializerDefineProperty(this, "ref", _descriptor, this);
    this.shouldReset = false;
    this.t = false;
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "ref", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _class);
function deprecateArrayLike(className, fnName, replName) {}

/**
  A record array is an array that contains records of a certain type (or modelName).
  The record array materializes records as needed when they are retrieved for the first
  time. You should not create record arrays yourself. Instead, an instance of
  `RecordArray` or its subclasses will be returned by your application's store
  in response to queries.

  This class should not be imported and instantiated by consuming applications.

  @class RecordArray
  @public
*/
let IdentifierArray = (_class3 = class IdentifierArray {
  [NOTIFY]() {
    notifyArray(this);
  }

  /**
   The modelClass represented by this record array.
    @property type
    @public
    @deprecated
   @type {subclass of Model}
   */

  /**
    The store that created this record array.
     @property store
    @private
    @type Store
    */

  destroy() {
    this.isDestroying = true;
    // changing the reference breaks the Proxy
    // this[SOURCE] = [];
    this[SOURCE].length = 0;
    this[NOTIFY]();
    this.isDestroyed = true;
  }

  // length must be on self for proxied methods to work properly
  get length() {
    return this[SOURCE].length;
  }
  set length(value) {
    this[SOURCE].length = value;
  }

  // here to support computed chains
  // and {{#each}}
  get '[]'() {
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_COMPUTED_CHAINS)) {
      return this;
    }
  }
  constructor(options) {
    /**
      The flag to signal a `RecordArray` is currently loading data.
      Example
      ```javascript
      let people = store.peekAll('person');
      people.isUpdating; // false
      people.update();
      people.isUpdating; // true
      ```
      @property isUpdating
      @public
      @type Boolean
    */
    _initializerDefineProperty(this, "isUpdating", _descriptor2, this);
    this.isLoaded = true;
    this.isDestroying = false;
    this.isDestroyed = false;
    this._updatingPromise = null;
    this[IDENTIFIER_ARRAY_TAG] = new Tag();
    this[SOURCE] = void 0;
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    let self = this;
    this.modelName = options.type;
    this.store = options.store;
    this._manager = options.manager;
    this[SOURCE] = options.identifiers;
    const store = options.store;
    const boundFns = new Map();
    const _TAG = this[IDENTIFIER_ARRAY_TAG];
    const PrivateState = {
      links: options.links || null,
      meta: options.meta || null
    };
    let transaction = false;

    // when a mutation occurs
    // we track all mutations within the call
    // and forward them as one

    const proxy = new Proxy(this[SOURCE], {
      get(target, prop, receiver) {
        let index = convertToInt(prop);
        if (_TAG.shouldReset && (index !== null || SYNC_PROPS.has(prop) || isArrayGetter(prop))) {
          options.manager._syncArray(receiver);
          _TAG.t = false;
          _TAG.shouldReset = false;
        }
        if (index !== null) {
          const identifier = target[index];
          if (!transaction) {
            subscribe(_TAG);
          }
          return identifier && store._instanceCache.getRecord(identifier);
        }
        if (prop === 'meta') return subscribe(_TAG), PrivateState.meta;
        if (prop === 'links') return subscribe(_TAG), PrivateState.links;
        if (prop === '[]') return subscribe(_TAG), receiver;
        if (isArrayGetter(prop)) {
          let fn = boundFns.get(prop);
          if (fn === undefined) {
            fn = function () {
              subscribe(_TAG);
              // array functions must run through Reflect to work properly
              // binding via other means will not work.
              transaction = true;
              let result = Reflect.apply(target[prop], receiver, arguments);
              transaction = false;
              return result;
            };
            boundFns.set(prop, fn);
          }
          return fn;
        }
        if (isArraySetter(prop)) {
          let fn = boundFns.get(prop);
          if (fn === undefined) {
            fn = function () {
              // array functions must run through Reflect to work properly
              // binding via other means will not work.
              if (!options.allowMutation) {
                return;
              }
              const args = Array.prototype.slice.call(arguments);
              transaction = true;
              let result = Reflect.apply(target[prop], receiver, args);
              self[MUTATE](prop, args, result);
              addToTransaction(_TAG);
              // TODO handle cache updates
              transaction = false;
              return result;
            };
            boundFns.set(prop, fn);
          }
          return fn;
        }
        if (prop in self) {
          if (macroCondition(getOwnConfig().deprecations.DEPRECATE_ARRAY_LIKE)) {
            if (prop === 'firstObject') {
              deprecateArrayLike(self.DEPRECATED_CLASS_NAME);
              return receiver[0];
            } else if (prop === 'lastObject') {
              deprecateArrayLike(self.DEPRECATED_CLASS_NAME);
              return receiver[receiver.length - 1];
            }
          }
          if (prop === NOTIFY || prop === IDENTIFIER_ARRAY_TAG || prop === SOURCE) {
            return self[prop];
          }
          let fn = boundFns.get(prop);
          if (fn) return fn;
          let outcome = self[prop];
          if (typeof outcome === 'function') {
            fn = function () {
              subscribe(_TAG);
              // array functions must run through Reflect to work properly
              // binding via other means will not work.
              return Reflect.apply(outcome, receiver, arguments);
            };
            boundFns.set(prop, fn);
            return fn;
          }
          return subscribe(_TAG), outcome;
        }
        return target[prop];
      },
      set(target, prop, value) {
        if (prop === 'length') {
          if (!transaction && value === 0) {
            transaction = true;
            addToTransaction(_TAG);
            Reflect.set(target, prop, value);
            self[MUTATE]('length 0', []);
            transaction = false;
            return true;
          } else if (transaction) {
            return Reflect.set(target, prop, value);
          } else ;
        }
        if (prop === 'links') {
          PrivateState.links = value || null;
          return true;
        }
        if (prop === 'meta') {
          PrivateState.meta = value || null;
          return true;
        }
        let index = convertToInt(prop);
        if (index === null || index > target.length) {
          if (prop in self) {
            self[prop] = value;
            return true;
          }
          return false;
        }
        if (!options.allowMutation) {
          return false;
        }
        let original = target[index];
        let newIdentifier = extractIdentifierFromRecord$1(value);
        target[index] = newIdentifier;
        if (!transaction) {
          self[MUTATE]('replace cell', [index, original, newIdentifier]);
          addToTransaction(_TAG);
        }
        return true;
      },
      deleteProperty(target, prop) {
        if (!transaction) {
          return false;
        }
        return Reflect.deleteProperty(target, prop);
      },
      getPrototypeOf() {
        return IdentifierArray.prototype;
      }
    });
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_A_USAGE)) {
      const meta = Ember.meta(this);
      meta.hasMixin = mixin => {
        if (mixin === NativeArray || mixin === ArrayMixin) {
          return true;
        }
        return false;
      };
    } else if (macroCondition(isDevelopingApp())) {
      const meta = Ember.meta(this);
      meta.hasMixin = mixin => {};
    }
    this[NOTIFY] = this[NOTIFY].bind(proxy);
    return proxy;
  }

  /**
    Used to get the latest version of all of the records in this array
    from the adapter.
     Example
     ```javascript
    let people = store.peekAll('person');
    people.isUpdating; // false
     people.update().then(function() {
      people.isUpdating; // false
    });
     people.isUpdating; // true
    ```
     @method update
    @public
  */
  update() {
    if (this.isUpdating) {
      return this._updatingPromise;
    }
    this.isUpdating = true;
    let updatingPromise = this._update();
    updatingPromise.finally(() => {
      this._updatingPromise = null;
      if (this.isDestroying || this.isDestroyed) {
        return;
      }
      this.isUpdating = false;
    });
    this._updatingPromise = updatingPromise;
    return updatingPromise;
  }

  /*
    Update this RecordArray and return a promise which resolves once the update
    is finished.
   */
  _update() {
    return this.store.findAll(this.modelName, {
      reload: true
    });
  }

  // TODO deprecate
  /**
    Saves all of the records in the `RecordArray`.
     Example
     ```javascript
    let messages = store.peekAll('message');
    messages.forEach(function(message) {
      message.hasBeenSeen = true;
    });
    messages.save();
    ```
     @method save
    @public
    @return {PromiseArray} promise
  */
  save() {
    let promise = Promise.all(this.map(record => this.store.saveRecord(record))).then(() => this);
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_PROMISE_PROXIES)) {
      return promiseArray(promise);
    }
    return promise;
  }
}, (_descriptor2 = _applyDecoratedDescriptor(_class3.prototype, "isUpdating", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _applyDecoratedDescriptor(_class3.prototype, "length", [dependentKeyCompat], Object.getOwnPropertyDescriptor(_class3.prototype, "length"), _class3.prototype)), _class3);
if (macroCondition(getOwnConfig().deprecations.DEPRECATE_SNAPSHOT_MODEL_CLASS_ACCESS)) {
  Object.defineProperty(IdentifierArray.prototype, 'type', {
    get() {
      if (!this.modelName) {
        return null;
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      return this.store.modelFor(this.modelName);
    }
  });
}
class Collection extends IdentifierArray {
  constructor(options) {
    super(options);
    this.query = null;
    this.query = options.query || null;
    this.isLoaded = options.isLoaded || false;
  }
  _update() {
    const {
      store,
      query
    } = this;

    // TODO save options from initial request?
    const promise = store.query(this.modelName, query, {
      _recordArray: this
    });
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_PROMISE_PROXIES)) {
      return promiseArray(promise);
    }
    return promise;
  }
  destroy() {
    super.destroy();
    this._manager._managed.delete(this);
    this._manager._pending.delete(this);
  }
}
// trick the proxy "in" check
Collection.prototype.query = null;

// Ensure instanceof works correctly
//Object.setPrototypeOf(IdentifierArray.prototype, Array.prototype);

if (macroCondition(getOwnConfig().deprecations.DEPRECATE_ARRAY_LIKE)) {
  IdentifierArray.prototype.DEPRECATED_CLASS_NAME = 'RecordArray';
  Collection.prototype.DEPRECATED_CLASS_NAME = 'RecordArray';
  const EmberObjectMethods = ['addObserver', 'cacheFor', 'decrementProperty', 'get', 'getProperties', 'incrementProperty', 'notifyPropertyChange', 'removeObserver', 'set', 'setProperties', 'toggleProperty'];
  EmberObjectMethods.forEach(method => {
    IdentifierArray.prototype[method] = function delegatedMethod() {
      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }
      return Ember[method](this, ...args);
    };
  });
  IdentifierArray.prototype.addObject = function (obj) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
    let index = this.indexOf(obj);
    if (index === -1) {
      this.push(obj);
    }
    return this;
  };
  IdentifierArray.prototype.addObjects = function (objs) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
    objs.forEach(obj => {
      let index = this.indexOf(obj);
      if (index === -1) {
        this.push(obj);
      }
    });
    return this;
  };
  IdentifierArray.prototype.popObject = function () {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
    return this.pop();
  };
  IdentifierArray.prototype.pushObject = function (obj) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
    this.push(obj);
    return obj;
  };
  IdentifierArray.prototype.pushObjects = function (objs) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
    this.push(...objs);
    return this;
  };
  IdentifierArray.prototype.shiftObject = function () {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
    return this.shift();
  };
  IdentifierArray.prototype.unshiftObject = function (obj) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
    this.unshift(obj);
    return obj;
  };
  IdentifierArray.prototype.unshiftObjects = function (objs) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
    this.unshift(...objs);
    return this;
  };
  IdentifierArray.prototype.objectAt = function (index) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
    //For negative index values go back from the end of the array
    let arrIndex = Math.sign(index) === -1 ? this.length + index : index;
    return this[arrIndex];
  };
  IdentifierArray.prototype.objectsAt = function (indeces) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
    return indeces.map(index => this.objectAt(index));
  };
  IdentifierArray.prototype.removeAt = function (index) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
    this.splice(index, 1);
    return this;
  };
  IdentifierArray.prototype.insertAt = function (index, obj) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
    this.splice(index, 0, obj);
    return this;
  };
  IdentifierArray.prototype.removeObject = function (obj) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
    this.splice(this.indexOf(obj), 1);
    return this;
  };
  IdentifierArray.prototype.removeObjects = function (objs) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
    objs.forEach(obj => this.splice(this.indexOf(obj), 1));
    return this;
  };
  IdentifierArray.prototype.toArray = function () {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
    return this.slice();
  };
  IdentifierArray.prototype.replace = function (idx, amt, objects) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
    if (objects) {
      this.splice(idx, amt, ...objects);
    } else {
      this.splice(idx, amt);
    }
  };
  IdentifierArray.prototype.clear = function () {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
    this.splice(0, this.length);
    return this;
  };
  IdentifierArray.prototype.setObjects = function (objects) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
    this.splice(0, this.length);
    this.push(...objects);
    return this;
  };
  IdentifierArray.prototype.reverseObjects = function () {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
    this.reverse();
    return this;
  };
  IdentifierArray.prototype.compact = function () {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
    return this.filter(v => v !== null && v !== undefined);
  };
  IdentifierArray.prototype.any = function (callback, target) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
    return this.some(callback, target);
  };
  IdentifierArray.prototype.isAny = function (prop, value) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
    let hasValue = arguments.length === 2;
    return this.some(v => hasValue ? v[prop] === value : v[prop] === true);
  };
  IdentifierArray.prototype.isEvery = function (prop, value) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
    let hasValue = arguments.length === 2;
    return this.every(v => hasValue ? v[prop] === value : v[prop] === true);
  };
  IdentifierArray.prototype.getEach = function (key) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
    return this.map(value => get(value, key));
  };
  IdentifierArray.prototype.mapBy = function (key) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
    return this.map(value => get(value, key));
  };
  IdentifierArray.prototype.findBy = function (key, value) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
    if (arguments.length === 2) {
      return this.find(val => {
        return get(val, key) === value;
      });
    } else {
      return this.find(val => Boolean(get(val, key)));
    }
  };
  IdentifierArray.prototype.filterBy = function (key, value) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
    if (arguments.length === 2) {
      return this.filter(record => {
        return get(record, key) === value;
      });
    }
    return this.filter(record => {
      return Boolean(get(record, key));
    });
  };
  IdentifierArray.prototype.sortBy = function () {
    for (var _len2 = arguments.length, sortKeys = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      sortKeys[_key2] = arguments[_key2];
    }
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
    return this.slice().sort((a, b) => {
      for (let i = 0; i < sortKeys.length; i++) {
        let key = sortKeys[i];
        let propA = get(a, key);
        let propB = get(b, key);
        // return 1 or -1 else continue to the next sortKey
        let compareValue = compare(propA, propB);
        if (compareValue) {
          return compareValue;
        }
      }
      return 0;
    });
  };

  // @ts-expect-error
  IdentifierArray.prototype.invoke = function (key) {
    for (var _len3 = arguments.length, args = new Array(_len3 > 1 ? _len3 - 1 : 0), _key3 = 1; _key3 < _len3; _key3++) {
      args[_key3 - 1] = arguments[_key3];
    }
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
    return this.map(value => value[key](...args));
  };

  // @ts-expect-error
  IdentifierArray.prototype.addArrayObserver = function () {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
  };

  // @ts-expect-error
  IdentifierArray.prototype.removeArrayObserver = function () {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
  };

  // @ts-expect-error
  IdentifierArray.prototype.arrayContentWillChange = function () {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
  };

  // @ts-expect-error
  IdentifierArray.prototype.arrayContentDidChange = function () {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
  };

  // @ts-expect-error
  IdentifierArray.prototype.reject = function (key, value) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
    if (arguments.length === 2) {
      return this.filter(value => {
        return !get(value, key);
      });
    }
    return this.filter(value => {
      return !get(value, key);
    });
  };
  IdentifierArray.prototype.rejectBy = function (key, value) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
    if (arguments.length === 2) {
      return this.filter(value => {
        return !get(value, key);
      });
    }
    return this.filter(value => {
      return !get(value, key);
    });
  };
  IdentifierArray.prototype.setEach = function (key, value) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
    this.forEach(item => set(item, key, value));
  };
  IdentifierArray.prototype.uniq = function () {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
    // all current managed arrays are already enforced as unique
    return this.slice();
  };

  // @ts-expect-error
  IdentifierArray.prototype.uniqBy = function (key) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
    // all current managed arrays are already enforced as unique
    let seen = new Set();
    let result = [];
    this.forEach(item => {
      let value = get(item, key);
      if (seen.has(value)) {
        return;
      }
      seen.add(value);
      result.push(item);
    });
    return result;
  };
  IdentifierArray.prototype.without = function (value) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME);
    const newArr = this.slice();
    const index = this.indexOf(value);
    if (index !== -1) {
      newArr.splice(index, 1);
    }
    return newArr;
  };

  // @ts-expect-error
  IdentifierArray.prototype.firstObject = null;
  // @ts-expect-error
  IdentifierArray.prototype.lastObject = null;
}
function extractIdentifierFromRecord$1(recordOrPromiseRecord) {
  if (!recordOrPromiseRecord) {
    return null;
  }
  if (isPromiseRecord$1(recordOrPromiseRecord)) {
    let content = recordOrPromiseRecord.content;
    return recordIdentifierFor(content);
  }
  return recordIdentifierFor(recordOrPromiseRecord);
}
function isPromiseRecord$1(record) {
  return !!record.then;
}

/**
  @module @ember-data/store
*/
const RecordArraysCache = new Map();
const FAKE_ARR = {};
const SLICE_BATCH_SIZE = 1200;
/**
 * This is a clever optimization.
 *
 * clever optimizations rarely stand the test of time, so if you're
 * ever curious or think something better is possible please benchmark
 * and discuss. The benchmark for this at the time of writing is in
 * `scripts/benchmark-push.js`
 *
 * This approach turns out to be 150x faster in Chrome and node than
 * simply using push or concat. It's highly susceptible to the specifics
 * of the batch size, and may require tuning.
 *
 * Clever optimizations should always come with a `why`. This optimization
 * exists for two reasons.
 *
 * 1) array.push(...objects) and Array.prototype.push.apply(arr, objects)
 *   are susceptible to stack overflows. The size of objects at which this
 *   occurs varies by environment, browser, and current stack depth and memory
 *   pressure; however, it occurs in all browsers in fairly pristine conditions
 *   somewhere around 125k to 200k elements. Since EmberData regularly encounters
 *   arrays larger than this in size, we cannot use push.
 *
 * 2) `array.concat` or simply setting the array to a new reference is often an
 *   easier approach; however, native Proxy to an array cannot swap it's target array
 *   and attempts at juggling multiple array sources have proven to be victim to a number
 *   of browser implementation bugs. Should these bugs be addressed then we could
 *   simplify to using `concat`, however, do note this is currently 150x faster
 *   than concat, and due to the overloaded signature of concat will likely always
 *   be faster.
 *
 * Sincerely,
 *   - runspired (Chris Thoburn) 08/21/2022
 *
 * @function fastPush
 * @internal
 * @param target the array to push into
 * @param source the items to push into target
 */
function fastPush(target, source) {
  let startLength = 0;
  let newLength = source.length;
  while (newLength - startLength > SLICE_BATCH_SIZE) {
    // eslint-disable-next-line prefer-spread
    target.push.apply(target, source.slice(startLength, startLength + SLICE_BATCH_SIZE));
    startLength += SLICE_BATCH_SIZE;
  }
  // eslint-disable-next-line prefer-spread
  target.push.apply(target, source.slice(startLength));
}
/**
  @class RecordArrayManager
  @internal
*/
class RecordArrayManager {
  constructor(options) {
    this.store = options.store;
    this.isDestroying = false;
    this.isDestroyed = false;
    this._live = new Map();
    this._managed = new Set();
    this._pending = new Map();
    this._staged = new Map();
    this._identifiers = RecordArraysCache;
  }
  _syncArray(array) {
    const pending = this._pending.get(array);
    if (!pending || this.isDestroying || this.isDestroyed) {
      return;
    }
    sync(array, pending);
    this._pending.delete(array);
  }

  /**
    Get the `RecordArray` for a modelName, which contains all loaded records of
    given modelName.
     @method liveArrayFor
    @internal
    @param {String} modelName
    @return {RecordArray}
  */
  liveArrayFor(type) {
    let array = this._live.get(type);
    let identifiers = [];
    let staged = this._staged.get(type);
    if (staged) {
      staged.forEach((value, key) => {
        if (value === 'add') {
          identifiers.push(key);
        }
      });
      this._staged.delete(type);
    }
    if (!array) {
      array = new IdentifierArray({
        type,
        identifiers,
        store: this.store,
        allowMutation: false,
        manager: this
      });
      this._live.set(type, array);
    }
    return array;
  }
  createArray(config) {
    let options = {
      type: config.type,
      links: config.doc?.links || null,
      meta: config.doc?.meta || null,
      query: config.query || null,
      identifiers: config.identifiers || [],
      isLoaded: !!config.identifiers?.length,
      allowMutation: false,
      store: this.store,
      manager: this
    };
    let array = new Collection(options);
    this._managed.add(array);
    if (config.identifiers) {
      associate(array, config.identifiers);
    }
    return array;
  }
  dirtyArray(array, delta) {
    if (array === FAKE_ARR) {
      return;
    }
    let tag = array[IDENTIFIER_ARRAY_TAG];
    if (!tag.shouldReset) {
      tag.shouldReset = true;
      addTransactionCB(array[NOTIFY]);
    } else if (delta > 0 && !tag.t) {
      addTransactionCB(array[NOTIFY]);
    }
  }
  _getPendingFor(identifier, includeManaged, isRemove) {
    if (this.isDestroying || this.isDestroyed) {
      return;
    }
    let liveArray = this._live.get(identifier.type);
    const allPending = this._pending;
    let pending = new Map();
    if (includeManaged) {
      let managed = RecordArraysCache.get(identifier);
      if (managed) {
        managed.forEach(arr => {
          let changes = allPending.get(arr);
          if (!changes) {
            changes = new Map();
            allPending.set(arr, changes);
          }
          pending.set(arr, changes);
        });
      }
    }

    // during unloadAll we can ignore removes since we've already
    // cleared the array.
    if (liveArray && liveArray[SOURCE].length === 0 && isRemove) {
      const pendingLive = allPending.get(liveArray);
      if (!pendingLive || pendingLive.size === 0) {
        return pending;
      }
    }
    if (!liveArray) {
      // start building a changeset for when we eventually
      // do have a live array
      let changes = this._staged.get(identifier.type);
      if (!changes) {
        changes = new Map();
        this._staged.set(identifier.type, changes);
      }
      pending.set(FAKE_ARR, changes);
    } else {
      let changes = allPending.get(liveArray);
      if (!changes) {
        changes = new Map();
        allPending.set(liveArray, changes);
      }
      pending.set(liveArray, changes);
    }
    return pending;
  }
  populateManagedArray(array, identifiers, payload) {
    this._pending.delete(array);
    const source = array[SOURCE];
    const old = source.slice();
    source.length = 0;
    fastPush(source, identifiers);
    notifyArray(array);
    array.meta = payload.meta || null;
    array.links = payload.links || null;
    array.isLoaded = true;
    disassociate(array, old);
    associate(array, identifiers);
  }
  identifierAdded(identifier) {
    let changeSets = this._getPendingFor(identifier, false);
    if (changeSets) {
      changeSets.forEach((changes, array) => {
        let existing = changes.get(identifier);
        if (existing === 'del') {
          changes.delete(identifier);
        } else {
          changes.set(identifier, 'add');
          this.dirtyArray(array, changes.size);
        }
      });
    }
  }
  identifierRemoved(identifier) {
    let changeSets = this._getPendingFor(identifier, true, true);
    if (changeSets) {
      changeSets.forEach((changes, array) => {
        let existing = changes.get(identifier);
        if (existing === 'add') {
          changes.delete(identifier);
        } else {
          changes.set(identifier, 'del');
          this.dirtyArray(array, changes.size);
        }
      });
    }
  }
  identifierChanged(identifier) {
    let newState = this.store._instanceCache.recordIsLoaded(identifier, true);
    if (newState) {
      this.identifierAdded(identifier);
    } else {
      this.identifierRemoved(identifier);
    }
  }
  clear() {
    this._live.forEach(array => array.destroy());
    this._managed.forEach(array => array.destroy());
    this._managed.clear();
    RecordArraysCache.clear();
  }
  destroy() {
    this.isDestroying = true;
    this.clear();
    this._live.clear();
    this.isDestroyed = true;
  }
}
function associate(array, identifiers) {
  for (let i = 0; i < identifiers.length; i++) {
    let identifier = identifiers[i];
    let cache = RecordArraysCache.get(identifier);
    if (!cache) {
      cache = new Set();
      RecordArraysCache.set(identifier, cache);
    }
    cache.add(array);
  }
}
function disassociate(array, identifiers) {
  for (let i = 0; i < identifiers.length; i++) {
    disassociateIdentifier(array, identifiers[i]);
  }
}
function disassociateIdentifier(array, identifier) {
  let cache = RecordArraysCache.get(identifier);
  if (cache) {
    cache.delete(array);
  }
}
function sync(array, changes) {
  let state = array[SOURCE];
  const adds = [];
  const removes = [];
  changes.forEach((value, key) => {
    if (value === 'add') {
      // likely we want to keep a Set along-side
      if (state.includes(key)) {
        return;
      }
      adds.push(key);
    } else {
      removes.push(key);
    }
  });
  if (removes.length) {
    if (removes.length === state.length) {
      state.length = 0;
      // changing the reference breaks the Proxy
      // state = array[SOURCE] = [];
    } else {
      removes.forEach(i => {
        state.splice(state.indexOf(i), 1);
      });
    }
  }
  if (adds.length) {
    fastPush(state, adds);
    // changing the reference breaks the Proxy
    // else we could do this
    /*
    if (state.length === 0) {
      array[SOURCE] = adds;
    } else {
      array[SOURCE] = state.concat(adds);
    }
    */
  }
}

/**
  @module @ember-data/store
*/

function _bind(fn) {
  for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    args[_key - 1] = arguments[_key];
  }
  return function () {
    return fn.apply(undefined, args);
  };
}
function _guard(promise, test) {
  let guarded = promise.finally(() => {
    if (!test()) {
      guarded._subscribers.length = 0;
    }
  });
  return guarded;
}
function _objectIsAlive(object) {
  return !(object.isDestroyed || object.isDestroying);
}
function guardDestroyedStore(promise, store, label) {
  let token;
  if (macroCondition(isDevelopingApp())) {
    token = store._trackAsyncRequestStart(label);
  }
  let wrapperPromise = resolve(promise, label).then(_v => {
    if (!_objectIsAlive(store)) {
      if (macroCondition(getOwnConfig().deprecations.DEPRECATE_RSVP_PROMISE)) ;
    }
    return promise;
  });
  return _guard(wrapperPromise, () => {
    if (macroCondition(isDevelopingApp())) {
      store._trackAsyncRequestEnd(token);
    }
    return _objectIsAlive(store);
  });
}

/**
  This is a helper method that validates a JSON API top-level document

  The format of a document is described here:
  http://jsonapi.org/format/#document-top-level

  @internal
*/
function validateDocumentStructure(doc) {
  if (macroCondition(isDevelopingApp())) {
    if (!doc || typeof doc !== 'object') ;else {
      if ('data' in doc) {
        if (!(doc.data === null || Array.isArray(doc.data) || typeof doc.data === 'object')) ;
      }
      if ('meta' in doc) {
        if (typeof doc.meta !== 'object') ;
      }
      if ('errors' in doc) {
        if (!Array.isArray(doc.errors)) ;
      }
      if ('links' in doc) {
        if (typeof doc.links !== 'object') ;
      }
      if ('jsonapi' in doc) {
        if (typeof doc.jsonapi !== 'object') ;
      }
      if ('included' in doc) {
        if (typeof doc.included !== 'object') ;
      }
    }
  }
}
function normalizeResponseHelper(serializer, store, modelClass, payload, id, requestType) {
  let normalizedResponse = serializer ? serializer.normalizeResponse(store, modelClass, payload, id, requestType) : payload;
  validateDocumentStructure(normalizedResponse);
  return normalizedResponse;
}
const Touching = Symbol('touching');
const RequestPromise = Symbol('promise');
function hasRecordIdentifier(op) {
  return 'recordIdentifier' in op;
}
class RequestCache {
  constructor() {
    this._pending = Object.create(null);
    this._done = new Map();
    this._subscriptions = Object.create(null);
  }
  enqueue(promise, queryRequest) {
    let query = queryRequest.data[0];
    if (hasRecordIdentifier(query)) {
      let lid = query.recordIdentifier.lid;
      let type = query.op === 'saveRecord' ? 'mutation' : 'query';
      if (!this._pending[lid]) {
        this._pending[lid] = [];
      }
      let request = {
        state: 'pending',
        request: queryRequest,
        type
      };
      request[Touching] = [query.recordIdentifier];
      request[RequestPromise] = promise;
      this._pending[lid].push(request);
      this._triggerSubscriptions(request);
      promise.then(result => {
        this._dequeue(lid, request);
        let finalizedRequest = {
          state: 'fulfilled',
          request: queryRequest,
          type,
          response: {
            data: result
          }
        };
        finalizedRequest[Touching] = request[Touching];
        this._addDone(finalizedRequest);
        this._triggerSubscriptions(finalizedRequest);
      }, error => {
        this._dequeue(lid, request);
        let finalizedRequest = {
          state: 'rejected',
          request: queryRequest,
          type,
          response: {
            data: error
          }
        };
        finalizedRequest[Touching] = request[Touching];
        this._addDone(finalizedRequest);
        this._triggerSubscriptions(finalizedRequest);
      });
    }
  }
  _triggerSubscriptions(req) {
    req[Touching].forEach(identifier => {
      if (this._subscriptions[identifier.lid]) {
        this._subscriptions[identifier.lid].forEach(callback => callback(req));
      }
    });
  }
  _dequeue(lid, request) {
    this._pending[lid] = this._pending[lid].filter(req => req !== request);
  }
  _addDone(request) {
    request[Touching].forEach(identifier => {
      // TODO add support for multiple
      let requestDataOp = request.request.data[0].op;
      let requests = this._done.get(identifier);
      if (requests) {
        requests = requests.filter(req => {
          // TODO add support for multiple
          let data;
          if (req.request.data instanceof Array) {
            data = req.request.data[0];
          } else {
            data = req.request.data;
          }
          return data.op !== requestDataOp;
        });
      }
      requests = requests || [];
      requests.push(request);
      this._done.set(identifier, requests);
    });
  }
  subscribeForRecord(identifier, callback) {
    if (!this._subscriptions[identifier.lid]) {
      this._subscriptions[identifier.lid] = [];
    }
    this._subscriptions[identifier.lid].push(callback);
  }
  getPendingRequestsForRecord(identifier) {
    if (this._pending[identifier.lid]) {
      return this._pending[identifier.lid];
    }
    return [];
  }
  getLastRequestForRecord(identifier) {
    let requests = this._done.get(identifier);
    if (requests) {
      return requests[requests.length - 1];
    }
    return null;
  }
}

/**
 * @module @ember-data/store
 */
const SaveOp = Symbol('SaveOp');
/**
 * Manages the state of network requests initiated by the store
 *
 * @class FetchManager
 * @private
 */
class FetchManager {
  // saves which are pending in the runloop

  // fetches pending in the runloop, waiting to be coalesced

  constructor(store) {
    this._store = store;
    // used to keep track of all the find requests that need to be coalesced
    this._pendingFetch = new Map();
    this._pendingSave = [];
    this.requestCache = new RequestCache();
    this.isDestroyed = false;
  }
  clearEntries(identifier) {
    this.requestCache._done.delete(identifier);
  }

  /**
    This method is called by `record.save`, and gets passed a
    resolver for the promise that `record.save` returns.
     It schedules saving to happen at the end of the run loop.
     @internal
  */
  scheduleSave(identifier, options) {
    let promiseLabel = 'DS: Model#save ' + this;
    let resolver = RSVP.defer(promiseLabel);
    let query = {
      op: 'saveRecord',
      recordIdentifier: identifier,
      options
    };
    let queryRequest = {
      data: [query]
    };
    let snapshot = new Snapshot(options, identifier, this._store);
    let pendingSaveItem = {
      snapshot: snapshot,
      resolver: resolver,
      identifier,
      options,
      queryRequest
    };
    this._pendingSave.push(pendingSaveItem);
    _backburner.scheduleOnce('actions', this, this._flushPendingSaves);
    this.requestCache.enqueue(resolver.promise, pendingSaveItem.queryRequest);
    return resolver.promise;
  }

  /**
    This method is called at the end of the run loop, and
    flushes any records passed into `scheduleSave`
     @method flushPendingSave
    @internal
  */
  _flushPendingSaves() {
    const store = this._store;
    let pending = this._pendingSave.slice();
    this._pendingSave = [];
    for (let i = 0, j = pending.length; i < j; i++) {
      let pendingItem = pending[i];
      _flushPendingSave(store, pendingItem);
    }
  }
  scheduleFetch(identifier, options) {
    // TODO Probably the store should pass in the query object
    let shouldTrace = isDevelopingApp() && this._store.generateStackTracesForTrackedRequests;
    let query = {
      op: 'findRecord',
      recordIdentifier: identifier,
      options
    };
    let queryRequest = {
      data: [query]
    };
    let pendingFetch = this.getPendingFetch(identifier, options);
    if (pendingFetch) {
      return pendingFetch;
    }
    let id = identifier.id;
    let modelName = identifier.type;
    let resolver = RSVP.defer(`Fetching ${modelName}' with id: ${id}`);
    let pendingFetchItem = {
      identifier,
      resolver,
      options,
      queryRequest
    };
    if (macroCondition(isDevelopingApp())) {
      if (shouldTrace) {
        let trace;
        try {
          throw new Error(`Trace Origin for scheduled fetch for ${modelName}:${id}.`);
        } catch (e) {
          trace = e;
        }

        // enable folks to discover the origin of this findRecord call when
        // debugging. Ideally we would have a tracked queue for requests with
        // labels or local IDs that could be used to merge this trace with
        // the trace made available when we detect an async leak
        pendingFetchItem.trace = trace;
      }
    }
    let resolverPromise = resolver.promise;
    const store = this._store;
    const isLoading = !store._instanceCache.recordIsLoaded(identifier); // we don't use isLoading directly because we are the request

    const promise = resolverPromise.then(payload => {
      // ensure that regardless of id returned we assign to the correct record
      if (payload.data && !Array.isArray(payload.data)) {
        payload.data.lid = identifier.lid;
      }

      // additional data received in the payload
      // may result in the merging of identifiers (and thus records)
      let potentiallyNewIm = store._push(payload);
      if (potentiallyNewIm && !Array.isArray(potentiallyNewIm)) {
        return potentiallyNewIm;
      }
      return identifier;
    }, error => {
      const recordData = store._instanceCache.peek({
        identifier,
        bucket: 'recordData'
      });
      if (!recordData || recordData.isEmpty(identifier) || isLoading) {
        let isReleasable = true;
        if (macroCondition(dependencySatisfies("@ember-data/record-data", "*"))) {
          if (!recordData) {
            const graphFor = importSync('@ember-data/record-data/-private').graphFor;
            const graph = graphFor(store);
            isReleasable = graph.isReleasable(identifier);
            if (!isReleasable) {
              graph.unload(identifier, true);
            }
          }
        }
        if (recordData || isReleasable) {
          store._instanceCache.unloadRecord(identifier);
        }
      }
      throw error;
    });
    if (this._pendingFetch.size === 0) {
      _backburner.schedule('actions', this, this.flushAllPendingFetches);
    }
    let fetches = this._pendingFetch;
    if (!fetches.has(modelName)) {
      fetches.set(modelName, []);
    }
    fetches.get(modelName).push(pendingFetchItem);
    pendingFetchItem.promise = promise;
    this.requestCache.enqueue(resolverPromise, pendingFetchItem.queryRequest);
    return promise;
  }
  getPendingFetch(identifier, options) {
    let pendingFetches = this._pendingFetch.get(identifier.type);

    // We already have a pending fetch for this
    if (pendingFetches) {
      let matchingPendingFetch = pendingFetches.find(fetch => fetch.identifier === identifier && isSameRequest(options, fetch.options));
      if (matchingPendingFetch) {
        return matchingPendingFetch.promise;
      }
    }
  }
  flushAllPendingFetches() {
    if (this.isDestroyed) {
      return;
    }
    const store = this._store;
    this._pendingFetch.forEach((fetchItem, type) => _flushPendingFetchForType(store, fetchItem, type));
    this._pendingFetch.clear();
  }
  destroy() {
    this.isDestroyed = true;
  }
}

// this function helps resolve whether we have a pending request that we should use instead
function isSameRequest(options, existingOptions) {
  if (options === void 0) {
    options = {};
  }
  if (existingOptions === void 0) {
    existingOptions = {};
  }
  let includedMatches = !options.include || options.include === existingOptions.include;
  let adapterOptionsMatches = options.adapterOptions === existingOptions.adapterOptions;
  return includedMatches && adapterOptionsMatches;
}
function _findMany(store, adapter, modelName, snapshots) {
  let modelClass = store.modelFor(modelName); // `adapter.findMany` gets the modelClass still
  const ids = snapshots.map(s => s.id);
  let promise = adapter.findMany(store, modelClass, ids, snapshots);
  let label = `DS: Handle Adapter#findMany of '${modelName}'`;
  if (promise === undefined) {
    throw new Error('adapter.findMany returned undefined, this was very likely a mistake');
  }
  promise = guardDestroyedStore(promise, store, label);
  return promise.then(adapterPayload => {
    let serializer = store.serializerFor(modelName);
    let payload = normalizeResponseHelper(serializer, store, modelClass, adapterPayload, null, 'findMany');
    return payload;
  });
}
function rejectFetchedItems(fetchMap, snapshots, error) {
  for (let i = 0, l = snapshots.length; i < l; i++) {
    let snapshot = snapshots[i];
    let pair = fetchMap.get(snapshot);
    if (pair) {
      pair.resolver.reject(error || new Error(`Expected: '<${snapshot.modelName}:${snapshot.id}>' to be present in the adapter provided payload, but it was not found.`));
    }
  }
}
function handleFoundRecords(store, fetchMap, snapshots, coalescedPayload) {
  /*
    It is possible that the same ID is included multiple times
    via multiple snapshots. This happens when more than one
    options hash was supplied, each of which must be uniquely
    accounted for.
     However, since we can't map from response to a specific
    options object, we resolve all snapshots by id with
    the first response we see.
  */
  let snapshotsById = new Map();
  for (let i = 0; i < snapshots.length; i++) {
    let id = snapshots[i].id;
    let snapshotGroup = snapshotsById.get(id);
    if (!snapshotGroup) {
      snapshotGroup = [];
      snapshotsById.set(id, snapshotGroup);
    }
    snapshotGroup.push(snapshots[i]);
  }
  const included = Array.isArray(coalescedPayload.included) ? coalescedPayload.included : [];

  // resolve found records
  let resources = coalescedPayload.data;
  for (let i = 0, l = resources.length; i < l; i++) {
    let resource = resources[i];
    let snapshotGroup = snapshotsById.get(resource.id);
    snapshotsById.delete(resource.id);
    if (!snapshotGroup) {
      // TODO consider whether this should be a deprecation/assertion
      included.push(resource);
    } else {
      snapshotGroup.forEach(snapshot => {
        let pair = fetchMap.get(snapshot);
        let resolver = pair.resolver;
        resolver.resolve({
          data: resource
        });
      });
    }
  }
  if (included.length > 0) {
    store._push({
      data: null,
      included
    });
  }
  if (snapshotsById.size === 0) {
    return;
  }

  // reject missing records
  let rejected = [];
  snapshotsById.forEach(snapshots => {
    rejected.push(...snapshots);
  });
  rejectFetchedItems(fetchMap, rejected);
}
function _fetchRecord(store, fetchItem) {
  let identifier = fetchItem.identifier;
  let modelName = identifier.type;
  let adapter = store.adapterFor(modelName);
  let snapshot = new Snapshot(fetchItem.options, identifier, store);
  let klass = store.modelFor(identifier.type);
  let id = identifier.id;
  let label = `DS: Handle Adapter#findRecord of '${modelName}' with id: '${id}'`;
  let promise = guardDestroyedStore(resolve().then(() => {
    return adapter.findRecord(store, klass, identifier.id, snapshot);
  }), store, label).then(adapterPayload => {
    let serializer = store.serializerFor(modelName);
    let payload = normalizeResponseHelper(serializer, store, klass, adapterPayload, id, 'findRecord');
    return payload;
  });
  fetchItem.resolver.resolve(promise);
}
function _processCoalescedGroup(store, fetchMap, group, adapter, modelName) {
  if (group.length > 1) {
    _findMany(store, adapter, modelName, group).then(payloads => {
      handleFoundRecords(store, fetchMap, group, payloads);
    }).catch(error => {
      rejectFetchedItems(fetchMap, group, error);
    });
  } else if (group.length === 1) {
    _fetchRecord(store, fetchMap.get(group[0]));
  } else ;
}
function _flushPendingFetchForType(store, pendingFetchItems, modelName) {
  let adapter = store.adapterFor(modelName);
  let shouldCoalesce = !!adapter.findMany && adapter.coalesceFindRequests;
  let totalItems = pendingFetchItems.length;
  if (shouldCoalesce) {
    let snapshots = new Array(totalItems);
    let fetchMap = new Map();
    for (let i = 0; i < totalItems; i++) {
      let fetchItem = pendingFetchItems[i];
      snapshots[i] = new Snapshot(fetchItem.options, fetchItem.identifier, store);
      fetchMap.set(snapshots[i], fetchItem);
    }
    let groups;
    if (adapter.groupRecordsForFindMany) {
      groups = adapter.groupRecordsForFindMany(store, snapshots);
    } else {
      groups = [snapshots];
    }
    for (let i = 0, l = groups.length; i < l; i++) {
      _processCoalescedGroup(store, fetchMap, groups[i], adapter, modelName);
    }
  } else {
    for (let i = 0; i < totalItems; i++) {
      _fetchRecord(store, pendingFetchItems[i]);
    }
  }
}
function _flushPendingSave(store, pending) {
  const {
    snapshot,
    resolver,
    identifier,
    options
  } = pending;
  const adapter = store.adapterFor(identifier.type);
  const operation = options[SaveOp];
  let modelName = snapshot.modelName;
  let modelClass = store.modelFor(modelName);
  const record = store._instanceCache.getRecord(identifier);
  let promise = resolve().then(() => adapter[operation](store, modelClass, snapshot));
  let serializer = store.serializerFor(modelName);
  let label = `DS: Extract and notify about ${operation} completion of ${identifier}`;
  promise = _guard(guardDestroyedStore(promise, store, label), _bind(_objectIsAlive, record)).then(adapterPayload => {
    if (!_objectIsAlive(record)) {
      if (macroCondition(getOwnConfig().deprecations.DEPRECATE_RSVP_PROMISE)) ;
    }
    if (adapterPayload) {
      return normalizeResponseHelper(serializer, store, modelClass, adapterPayload, snapshot.id, operation);
    }
  });
  resolver.resolve(promise);
}

/**
  SnapshotRecordArray is not directly instantiable.
  Instances are provided to consuming application's
  adapters for certain requests.

  @class SnapshotRecordArray
  @public
*/
class SnapshotRecordArray {
  /**
    SnapshotRecordArray is not directly instantiable.
    Instances are provided to consuming application's
    adapters and serializers for certain requests.
     @method constructor
    @private
    @constructor
    @param {RecordArray} recordArray
    @param options
   */
  constructor(store, recordArray, options) {
    if (options === void 0) {
      options = {};
    }
    this.__store = store;
    /**
      An array of snapshots
      @private
      @property _snapshots
      @type {Array}
    */
    this._snapshots = null;

    /**
      An array of records
      @private
      @property _recordArray
      @type {Array}
    */
    this._recordArray = recordArray;

    /**
      Number of records in the array
       Example
       ```app/adapters/post.js
      import JSONAPIAdapter from '@ember-data/adapter/json-api';
       export default class PostAdapter extends JSONAPIAdapter {
        shouldReloadAll(store, snapshotRecordArray) {
          return !snapshotRecordArray.length;
        }
      });
      ```
       @property length
      @public
      @type {Number}
    */
    this.length = recordArray.length; // deal with computedProperty shennanigans

    /**
      A hash of adapter options passed into the store method for this request.
       Example
       ```app/adapters/post.js
      import MyCustomAdapter from './custom-adapter';
       export default class PostAdapter extends MyCustomAdapter {
        findAll(store, type, sinceToken, snapshotRecordArray) {
          if (snapshotRecordArray.adapterOptions.subscribe) {
            // ...
          }
          // ...
        }
      }
      ```
       @property adapterOptions
      @public
      @type {Object}
    */
    this.adapterOptions = options.adapterOptions;

    /**
      The relationships to include for this request.
       Example
       ```app/adapters/application.js
      import Adapter from '@ember-data/adapter';
       export default class ApplicationAdapter extends Adapter {
        findAll(store, type, snapshotRecordArray) {
          let url = `/${type.modelName}?include=${encodeURIComponent(snapshotRecordArray.include)}`;
           return fetch(url).then((response) => response.json())
        }
      }
      ```
       @property include
      @public
      @type {String|Array}
    */
    this.include = options.include;
  }

  /**
    The type of the underlying records for the snapshots in the array, as a Model
    @property type
    @deprecated
    @public
    @type {Model}
  */

  /**
    The modelName of the underlying records for the snapshots in the array, as a Model
    @property modelName
    @public
    @type {Model}
  */
  get modelName() {
    return this._recordArray.modelName;
  }

  /**
    Get snapshots of the underlying record array
     Example
     ```app/adapters/post.js
    import JSONAPIAdapter from '@ember-data/adapter/json-api';
     export default class PostAdapter extends JSONAPIAdapter {
      shouldReloadAll(store, snapshotArray) {
        let snapshots = snapshotArray.snapshots();
         return snapshots.any(function(ticketSnapshot) {
          let timeDiff = moment().diff(ticketSnapshot.attr('lastAccessedAt'), 'minutes');
          if (timeDiff > 20) {
            return true;
          } else {
            return false;
          }
        });
      }
    }
    ```
     @method snapshots
    @public
    @return {Array} Array of snapshots
  */
  snapshots() {
    if (this._snapshots !== null) {
      return this._snapshots;
    }
    const {
      _instanceCache
    } = this.__store;
    this._snapshots = this._recordArray[SOURCE].map(identifier => _instanceCache.createSnapshot(identifier));
    return this._snapshots;
  }
}
if (macroCondition(getOwnConfig().deprecations.DEPRECATE_SNAPSHOT_MODEL_CLASS_ACCESS)) {
  Object.defineProperty(SnapshotRecordArray.prototype, 'type', {
    get() {
      return this._recordArray.type;
    }
  });
}
function _findAll(adapter, store, modelName, options, snapshotArray) {
  let modelClass = store.modelFor(modelName); // adapter.findAll depends on the class
  let recordArray = store.peekAll(modelName);
  snapshotArray = snapshotArray || new SnapshotRecordArray(store, recordArray, options);
  let promise = Promise$1.resolve().then(() => adapter.findAll(store, modelClass, null, snapshotArray));
  let label = 'DS: Handle Adapter#findAll of ' + modelClass;
  promise = guardDestroyedStore(promise, store, label);
  return promise.then(adapterPayload => {
    let serializer = store.serializerFor(modelName);
    let payload = normalizeResponseHelper(serializer, store, modelClass, adapterPayload, null, 'findAll');
    store._push(payload);
    recordArray.isUpdating = false;
    return recordArray;
  }, null, 'DS: Extract payload of findAll ${modelName}');
}
function _query(adapter, store, modelName, query, recordArray, options) {
  let modelClass = store.modelFor(modelName); // adapter.query needs the class

  // TODO @deprecate RecordArrays being passed to Adapters
  recordArray = recordArray || store.recordArrayManager.createArray({
    type: modelName,
    query
  });
  let promise = Promise$1.resolve().then(() => adapter.query(store, modelClass, query, recordArray, options));
  let label = `DS: Handle Adapter#query of ${modelName}`;
  promise = guardDestroyedStore(promise, store, label);
  return promise.then(adapterPayload => {
    let serializer = store.serializerFor(modelName);
    let payload = normalizeResponseHelper(serializer, store, modelClass, adapterPayload, null, 'query');
    let identifiers = store._push(payload);
    store.recordArrayManager.populateManagedArray(recordArray, identifiers, payload);
    return recordArray;
  }, null, `DS: Extract payload of query ${modelName}`);
}
function _queryRecord(adapter, store, modelName, query, options) {
  let modelClass = store.modelFor(modelName); // adapter.queryRecord needs the class
  let promise = Promise$1.resolve().then(() => adapter.queryRecord(store, modelClass, query, options));
  let label = `DS: Handle Adapter#queryRecord of ${modelName}`;
  promise = guardDestroyedStore(promise, store, label);
  return promise.then(adapterPayload => {
    let serializer = store.serializerFor(modelName);
    let payload = normalizeResponseHelper(serializer, store, modelClass, adapterPayload, null, 'queryRecord');
    return store._push(payload);
  }, null, `DS: Extract payload of queryRecord ${modelName}`);
}
function promiseRecord(store, promise) {
  let toReturn = promise.then(identifier => store.peekRecord(identifier));
  return promiseObject(toReturn);
}

/**
  @module @ember-data/store
 */

// hello world

let _RecordData;
function freeze(obj) {
  if (typeof Object.freeze === 'function') {
    return Object.freeze(obj);
  }
  return obj;
}
/**
  The store contains all of the data for records loaded from the server.
  It is also responsible for creating instances of `Model` that wrap
  the individual data for a record, so that they can be bound to in your
  Handlebars templates.

  Define your application's store like this:

  ```app/services/store.js
  import Store from '@ember-data/store';

  export default class MyStore extends Store {}
  ```

  Most Ember.js applications will only have a single `Store` that is
  automatically created by their `Ember.Application`.

  You can retrieve models from the store in several ways. To retrieve a record
  for a specific id, use `Store`'s `findRecord()` method:

  ```javascript
  store.findRecord('person', 123).then(function (person) {
  });
  ```

  By default, the store will talk to your backend using a standard
  REST mechanism. You can customize how the store talks to your
  backend by specifying a custom adapter:

  ```app/adapters/application.js
  import Adapter from '@ember-data/adapter';

  export default class ApplicationAdapter extends Adapter {
  }
  ```

  You can learn more about writing a custom adapter by reading the `Adapter`
  documentation.

  ### Store createRecord() vs. push() vs. pushPayload()

  The store provides multiple ways to create new record objects. They have
  some subtle differences in their use which are detailed below:

  [createRecord](../methods/createRecord?anchor=createRecord) is used for creating new
  records on the client side. This will return a new record in the
  `created.uncommitted` state. In order to persist this record to the
  backend, you will need to call `record.save()`.

  [push](../methods/push?anchor=push) is used to notify Ember Data's store of new or
  updated records that exist in the backend. This will return a record
  in the `loaded.saved` state. The primary use-case for `store#push` is
  to notify Ember Data about record updates (full or partial) that happen
  outside of the normal adapter methods (for example
  [SSE](http://dev.w3.org/html5/eventsource/) or [Web
  Sockets](http://www.w3.org/TR/2009/WD-websockets-20091222/)).

  [pushPayload](../methods/pushPayload?anchor=pushPayload) is a convenience wrapper for
  `store#push` that will deserialize payloads if the
  Serializer implements a `pushPayload` method.

  Note: When creating a new record using any of the above methods
  Ember Data will update `RecordArray`s such as those returned by
  `store#peekAll()` or `store#findAll()`. This means any
  data bindings or computed properties that depend on the RecordArray
  will automatically be synced to include the new or updated record
  values.

  @main @ember-data/store
  @class Store
  @public
  @extends Ember.Service
*/

class Store extends Service {
  // DEBUG-only properties

  /**
    @method init
    @private
  */
  constructor() {
    super(...arguments);

    /**
     * Provides access to the IdentifierCache instance
     * for this store.
     *
     * The IdentifierCache can be used to generate or
     * retrieve a stable unique identifier for any resource.
     *
     * @property {IdentifierCache} identifierCache
     * @public
     */
    this.__private_singleton_recordData = void 0;
    this.identifierCache = new IdentifierCache();

    // private but maybe useful to be here, somewhat intimate
    this.recordArrayManager = new RecordArrayManager({
      store: this
    });

    // private, TODO consider taking public as the instance is public to instantiateRecord anyway
    this._notificationManager = new NotificationManager(this);

    // private
    this._fetchManager = new FetchManager(this);
    this._instanceCache = new InstanceCache(this);
    this._adapterCache = Object.create(null);
    this._serializerCache = Object.create(null);
    this._modelFactoryCache = Object.create(null);
    if (macroCondition(isDevelopingApp())) {
      if (this.generateStackTracesForTrackedRequests === undefined) {
        this.generateStackTracesForTrackedRequests = false;
      }
      this._trackedAsyncRequests = [];
      this._trackAsyncRequestStart = label => {
        let trace = 'set `store.generateStackTracesForTrackedRequests = true;` to get a detailed trace for where this request originated';
        if (this.generateStackTracesForTrackedRequests) {
          try {
            throw new Error(`EmberData TrackedRequest: ${label}`);
          } catch (e) {
            trace = e;
          }
        }
        let token = freeze({
          label,
          trace
        });
        this._trackedAsyncRequests.push(token);
        return token;
      };
      this._trackAsyncRequestEnd = token => {
        let index = this._trackedAsyncRequests.indexOf(token);
        if (index === -1) {
          throw new Error(`Attempted to end tracking for the following request but it was not being tracked:\n${token}`);
        }
        this._trackedAsyncRequests.splice(index, 1);
      };
      this.__asyncWaiter = () => {
        let tracked = this._trackedAsyncRequests;
        return this.DISABLE_WAITER || tracked.length === 0;
      };
      registerWaiter(this.__asyncWaiter);
    }
  }
  _run(cb) {
    const _cbs = this._cbs = {};
    cb();
    if (_cbs.coalesce) {
      _cbs.coalesce();
    }
    if (_cbs.sync) {
      _cbs.sync();
    }
    if (_cbs.notify) {
      _cbs.notify();
    }
    this._cbs = null;
  }
  _join(cb) {
    if (this._cbs) {
      cb();
    } else {
      this._run(cb);
    }
  }
  _schedule(name, cb) {
    this._cbs[name] = cb;
  }

  /**
   * Retrieve the RequestStateService instance
   * associated with this Store.
   *
   * This can be used to query the status of requests
   * that have been initiated for a given identifier.
   *
   * @method getRequestStateService
   * @returns {RequestStateService}
   * @public
   */
  getRequestStateService() {
    return this._fetchManager.requestCache;
  }

  /**
   * A hook which an app or addon may implement. Called when
   * the Store is attempting to create a Record Instance for
   * a resource.
   *
   * This hook can be used to select or instantiate any desired
   * mechanism of presentating cache data to the ui for access
   * mutation, and interaction.
   *
   * @method instantiateRecord (hook)
   * @param identifier
   * @param createRecordArgs
   * @param recordDataFor
   * @param notificationManager
   * @returns A record instance
   * @public
   */
  instantiateRecord(identifier, createRecordArgs, recordDataFor, notificationManager) {
    if (macroCondition(dependencySatisfies("@ember-data/model", "*"))) {
      let modelName = identifier.type;
      let recordData = this._instanceCache.getRecordData(identifier);
      // TODO deprecate allowing unknown args setting
      let createOptions = {
        _createProps: createRecordArgs,
        // TODO @deprecate consider deprecating accessing record properties during init which the below is necessary for
        _secretInit: {
          identifier,
          recordData,
          store: this,
          cb: secretInit
        }
      };

      // ensure that `getOwner(this)` works inside a model instance
      setOwner(createOptions, getOwner(this));
      return getModelFactory(this, this._modelFactoryCache, modelName).class.create(createOptions);
    }
  }

  /**
   * A hook which an app or addon may implement. Called when
   * the Store is destroying a Record Instance. This hook should
   * be used to teardown any custom record instances instantiated
   * with `instantiateRecord`.
   *
   * @method teardownRecord (hook)
   * @public
   * @param record
   */
  teardownRecord(record) {
    if (macroCondition(dependencySatisfies("@ember-data/model", "*"))) {
      record.destroy();
    }
  }

  /**
   * Provides access to the SchemaDefinitionService instance
   * for this Store instance.
   *
   * The SchemaDefinitionService can be used to query for
   * information about the schema of a resource.
   *
   * @method getSchemaDefinitionService
   * @public
   */
  getSchemaDefinitionService() {
    if (macroCondition(dependencySatisfies("@ember-data/model", "*"))) {
      if (!this._schemaDefinitionService) {
        // it is potentially a mistake for the RFC to have not enabled chaining these services, though highlander rule is nice.
        // what ember-m3 did via private API to allow both worlds to interop would be much much harder using this.
        this._schemaDefinitionService = new DSModelSchemaDefinitionService(this);
      }
    }
    return this._schemaDefinitionService;
  }

  /**
   * Allows an app to register a custom SchemaDefinitionService
   * for use when information about a resource's schema needs
   * to be queried.
   *
   * This method can only be called more than once, but only one schema
   * definition service may exist. Therefore if you wish to chain services
   * you must lookup the existing service and close over it with the new
   * service by calling `getSchemaDefinitionService` prior to registration.
   *
   * For Example:
   *
   * ```ts
   * import Store from '@ember-data/store';
   *
   * class SchemaDelegator {
   *   constructor(schema) {
   *     this._schema = schema;
   *   }
   *
   *   doesTypeExist(type: string): boolean {
   *     if (AbstractSchemas.has(type)) {
   *       return true;
   *     }
   *     return this._schema.doesTypeExist(type);
   *   }
   *
   *   attributesDefinitionFor(identifier: RecordIdentifier | { type: string }): AttributesSchema {
   *     return this._schema.attributesDefinitionFor(identifier);
   *   }
   *
   *   relationshipsDefinitionFor(identifier: RecordIdentifier | { type: string }): RelationshipsSchema {
   *     const schema = AbstractSchemas.get(identifier.type);
   *     return schema || this._schema.relationshipsDefinitionFor(identifier);
   *   }
   * }
   *
   * export default class extends Store {
   *   constructor(...args) {
   *     super(...args);
   *
   *     const schema = this.getSchemaDefinitionService();
   *     this.registerSchemaDefinitionService(new SchemaDelegator(schema));
   *   }
   * }
   * ```
   *
   * @method registerSchemaDefinitionService
   * @param {SchemaDefinitionService} schema
   * @public
   */
  registerSchemaDefinitionService(schema) {
    this._schemaDefinitionService = schema;
  }

  /**
    Returns the schema for a particular `modelName`.
     When used with Model from @ember-data/model the return is the model class,
    but this is not guaranteed.
     If looking to query attribute or relationship information it is
    recommended to use `getSchemaDefinitionService` instead. This method
    should be considered legacy and exists primarily to continue to support
    Adapter/Serializer APIs which expect it's return value in their method
    signatures.
     The class of a model might be useful if you want to get a list of all the
    relationship names of the model, see
    [`relationshipNames`](/ember-data/release/classes/Model?anchor=relationshipNames)
    for example.
     @method modelFor
    @public
    @param {String} modelName
    @return {subclass of Model | ShimModelClass}
    */
  // TODO @deprecate in favor of schema APIs, requires adapter/serializer overhaul or replacement
  modelFor(modelName) {
    if (macroCondition(isDevelopingApp())) {
      assertDestroyedStoreOnly(this, 'modelFor');
    }
    if (macroCondition(dependencySatisfies("@ember-data/model", "*"))) {
      let normalizedModelName = normalizeModelName$1(modelName);
      let maybeFactory = getModelFactory(this, this._modelFactoryCache, normalizedModelName);

      // for factorFor factory/class split
      let klass = maybeFactory && maybeFactory.class ? maybeFactory.class : maybeFactory;
      if (!klass || !klass.isModel) {
        return getShimClass(this, modelName);
      } else {
        // TODO @deprecate ever returning the klass, always return the shim
        return klass;
      }
    }
    return getShimClass(this, modelName);
  }

  /**
    Create a new record in the current store. The properties passed
    to this method are set on the newly created record.
     To create a new instance of a `Post`:
     ```js
    store.createRecord('post', {
      title: 'Ember is awesome!'
    });
    ```
     To create a new instance of a `Post` that has a relationship with a `User` record:
     ```js
    let user = this.store.peekRecord('user', 1);
    store.createRecord('post', {
      title: 'Ember is awesome!',
      user: user
    });
    ```
     @method createRecord
    @public
    @param {String} modelName
    @param {Object} inputProperties a hash of properties to set on the
      newly created record.
    @return {Model} record
  */
  createRecord(modelName, inputProperties) {
    if (macroCondition(isDevelopingApp())) {
      assertDestroyingStore(this, 'createRecord');
    }
    //   calls to `createRecord`. The run loop usage here is because we batch the joining and updating
    //   of record-arrays via ember's run loop, not our own.
    //
    //   to remove this, we would need to move to a new `async` API.
    let record;
    _backburner.join(() => {
      this._join(() => {
        let normalizedModelName = normalizeModelName$1(modelName);
        let properties = {
          ...inputProperties
        };

        // If the passed properties do not include a primary key,
        // give the adapter an opportunity to generate one. Typically,
        // client-side ID generators will use something like uuid.js
        // to avoid conflicts.

        if (properties.id === null || properties.id === undefined) {
          let adapter = this.adapterFor(modelName);
          if (adapter && adapter.generateIdForRecord) {
            properties.id = adapter.generateIdForRecord(this, modelName, properties);
          } else {
            properties.id = null;
          }
        }

        // Coerce ID to a string
        properties.id = coerceId(properties.id);
        const resource = {
          type: normalizedModelName,
          id: properties.id
        };
        if (resource.id) {
          this.identifierCache.peekRecordIdentifier(resource);
        }
        const identifier = this.identifierCache.createIdentifierForNewRecord(resource);
        const recordData = this._instanceCache.getRecordData(identifier);
        const createOptions = normalizeProperties(this, identifier, properties, recordData.managedVersion === '1');
        const resultProps = recordData.clientDidCreate(identifier, createOptions);
        this.recordArrayManager.identifierAdded(identifier);
        record = this._instanceCache.getRecord(identifier, resultProps);
      });
    });
    return record;
  }

  /**
    For symmetry, a record can be deleted via the store.
     Example
     ```javascript
    let post = store.createRecord('post', {
      title: 'Ember is awesome!'
    });
     store.deleteRecord(post);
    ```
     @method deleteRecord
    @public
    @param {Model} record
  */
  deleteRecord(record) {
    if (macroCondition(isDevelopingApp())) {
      assertDestroyingStore(this, 'deleteRecord');
    }
    const identifier = peekRecordIdentifier(record);
    const recordData = identifier && this._instanceCache.peek({
      identifier,
      bucket: 'recordData'
    });
    this._join(() => {
      recordData.setIsDeleted(identifier, true);
      if (recordData.isNew(identifier)) {
        _backburner.join(() => {
          this._instanceCache.unloadRecord(identifier);
        });
      }
    });
  }

  /**
    For symmetry, a record can be unloaded via the store.
    This will cause the record to be destroyed and freed up for garbage collection.
     Example
     ```javascript
    store.findRecord('post', 1).then(function(post) {
      store.unloadRecord(post);
    });
    ```
     @method unloadRecord
    @public
    @param {Model} record
  */
  unloadRecord(record) {
    if (macroCondition(isDevelopingApp())) {
      assertDestroyingStore(this, 'unloadRecord');
    }
    const identifier = peekRecordIdentifier(record);
    if (identifier) {
      this._instanceCache.unloadRecord(identifier);
    }
  }

  /**
    @method find
    @param {String} modelName
    @param {String|Integer} id
    @param {Object} options
    @return {Promise} promise
    @deprecated
    @private
  */
  find(modelName, id, options) {
    if (macroCondition(isDevelopingApp())) {
      assertDestroyingStore(this, 'find');
    }
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_STORE_FIND)) {
      return this.findRecord(modelName, id);
    }
  }

  /**
    This method returns a record for a given identifier or type and id combination.
     The `findRecord` method will always resolve its promise with the same
    object for a given identifier or type and `id`.
     The `findRecord` method will always return a **promise** that will be
    resolved with the record.
     **Example 1**
     ```app/routes/post.js
    import Route from '@ember/routing/route';
     export default class PostRoute extends Route {
      model({ post_id }) {
        return this.store.findRecord('post', post_id);
      }
    }
    ```
     **Example 2**
     `findRecord` can be called with a single identifier argument instead of the combination
    of `type` (modelName) and `id` as separate arguments. You may recognize this combo as
    the typical pairing from [JSON:API](https://jsonapi.org/format/#document-resource-object-identification)
     ```app/routes/post.js
    import Route from '@ember/routing/route';
     export default class PostRoute extends Route {
      model({ post_id: id }) {
        return this.store.findRecord({ type: 'post', id });
      }
    }
    ```
     **Example 3**
     If you have previously received an lid via an Identifier for this record, and the record
    has already been assigned an id, you can find the record again using just the lid.
     ```app/routes/post.js
    store.findRecord({ lid });
    ```
     If the record is not yet available, the store will ask the adapter's `findRecord`
    method to retrieve and supply the necessary data. If the record is already present
    in the store, it depends on the reload behavior _when_ the returned promise
    resolves.
     ### Preloading
     You can optionally `preload` specific attributes and relationships that you know of
    by passing them via the passed `options`.
     For example, if your Ember route looks like `/posts/1/comments/2` and your API route
    for the comment also looks like `/posts/1/comments/2` if you want to fetch the comment
    without also fetching the post you can pass in the post to the `findRecord` call:
     ```app/routes/post-comments.js
    import Route from '@ember/routing/route';
     export default class PostRoute extends Route {
      model({ post_id, comment_id: id }) {
        return this.store.findRecord({ type: 'comment', id, { preload: { post: post_id }} });
      }
    }
    ```
     In your adapter you can then access this id without triggering a network request via the
    snapshot:
     ```app/adapters/application.js
    import EmberObject from '@ember/object';
     export default class Adapter extends EmberObject {
       findRecord(store, schema, id, snapshot) {
        let type = schema.modelName;
         if (type === 'comment')
          let postId = snapshot.belongsTo('post', { id: true });
           return fetch(`./posts/${postId}/comments/${id}`)
            .then(response => response.json())
        }
      }
    }
    ```
     This could also be achieved by supplying the post id to the adapter via the adapterOptions
    property on the options hash.
     ```app/routes/post-comments.js
    import Route from '@ember/routing/route';
     export default class PostRoute extends Route {
      model({ post_id, comment_id: id }) {
        return this.store.findRecord({ type: 'comment', id, { adapterOptions: { post: post_id }} });
      }
    }
    ```
     ```app/adapters/application.js
    import EmberObject from '@ember/object';
     export default class Adapter extends EmberObject {
       findRecord(store, schema, id, snapshot) {
        let type = schema.modelName;
         if (type === 'comment')
          let postId = snapshot.adapterOptions.post;
           return fetch(`./posts/${postId}/comments/${id}`)
            .then(response => response.json())
        }
      }
    }
    ```
     If you have access to the post model you can also pass the model itself to preload:
     ```javascript
    let post = await store.findRecord('post', 1);
    let comment = await store.findRecord('comment', 2, { post: myPostModel });
    ```
     ### Reloading
     The reload behavior is configured either via the passed `options` hash or
    the result of the adapter's `shouldReloadRecord`.
     If `{ reload: true }` is passed or `adapter.shouldReloadRecord` evaluates
    to `true`, then the returned promise resolves once the adapter returns
    data, regardless if the requested record is already in the store:
     ```js
    store.push({
      data: {
        id: 1,
        type: 'post',
        revision: 1
      }
    });
     // adapter#findRecord resolves with
    // [
    //   {
    //     id: 1,
    //     type: 'post',
    //     revision: 2
    //   }
    // ]
    store.findRecord('post', 1, { reload: true }).then(function(post) {
      post.revision; // 2
    });
    ```
     If no reload is indicated via the above mentioned ways, then the promise
    immediately resolves with the cached version in the store.
     ### Background Reloading
     Optionally, if `adapter.shouldBackgroundReloadRecord` evaluates to `true`,
    then a background reload is started, which updates the records' data, once
    it is available:
     ```js
    // app/adapters/post.js
    import ApplicationAdapter from "./application";
     export default class PostAdapter extends ApplicationAdapter {
      shouldReloadRecord(store, snapshot) {
        return false;
      },
       shouldBackgroundReloadRecord(store, snapshot) {
        return true;
      }
    });
     // ...
     store.push({
      data: {
        id: 1,
        type: 'post',
        revision: 1
      }
    });
     let blogPost = store.findRecord('post', 1).then(function(post) {
      post.revision; // 1
    });
     // later, once adapter#findRecord resolved with
    // [
    //   {
    //     id: 1,
    //     type: 'post',
    //     revision: 2
    //   }
    // ]
     blogPost.revision; // 2
    ```
     If you would like to force or prevent background reloading, you can set a
    boolean value for `backgroundReload` in the options object for
    `findRecord`.
     ```app/routes/post/edit.js
    import Route from '@ember/routing/route';
     export default class PostEditRoute extends Route {
      model(params) {
        return this.store.findRecord('post', params.post_id, { backgroundReload: false });
      }
    }
    ```
     If you pass an object on the `adapterOptions` property of the options
    argument it will be passed to your adapter via the snapshot
     ```app/routes/post/edit.js
    import Route from '@ember/routing/route';
     export default class PostEditRoute extends Route {
      model(params) {
        return this.store.findRecord('post', params.post_id, {
          adapterOptions: { subscribe: false }
        });
      }
    }
    ```
     ```app/adapters/post.js
    import MyCustomAdapter from './custom-adapter';
     export default class PostAdapter extends MyCustomAdapter {
      findRecord(store, type, id, snapshot) {
        if (snapshot.adapterOptions.subscribe) {
          // ...
        }
        // ...
      }
    }
    ```
     See [peekRecord](../methods/peekRecord?anchor=peekRecord) to get the cached version of a record.
     ### Retrieving Related Model Records
     If you use an adapter such as Ember's default
    [`JSONAPIAdapter`](/ember-data/release/classes/JSONAPIAdapter)
    that supports the [JSON API specification](http://jsonapi.org/) and if your server
    endpoint supports the use of an
    ['include' query parameter](http://jsonapi.org/format/#fetching-includes),
    you can use `findRecord()` or `findAll()` to automatically retrieve additional records related to
    the one you request by supplying an `include` parameter in the `options` object.
     For example, given a `post` model that has a `hasMany` relationship with a `comment`
    model, when we retrieve a specific post we can have the server also return that post's
    comments in the same request:
     ```app/routes/post.js
    import Route from '@ember/routing/route';
     export default class PostRoute extends Route {
      model(params) {
        return this.store.findRecord('post', params.post_id, { include: 'comments' });
      }
    }
    ```
     ```app/adapters/application.js
    import EmberObject from '@ember/object';
     export default class Adapter extends EmberObject {
       findRecord(store, schema, id, snapshot) {
        let type = schema.modelName;
         if (type === 'post')
          let includes = snapshot.adapterOptions.include;
           return fetch(`./posts/${postId}?include=${includes}`)
            .then(response => response.json())
        }
      }
    }
    ```
     In this case, the post's comments would then be available in your template as
    `model.comments`.
     Multiple relationships can be requested using an `include` parameter consisting of a
    comma-separated list (without white-space) while nested relationships can be specified
    using a dot-separated sequence of relationship names. So to request both the post's
    comments and the authors of those comments the request would look like this:
     ```app/routes/post.js
    import Route from '@ember/routing/route';
     export default class PostRoute extends Route {
      model(params) {
        return this.store.findRecord('post', params.post_id, { include: 'comments,comments.author' });
      }
    }
    ```
     ### Retrieving Specific Fields by Type
     If your server endpoint supports the use of a ['fields' query parameter](https://jsonapi.org/format/#fetching-sparse-fieldsets),
    you can use pass those fields through to your server.  At this point in time, this requires a few manual steps on your part.
     1. Implement `buildQuery` in your adapter.
     ```app/adapters/application.js
    buildQuery(snapshot) {
      let query = super.buildQuery(...arguments);
       let { fields } = snapshot.adapterOptions;
       if (fields) {
        query.fields = fields;
      }
       return query;
    }
    ```
     2. Then pass through the applicable fields to your `findRecord` request.
     Given a `post` model with attributes body, title, publishDate and meta, you can retrieve a filtered list of attributes.
     ```app/routes/post.js
    import Route from '@ember/routing/route';
    export default Route.extend({
      model(params) {
        return this.store.findRecord('post', params.post_id, { adapterOptions: { fields: { post: 'body,title' } });
      }
    });
    ```
     Moreover, you can filter attributes on related models as well. If a `post` has a `belongsTo` relationship to a user,
    just include the relationship key and attributes.
     ```app/routes/post.js
    import Route from '@ember/routing/route';
    export default Route.extend({
      model(params) {
        return this.store.findRecord('post', params.post_id, { adapterOptions: { fields: { post: 'body,title', user: 'name,email' } });
      }
    });
    ```
     @since 1.13.0
    @method findRecord
    @public
    @param {String|object} modelName - either a string representing the modelName or a ResourceIdentifier object containing both the type (a string) and the id (a string) for the record or an lid (a string) of an existing record
    @param {(String|Integer|Object)} id - optional object with options for the request only if the first param is a ResourceIdentifier, else the string id of the record to be retrieved
    @param {Object} [options] - if the first param is a string this will be the optional options for the request. See examples for available options.
    @return {Promise} promise
  */

  findRecord(resource, id, options) {
    if (macroCondition(isDevelopingApp())) {
      assertDestroyingStore(this, 'findRecord');
    }
    if (isMaybeIdentifier(resource)) {
      options = id;
    } else {
      const type = normalizeModelName$1(resource);
      const normalizedId = ensureStringId(id);
      resource = constructResource(type, normalizedId);
    }
    const identifier = this.identifierCache.getOrCreateRecordIdentifier(resource);
    let promise;
    options = options || {};

    // if not loaded start loading
    if (!this._instanceCache.recordIsLoaded(identifier)) {
      promise = this._instanceCache._fetchDataIfNeededForIdentifier(identifier, options);

      // Refetch if the reload option is passed
    } else if (options.reload) {
      promise = this._fetchManager.scheduleFetch(identifier, options);
    } else {
      let snapshot = null;
      let adapter = this.adapterFor(identifier.type);

      // Refetch the record if the adapter thinks the record is stale
      if (typeof options.reload === 'undefined' && adapter.shouldReloadRecord && adapter.shouldReloadRecord(this, snapshot = this._instanceCache.createSnapshot(identifier, options))) {
        promise = this._fetchManager.scheduleFetch(identifier, options);
      } else {
        // Trigger the background refetch if backgroundReload option is passed
        if (options.backgroundReload !== false && (options.backgroundReload || !adapter.shouldBackgroundReloadRecord || adapter.shouldBackgroundReloadRecord(this, snapshot = snapshot || this._instanceCache.createSnapshot(identifier, options)))) {
          this._fetchManager.scheduleFetch(identifier, options);
        }

        // Return the cached record
        promise = resolve(identifier);
      }
    }
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_PROMISE_PROXIES)) {
      return promiseRecord(this, promise);
    }
    return promise.then(identifier => this.peekRecord(identifier));
  }

  /**
    Get the reference for the specified record.
     Example
     ```javascript
    let userRef = store.getReference('user', 1);
     // check if the user is loaded
    let isLoaded = userRef.value() !== null;
     // get the record of the reference (null if not yet available)
    let user = userRef.value();
     // get the identifier of the reference
    if (userRef.remoteType() === 'id') {
    let id = userRef.id();
    }
     // load user (via store.find)
    userRef.load().then(...)
     // or trigger a reload
    userRef.reload().then(...)
     // provide data for reference
    userRef.push({ id: 1, username: '@user' }).then(function(user) {
      userRef.value() === user;
    });
    ```
     @method getReference
    @public
    @param {String|object} resource - modelName (string) or Identifier (object)
    @param {String|Integer} id
    @since 2.5.0
    @return {RecordReference}
  */
  // TODO @deprecate getReference (and references generally)
  getReference(resource, id) {
    if (macroCondition(isDevelopingApp())) {
      assertDestroyingStore(this, 'getReference');
    }
    let resourceIdentifier;
    if (arguments.length === 1 && isMaybeIdentifier(resource)) {
      resourceIdentifier = resource;
    } else {
      const type = normalizeModelName$1(resource);
      const normalizedId = ensureStringId(id);
      resourceIdentifier = constructResource(type, normalizedId);
    }
    let identifier = this.identifierCache.getOrCreateRecordIdentifier(resourceIdentifier);
    return this._instanceCache.getReference(identifier);
  }

  /**
    Get a record by a given type and ID without triggering a fetch.
     This method will synchronously return the record if it is available in the store,
    otherwise it will return `null`. A record is available if it has been fetched earlier, or
    pushed manually into the store.
     See [findRecord](../methods/findRecord?anchor=findRecord) if you would like to request this record from the backend.
     _Note: This is a synchronous method and does not return a promise._
     **Example 1**
     ```js
    let post = store.peekRecord('post', 1);
     post.id; // 1
    ```
     `peekRecord` can be called with a single identifier argument instead of the combination
    of `type` (modelName) and `id` as separate arguments. You may recognize this combo as
    the typical pairing from [JSON:API](https://jsonapi.org/format/#document-resource-object-identification)
     **Example 2**
     ```js
    let post = store.peekRecord({ type: 'post', id });
    post.id; // 1
    ```
     If you have previously received an lid from an Identifier for this record, you can lookup the record again using
    just the lid.
     **Example 3**
     ```js
    let post = store.peekRecord({ lid });
    post.id; // 1
    ```
      @since 1.13.0
    @method peekRecord
    @public
    @param {String|object} modelName - either a string representing the modelName or a ResourceIdentifier object containing both the type (a string) and the id (a string) for the record or an lid (a string) of an existing record
    @param {String|Integer} id - optional only if the first param is a ResourceIdentifier, else the string id of the record to be retrieved.
    @return {Model|null} record
  */

  peekRecord(identifier, id) {
    if (arguments.length === 1 && isMaybeIdentifier(identifier)) {
      const stableIdentifier = this.identifierCache.peekRecordIdentifier(identifier);
      const isLoaded = stableIdentifier && this._instanceCache.recordIsLoaded(stableIdentifier);
      // TODO come up with a better mechanism for determining if we have data and could peek.
      // this is basically an "are we not empty" query.
      return isLoaded ? this._instanceCache.getRecord(stableIdentifier) : null;
    }
    if (macroCondition(isDevelopingApp())) {
      assertDestroyingStore(this, 'peekRecord');
    }
    const type = normalizeModelName$1(identifier);
    const normalizedId = ensureStringId(id);
    const resource = {
      type,
      id: normalizedId
    };
    const stableIdentifier = this.identifierCache.peekRecordIdentifier(resource);
    const isLoaded = stableIdentifier && this._instanceCache.recordIsLoaded(stableIdentifier);
    return isLoaded ? this._instanceCache.getRecord(stableIdentifier) : null;
  }

  /**
   This method returns true if a record for a given modelName and id is already
   loaded in the store. Use this function to know beforehand if a findRecord()
   will result in a request or that it will be a cache hit.
    Example
    ```javascript
   store.hasRecordForId('post', 1); // false
   store.findRecord('post', 1).then(function() {
     store.hasRecordForId('post', 1); // true
   });
   ```
     @method hasRecordForId
    @deprecated
    @public
    @param {String} modelName
    @param {(String|Integer)} id
    @return {Boolean}
  */
  hasRecordForId(modelName, id) {
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_HAS_RECORD)) {
      if (macroCondition(isDevelopingApp())) {
        assertDestroyingStore(this, 'hasRecordForId');
      }
      const type = normalizeModelName$1(modelName);
      const trueId = ensureStringId(id);
      const resource = {
        type,
        id: trueId
      };
      const identifier = this.identifierCache.peekRecordIdentifier(resource);
      return Boolean(identifier && this._instanceCache.recordIsLoaded(identifier));
    }
  }

  /**
    This method delegates a query to the adapter. This is the one place where
    adapter-level semantics are exposed to the application.
     Each time this method is called a new request is made through the adapter.
     Exposing queries this way seems preferable to creating an abstract query
    language for all server-side queries, and then require all adapters to
    implement them.
     ---
     If you do something like this:
     ```javascript
    store.query('person', { page: 1 });
    ```
     The request made to the server will look something like this:
     ```
    GET "/api/v1/person?page=1"
    ```
     ---
     If you do something like this:
     ```javascript
    store.query('person', { ids: [1, 2, 3] });
    ```
     The request made to the server will look something like this:
     ```
    GET "/api/v1/person?ids%5B%5D=1&ids%5B%5D=2&ids%5B%5D=3"
    decoded: "/api/v1/person?ids[]=1&ids[]=2&ids[]=3"
    ```
     This method returns a promise, which is resolved with a
    [`Collection`](/ember-data/release/classes/Collection)
    once the server returns.
     @since 1.13.0
    @method query
    @public
    @param {String} modelName
    @param {any} query an opaque query to be used by the adapter
    @param {Object} options optional, may include `adapterOptions` hash which will be passed to adapter.query
    @return {Promise} promise
  */
  query(modelName, query, options) {
    if (macroCondition(isDevelopingApp())) {
      assertDestroyingStore(this, 'query');
    }
    let adapterOptionsWrapper = {};
    if (options && options.adapterOptions) {
      adapterOptionsWrapper.adapterOptions = options.adapterOptions;
    }
    let recordArray = options?._recordArray || null;
    let normalizedModelName = normalizeModelName$1(modelName);
    let adapter = this.adapterFor(normalizedModelName);
    let queryPromise = _query(adapter, this, normalizedModelName, query, recordArray, adapterOptionsWrapper);
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_PROMISE_PROXIES)) {
      return promiseArray(queryPromise);
    }
    return queryPromise;
  }

  /**
    This method makes a request for one record, where the `id` is not known
    beforehand (if the `id` is known, use [`findRecord`](../methods/findRecord?anchor=findRecord)
    instead).
     This method can be used when it is certain that the server will return a
    single object for the primary data.
     Each time this method is called a new request is made through the adapter.
     Let's assume our API provides an endpoint for the currently logged in user
    via:
     ```
    // GET /api/current_user
    {
      user: {
        id: 1234,
        username: 'admin'
      }
    }
    ```
     Since the specific `id` of the `user` is not known beforehand, we can use
    `queryRecord` to get the user:
     ```javascript
    store.queryRecord('user', {}).then(function(user) {
      let username = user.username;
      // do thing
    });
    ```
     The request is made through the adapters' `queryRecord`:
     ```app/adapters/user.js
    import Adapter from '@ember-data/adapter';
    import $ from 'jquery';
     export default class UserAdapter extends Adapter {
      queryRecord(modelName, query) {
        return $.getJSON('/api/current_user');
      }
    }
    ```
     Note: the primary use case for `store.queryRecord` is when a single record
    is queried and the `id` is not known beforehand. In all other cases
    `store.query` and using the first item of the array is likely the preferred
    way:
     ```
    // GET /users?username=unique
    {
      data: [{
        id: 1234,
        type: 'user',
        attributes: {
          username: "unique"
        }
      }]
    }
    ```
     ```javascript
    store.query('user', { username: 'unique' }).then(function(users) {
      return users.firstObject;
    }).then(function(user) {
      let id = user.id;
    });
    ```
     This method returns a promise, which resolves with the found record.
     If the adapter returns no data for the primary data of the payload, then
    `queryRecord` resolves with `null`:
     ```
    // GET /users?username=unique
    {
      data: null
    }
    ```
     ```javascript
    store.queryRecord('user', { username: 'unique' }).then(function(user) {
       // user is null
    });
    ```
     @since 1.13.0
    @method queryRecord
    @public
    @param {String} modelName
    @param {any} query an opaque query to be used by the adapter
    @param {Object} options optional, may include `adapterOptions` hash which will be passed to adapter.queryRecord
    @return {Promise} promise which resolves with the found record or `null`
  */
  queryRecord(modelName, query, options) {
    if (macroCondition(isDevelopingApp())) {
      assertDestroyingStore(this, 'queryRecord');
    }
    let normalizedModelName = normalizeModelName$1(modelName);
    let adapter = this.adapterFor(normalizedModelName);
    let adapterOptionsWrapper = {};
    if (options && options.adapterOptions) {
      adapterOptionsWrapper.adapterOptions = options.adapterOptions;
    }
    const promise = _queryRecord(adapter, this, normalizedModelName, query, adapterOptionsWrapper);
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_PROMISE_PROXIES)) {
      return promiseObject(promise.then(identifier => identifier && this.peekRecord(identifier)));
    }
    return promise.then(identifier => identifier && this.peekRecord(identifier));
  }

  /**
    `findAll` asks the adapter's `findAll` method to find the records for the
    given type, and returns a promise which will resolve with all records of
    this type present in the store, even if the adapter only returns a subset
    of them.
     ```app/routes/authors.js
    import Route from '@ember/routing/route';
     export default class AuthorsRoute extends Route {
      model(params) {
        return this.store.findAll('author');
      }
    }
    ```
     _When_ the returned promise resolves depends on the reload behavior,
    configured via the passed `options` hash and the result of the adapter's
    `shouldReloadAll` method.
     ### Reloading
     If `{ reload: true }` is passed or `adapter.shouldReloadAll` evaluates to
    `true`, then the returned promise resolves once the adapter returns data,
    regardless if there are already records in the store:
     ```js
    store.push({
      data: {
        id: 'first',
        type: 'author'
      }
    });
     // adapter#findAll resolves with
    // [
    //   {
    //     id: 'second',
    //     type: 'author'
    //   }
    // ]
    store.findAll('author', { reload: true }).then(function(authors) {
      authors.getEach('id'); // ['first', 'second']
    });
    ```
     If no reload is indicated via the above mentioned ways, then the promise
    immediately resolves with all the records currently loaded in the store.
     ### Background Reloading
     Optionally, if `adapter.shouldBackgroundReloadAll` evaluates to `true`,
    then a background reload is started. Once this resolves, the array with
    which the promise resolves, is updated automatically so it contains all the
    records in the store:
     ```app/adapters/application.js
    import Adapter from '@ember-data/adapter';
     export default class ApplicationAdapter extends Adapter {
      shouldReloadAll(store, snapshotsArray) {
        return false;
      },
       shouldBackgroundReloadAll(store, snapshotsArray) {
        return true;
      }
    });
     // ...
     store.push({
      data: {
        id: 'first',
        type: 'author'
      }
    });
     let allAuthors;
    store.findAll('author').then(function(authors) {
      authors.getEach('id'); // ['first']
       allAuthors = authors;
    });
     // later, once adapter#findAll resolved with
    // [
    //   {
    //     id: 'second',
    //     type: 'author'
    //   }
    // ]
     allAuthors.getEach('id'); // ['first', 'second']
    ```
     If you would like to force or prevent background reloading, you can set a
    boolean value for `backgroundReload` in the options object for
    `findAll`.
     ```app/routes/post/edit.js
    import Route from '@ember/routing/route';
     export default class PostEditRoute extends Route {
      model() {
        return this.store.findAll('post', { backgroundReload: false });
      }
    }
    ```
     If you pass an object on the `adapterOptions` property of the options
    argument it will be passed to you adapter via the `snapshotRecordArray`
     ```app/routes/posts.js
    import Route from '@ember/routing/route';
     export default class PostsRoute extends Route {
      model(params) {
        return this.store.findAll('post', {
          adapterOptions: { subscribe: false }
        });
      }
    }
    ```
     ```app/adapters/post.js
    import MyCustomAdapter from './custom-adapter';
     export default class UserAdapter extends MyCustomAdapter {
      findAll(store, type, sinceToken, snapshotRecordArray) {
        if (snapshotRecordArray.adapterOptions.subscribe) {
          // ...
        }
        // ...
      }
    }
    ```
     See [peekAll](../methods/peekAll?anchor=peekAll) to get an array of current records in the
    store, without waiting until a reload is finished.
     ### Retrieving Related Model Records
     If you use an adapter such as Ember's default
    [`JSONAPIAdapter`](/ember-data/release/classes/JSONAPIAdapter)
    that supports the [JSON API specification](http://jsonapi.org/) and if your server
    endpoint supports the use of an
    ['include' query parameter](http://jsonapi.org/format/#fetching-includes),
    you can use `findAll()` to automatically retrieve additional records related to
    those requested by supplying an `include` parameter in the `options` object.
     For example, given a `post` model that has a `hasMany` relationship with a `comment`
    model, when we retrieve all of the post records we can have the server also return
    all of the posts' comments in the same request:
     ```app/routes/posts.js
    import Route from '@ember/routing/route';
     export default class PostsRoute extends Route {
      model() {
        return this.store.findAll('post', { include: 'comments' });
      }
    }
    ```
    Multiple relationships can be requested using an `include` parameter consisting of a
    comma-separated list (without white-space) while nested relationships can be specified
    using a dot-separated sequence of relationship names. So to request both the posts'
    comments and the authors of those comments the request would look like this:
     ```app/routes/posts.js
    import Route from '@ember/routing/route';
     export default class PostsRoute extends Route {
      model() {
        return this.store.findAll('post', { include: 'comments,comments.author' });
      }
    }
    ```
     See [query](../methods/query?anchor=query) to only get a subset of records from the server.
     @since 1.13.0
    @method findAll
    @public
    @param {String} modelName
    @param {Object} options
    @return {Promise} promise
  */
  findAll(modelName, options) {
    if (options === void 0) {
      options = {};
    }
    if (macroCondition(isDevelopingApp())) {
      assertDestroyingStore(this, 'findAll');
    }
    let normalizedModelName = normalizeModelName$1(modelName);
    let array = this.peekAll(normalizedModelName);
    let fetch;
    let adapter = this.adapterFor(normalizedModelName);
    if (options.reload) {
      array.isUpdating = true;
      fetch = _findAll(adapter, this, normalizedModelName, options);
    } else {
      let snapshotArray = new SnapshotRecordArray(this, array, options);
      if (options.reload !== false) {
        if (adapter.shouldReloadAll && adapter.shouldReloadAll(this, snapshotArray) || !adapter.shouldReloadAll && snapshotArray.length === 0) {
          array.isUpdating = true;
          fetch = _findAll(adapter, this, modelName, options, snapshotArray);
        }
      }
      if (!fetch) {
        if (options.backgroundReload === false) {
          fetch = resolve(array);
        } else if (options.backgroundReload || !adapter.shouldBackgroundReloadAll || adapter.shouldBackgroundReloadAll(this, snapshotArray)) {
          array.isUpdating = true;
          _findAll(adapter, this, modelName, options, snapshotArray);
        }
        fetch = resolve(array);
      }
    }
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_PROMISE_PROXIES)) {
      return promiseArray(fetch);
    }
    return fetch;
  }

  /**
    This method returns a filtered array that contains all of the
    known records for a given type in the store.
     Note that because it's just a filter, the result will contain any
    locally created records of the type, however, it will not make a
    request to the backend to retrieve additional records. If you
    would like to request all the records from the backend please use
    [store.findAll](../methods/findAll?anchor=findAll).
     Also note that multiple calls to `peekAll` for a given type will always
    return the same `RecordArray`.
     Example
     ```javascript
    let localPosts = store.peekAll('post');
    ```
     @since 1.13.0
    @method peekAll
    @public
    @param {String} modelName
    @return {RecordArray}
  */
  peekAll(modelName) {
    if (macroCondition(isDevelopingApp())) {
      assertDestroyingStore(this, 'peekAll');
    }
    let type = normalizeModelName$1(modelName);
    return this.recordArrayManager.liveArrayFor(type);
  }

  /**
    This method unloads all records in the store.
    It schedules unloading to happen during the next run loop.
     Optionally you can pass a type which unload all records for a given type.
     ```javascript
    store.unloadAll();
    store.unloadAll('post');
    ```
     @method unloadAll
    @public
    @param {String} modelName
  */
  unloadAll(modelName) {
    if (macroCondition(isDevelopingApp())) {
      assertDestroyedStoreOnly(this, 'unloadAll');
    }
    this._join(() => {
      if (modelName === undefined) {
        // destroy the graph before unloadAll
        // since then we avoid churning relationships
        // during unload
        if (macroCondition(dependencySatisfies("@ember-data/record-data", "*"))) {
          const peekGraph = importSync('@ember-data/record-data/-private').peekGraph;
          let graph = peekGraph(this);
          if (graph) {
            graph.identifiers.clear();
          }
        }
        this._notificationManager.destroy();
        this.recordArrayManager.clear();
        this._instanceCache.clear();
      } else {
        let normalizedModelName = normalizeModelName$1(modelName);
        this._instanceCache.clear(normalizedModelName);
      }
    });
  }

  /**
    Push some data for a given type into the store.
     This method expects normalized [JSON API](http://jsonapi.org/) document. This means you have to follow [JSON API specification](http://jsonapi.org/format/) with few minor adjustments:
    - record's `type` should always be in singular, dasherized form
    - members (properties) should be camelCased
     [Your primary data should be wrapped inside `data` property](http://jsonapi.org/format/#document-top-level):
     ```js
    store.push({
      data: {
        // primary data for single record of type `Person`
        id: '1',
        type: 'person',
        attributes: {
          firstName: 'Daniel',
          lastName: 'Kmak'
        }
      }
    });
    ```
     [Demo.](http://ember-twiddle.com/fb99f18cd3b4d3e2a4c7)
     `data` property can also hold an array (of records):
     ```js
    store.push({
      data: [
        // an array of records
        {
          id: '1',
          type: 'person',
          attributes: {
            firstName: 'Daniel',
            lastName: 'Kmak'
          }
        },
        {
          id: '2',
          type: 'person',
          attributes: {
            firstName: 'Tom',
            lastName: 'Dale'
          }
        }
      ]
    });
    ```
     [Demo.](http://ember-twiddle.com/69cdbeaa3702159dc355)
     There are some typical properties for `JSONAPI` payload:
    * `id` - mandatory, unique record's key
    * `type` - mandatory string which matches `model`'s dasherized name in singular form
    * `attributes` - object which holds data for record attributes - `attr`'s declared in model
    * `relationships` - object which must contain any of the following properties under each relationships' respective key (example path is `relationships.achievements.data`):
      - [`links`](http://jsonapi.org/format/#document-links)
      - [`data`](http://jsonapi.org/format/#document-resource-object-linkage) - place for primary data
      - [`meta`](http://jsonapi.org/format/#document-meta) - object which contains meta-information about relationship
     For this model:
     ```app/models/person.js
    import Model, { attr, hasMany } from '@ember-data/model';
     export default class PersonRoute extends Route {
      @attr('string') firstName;
      @attr('string') lastName;
       @hasMany('person') children;
    }
    ```
     To represent the children as IDs:
     ```js
    {
      data: {
        id: '1',
        type: 'person',
        attributes: {
          firstName: 'Tom',
          lastName: 'Dale'
        },
        relationships: {
          children: {
            data: [
              {
                id: '2',
                type: 'person'
              },
              {
                id: '3',
                type: 'person'
              },
              {
                id: '4',
                type: 'person'
              }
            ]
          }
        }
      }
    }
    ```
     [Demo.](http://ember-twiddle.com/343e1735e034091f5bde)
     To represent the children relationship as a URL:
     ```js
    {
      data: {
        id: '1',
        type: 'person',
        attributes: {
          firstName: 'Tom',
          lastName: 'Dale'
        },
        relationships: {
          children: {
            links: {
              related: '/people/1/children'
            }
          }
        }
      }
    }
    ```
     If you're streaming data or implementing an adapter, make sure
    that you have converted the incoming data into this form. The
    store's [normalize](../methods/normalize?anchor=normalize) method is a convenience
    helper for converting a json payload into the form Ember Data
    expects.
     ```js
    store.push(store.normalize('person', data));
    ```
     This method can be used both to push in brand new
    records, as well as to update existing records.
     @method push
    @public
    @param {Object} data
    @return the record(s) that was created or
      updated.
  */

  push(data) {
    if (macroCondition(isDevelopingApp())) {
      assertDestroyingStore(this, 'push');
    }
    let pushed = this._push(data);
    if (Array.isArray(pushed)) {
      let records = pushed.map(identifier => this._instanceCache.getRecord(identifier));
      return records;
    }
    if (pushed === null) {
      return null;
    }
    return this._instanceCache.getRecord(pushed);
  }

  /**
    Push some data in the form of a json-api document into the store,
    without creating materialized records.
     @method _push
    @private
    @param {Object} jsonApiDoc
    @return {StableRecordIdentifier|Array<StableRecordIdentifier>} identifiers for the primary records that had data loaded
  */
  _push(jsonApiDoc) {
    if (macroCondition(isDevelopingApp())) {
      assertDestroyingStore(this, '_push');
    }
    if (macroCondition(getOwnConfig().debug.LOG_PAYLOADS)) {
      try {
        let data = JSON.parse(JSON.stringify(jsonApiDoc));
        // eslint-disable-next-line no-console
        console.log('EmberData | Payload - push', data);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log('EmberData | Payload - push', jsonApiDoc);
      }
    }
    let ret;
    this._join(() => {
      let included = jsonApiDoc.included;
      let i, length;
      if (included) {
        for (i = 0, length = included.length; i < length; i++) {
          this._instanceCache.loadData(included[i]);
        }
      }
      if (Array.isArray(jsonApiDoc.data)) {
        length = jsonApiDoc.data.length;
        let identifiers = new Array(length);
        for (i = 0; i < length; i++) {
          identifiers[i] = this._instanceCache.loadData(jsonApiDoc.data[i]);
        }
        ret = identifiers;
        return;
      }
      if (jsonApiDoc.data === null) {
        ret = null;
        return;
      }
      ret = this._instanceCache.loadData(jsonApiDoc.data);
      return;
    });
    return ret;
  }

  /**
    Push some raw data into the store.
     This method can be used both to push in brand new
    records, as well as to update existing records. You
    can push in more than one type of object at once.
    All objects should be in the format expected by the
    serializer.
     ```app/serializers/application.js
    import RESTSerializer from '@ember-data/serializer/rest';
     export default class ApplicationSerializer extends RESTSerializer;
    ```
     ```js
    let pushData = {
      posts: [
        { id: 1, postTitle: "Great post", commentIds: [2] }
      ],
      comments: [
        { id: 2, commentBody: "Insightful comment" }
      ]
    }
     store.pushPayload(pushData);
    ```
     By default, the data will be deserialized using a default
    serializer (the application serializer if it exists).
     Alternatively, `pushPayload` will accept a model type which
    will determine which serializer will process the payload.
     ```app/serializers/application.js
    import RESTSerializer from '@ember-data/serializer/rest';
      export default class ApplicationSerializer extends RESTSerializer;
    ```
     ```app/serializers/post.js
    import JSONSerializer from '@ember-data/serializer/json';
     export default JSONSerializer;
    ```
     ```js
    store.pushPayload(pushData); // Will use the application serializer
    store.pushPayload('post', pushData); // Will use the post serializer
    ```
     @method pushPayload
    @public
    @param {String} modelName Optionally, a model type used to determine which serializer will be used
    @param {Object} inputPayload
  */
  // TODO @runspired @deprecate pushPayload in favor of looking up the serializer
  pushPayload(modelName, inputPayload) {
    if (macroCondition(isDevelopingApp())) {
      assertDestroyingStore(this, 'pushPayload');
    }
    let serializer;
    let payload;
    if (!inputPayload) {
      payload = modelName;
      serializer = this.serializerFor('application');
    } else {
      payload = inputPayload;
      let normalizedModelName = normalizeModelName$1(modelName);
      serializer = this.serializerFor(normalizedModelName);
    }
    serializer.pushPayload(this, payload);
  }

  // TODO @runspired @deprecate records should implement their own serialization if desired
  serializeRecord(record, options) {
    // TODO we used to check if the record was destroyed here
    return this._instanceCache.createSnapshot(recordIdentifierFor(record)).serialize(options);
  }

  /**
   * Trigger a save for a Record.
   *
   * @method saveRecord
   * @public
   * @param {RecordInstance} record
   * @param options
   * @returns {Promise<RecordInstance>}
   */
  saveRecord(record, options) {
    if (options === void 0) {
      options = {};
    }
    let identifier = recordIdentifierFor(record);
    let recordData = identifier && this._instanceCache.peek({
      identifier,
      bucket: 'recordData'
    });
    if (!recordData) {
      // this commonly means we're disconnected
      // but just in case we reject here to prevent bad things.
      return reject(`Record Is Disconnected`);
    }
    if (recordDataIsFullyDeleted(this._instanceCache, identifier)) {
      return resolve(record);
    }
    recordData.willCommit(identifier);
    if (isDSModel(record)) {
      record.errors.clear();
    }
    if (!options) {
      options = {};
    }
    let operation = 'updateRecord';
    if (recordData.isNew(identifier)) {
      operation = 'createRecord';
    } else if (recordData.isDeleted(identifier)) {
      operation = 'deleteRecord';
    }
    const saveOptions = Object.assign({
      [SaveOp]: operation
    }, options);
    let fetchManagerPromise = this._fetchManager.scheduleSave(identifier, saveOptions);
    return fetchManagerPromise.then(payload => {
      if (macroCondition(getOwnConfig().debug.LOG_PAYLOADS)) {
        try {
          let data = payload ? JSON.parse(JSON.stringify(payload)) : payload;
          // eslint-disable-next-line no-console
          console.log(`EmberData | Payload - ${operation}`, data);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.log(`EmberData | Payload - ${operation}`, payload);
        }
      }
      /*
      // TODO @runspired re-evaluate the below claim now that
      // the save request pipeline is more streamlined.
       Note to future spelunkers hoping to optimize.
      We rely on this `run` to create a run loop if needed
      that `store._push` and `store.saveRecord` will both share.
       We use `join` because it is often the case that we
      have an outer run loop available still from the first
      call to `store._push`;
      */
      this._join(() => {
        if (macroCondition(isDevelopingApp())) {
          assertDestroyingStore(this, 'saveRecord');
        }
        let data = payload && payload.data;
        const cache = this.identifierCache;
        let actualIdentifier = identifier;
        if (operation !== 'deleteRecord' && data) {
          actualIdentifier = cache.updateRecordIdentifier(identifier, data);
        }

        //We first make sure the primary data has been updated
        const recordData = this._instanceCache.getRecordData(actualIdentifier);
        recordData.didCommit(identifier, data);
        if (operation === 'deleteRecord') {
          this.recordArrayManager.identifierRemoved(actualIdentifier);
        }
        if (payload && payload.included) {
          this._push({
            data: null,
            included: payload.included
          });
        }
      });
      return record;
    }, e => {
      let err = e;
      if (!e) {
        err = new Error(`Unknown Error Occurred During Request`);
      } else if (typeof e === 'string') {
        err = new Error(e);
      }
      adapterDidInvalidate(this, identifier, err);
      throw err;
    });
  }

  /**
   * Instantiation hook allowing applications or addons to configure the store
   * to utilize a custom RecordData implementation.
   *
   * @method createRecordDataFor (hook)
   * @public
   * @param identifier
   * @param storeWrapper
   */
  createRecordDataFor(identifier, storeWrapper) {
    if (macroCondition(dependencySatisfies("@ember-data/record-data", "*"))) {
      // we can't greedily use require as this causes
      // a cycle we can't easily fix (or clearly pin point) at present.
      //
      // it can be reproduced in partner tests by running
      // node ./scripts/packages-for-commit.js && pnpm test-external:ember-observer
      if (_RecordData === undefined) {
        _RecordData = importSync('@ember-data/record-data/-private').RecordData;
      }
      if (macroCondition(getOwnConfig().deprecations.DEPRECATE_V1CACHE_STORE_APIS)) {
        if (arguments.length === 4) {
          identifier = this.identifierCache.getOrCreateRecordIdentifier({
            type: arguments[0],
            id: arguments[1],
            lid: arguments[2]
          });
          storeWrapper = arguments[3];
        }
      }
      this.__private_singleton_recordData = this.__private_singleton_recordData || new _RecordData(storeWrapper);
      this.__private_singleton_recordData.createCache(identifier);
      return this.__private_singleton_recordData;
    }
  }

  /**
    `normalize` converts a json payload into the normalized form that
    [push](../methods/push?anchor=push) expects.
     Example
     ```js
    socket.on('message', function(message) {
      let modelName = message.model;
      let data = message.data;
      store.push(store.normalize(modelName, data));
    });
    ```
     @method normalize
    @public
    @param {String} modelName The name of the model type for this payload
    @param {Object} payload
    @return {Object} The normalized payload
  */
  // TODO @runspired @deprecate users should call normalize on the associated serializer directly
  normalize(modelName, payload) {
    if (macroCondition(isDevelopingApp())) {
      assertDestroyingStore(this, 'normalize');
    }
    let normalizedModelName = normalizeModelName$1(modelName);
    let serializer = this.serializerFor(normalizedModelName);
    let model = this.modelFor(normalizedModelName);
    return serializer.normalize(model, payload);
  }

  /**
    Returns an instance of the adapter for a given type. For
    example, `adapterFor('person')` will return an instance of
    the adapter located at `app/adapters/person.js`
     If no `person` adapter is found, this method will look
    for an `application` adapter (the default adapter for
    your entire application).
     @method adapterFor
    @public
    @param {String} modelName
    @return Adapter
  */
  adapterFor(modelName) {
    if (macroCondition(isDevelopingApp())) {
      assertDestroyingStore(this, 'adapterFor');
    }
    let normalizedModelName = normalizeModelName$1(modelName);
    let {
      _adapterCache
    } = this;
    let adapter = _adapterCache[normalizedModelName];
    if (adapter) {
      return adapter;
    }
    let owner = getOwner(this);

    // name specific adapter
    adapter = owner.lookup(`adapter:${normalizedModelName}`);
    if (adapter !== undefined) {
      _adapterCache[normalizedModelName] = adapter;
      return adapter;
    }

    // no adapter found for the specific name, fallback and check for application adapter
    adapter = _adapterCache.application || owner.lookup('adapter:application');
    if (adapter !== undefined) {
      _adapterCache[normalizedModelName] = adapter;
      _adapterCache.application = adapter;
      return adapter;
    }
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_JSON_API_FALLBACK)) {
      // final fallback, no model specific adapter, no application adapter, no
      // `adapter` property on store: use json-api adapter
      adapter = _adapterCache['-json-api'] || owner.lookup('adapter:-json-api');
      if (adapter !== undefined) {
        _adapterCache[normalizedModelName] = adapter;
        _adapterCache['-json-api'] = adapter;
        return adapter;
      }
    }
  }

  /**
    Returns an instance of the serializer for a given type. For
    example, `serializerFor('person')` will return an instance of
    `App.PersonSerializer`.
     If no `App.PersonSerializer` is found, this method will look
    for an `App.ApplicationSerializer` (the default serializer for
    your entire application).
     If a serializer cannot be found on the adapter, it will fall back
    to an instance of `JSONSerializer`.
     @method serializerFor
    @public
    @param {String} modelName the record to serialize
    @return {Serializer}
  */
  serializerFor(modelName) {
    if (macroCondition(isDevelopingApp())) {
      assertDestroyingStore(this, 'serializerFor');
    }
    let normalizedModelName = normalizeModelName$1(modelName);
    let {
      _serializerCache
    } = this;
    let serializer = _serializerCache[normalizedModelName];
    if (serializer) {
      return serializer;
    }
    let owner = getOwner(this);

    // by name
    serializer = owner.lookup(`serializer:${normalizedModelName}`);
    if (serializer !== undefined) {
      _serializerCache[normalizedModelName] = serializer;
      return serializer;
    }

    // no serializer found for the specific model, fallback and check for application serializer
    serializer = _serializerCache.application || owner.lookup('serializer:application');
    if (serializer !== undefined) {
      _serializerCache[normalizedModelName] = serializer;
      _serializerCache.application = serializer;
      return serializer;
    }
    return null;
  }
  destroy() {
    // enqueue destruction of any adapters/serializers we have created
    for (let adapterName in this._adapterCache) {
      let adapter = this._adapterCache[adapterName];
      if (typeof adapter.destroy === 'function') {
        adapter.destroy();
      }
    }
    for (let serializerName in this._serializerCache) {
      let serializer = this._serializerCache[serializerName];
      if (typeof serializer.destroy === 'function') {
        serializer.destroy();
      }
    }
    if (macroCondition(dependencySatisfies("@ember-data/record-data", "*"))) {
      const peekGraph = importSync('@ember-data/record-data/-private').peekGraph;
      let graph = peekGraph(this);
      if (graph) {
        graph.destroy();
      }
    }
    return super.destroy();
  }
  willDestroy() {
    super.willDestroy();
    this.recordArrayManager.destroy();
    this.identifierCache.destroy();
    this.unloadAll();
    if (macroCondition(isDevelopingApp())) {
      unregisterWaiter(this.__asyncWaiter);
      let tracked = this._trackedAsyncRequests;
      let isSettled = tracked.length === 0;
      if (!isSettled) {
        throw new Error('Async Request leaks detected. Add a breakpoint here and set `store.generateStackTracesForTrackedRequests = true;`to inspect traces for leak origins:\n\t - ' + tracked.map(o => o.label).join('\n\t - '));
      }
    }
  }
}
let assertDestroyingStore;
let assertDestroyedStoreOnly;
if (macroCondition(isDevelopingApp())) {
  assertDestroyingStore = function assertDestroyedStore(store, method) {};
  assertDestroyedStoreOnly = function assertDestroyedStoreOnly(store, method) {};
}
function isMaybeIdentifier(maybeIdentifier) {
  return Boolean(maybeIdentifier !== null && typeof maybeIdentifier === 'object' && ('id' in maybeIdentifier && 'type' in maybeIdentifier && maybeIdentifier.id && maybeIdentifier.type || maybeIdentifier.lid));
}
function isDSModel(record) {
  if (macroCondition(!dependencySatisfies("@ember-data/model", "*"))) {
    return false;
  }
  return !!record && 'constructor' in record && 'isModel' in record.constructor && record.constructor.isModel === true;
}
function adapterDidInvalidate(store, identifier, error) {
  if (error && error.isAdapterError === true && error.code === 'InvalidError') {
    let serializer = store.serializerFor(identifier.type);

    // TODO @deprecate extractErrors being called
    // TODO remove extractErrors from the default serializers.
    if (serializer && typeof serializer.extractErrors === 'function') {
      let errorsHash = serializer.extractErrors(store, store.modelFor(identifier.type), error, identifier.id);
      error.errors = errorsHashToArray(errorsHash);
    }
  }
  const recordData = store._instanceCache.getRecordData(identifier);
  if (error.errors) {
    let jsonApiErrors = error.errors;
    if (jsonApiErrors.length === 0) {
      jsonApiErrors = [{
        title: 'Invalid Error',
        detail: '',
        source: {
          pointer: '/data'
        }
      }];
    }
    recordData.commitWasRejected(identifier, jsonApiErrors);
  } else {
    recordData.commitWasRejected(identifier);
  }
}
function makeArray(value) {
  return Array.isArray(value) ? value : [value];
}
const PRIMARY_ATTRIBUTE_KEY = 'base';
function errorsHashToArray(errors) {
  const out = [];
  if (errors) {
    Object.keys(errors).forEach(key => {
      let messages = makeArray(errors[key]);
      for (let i = 0; i < messages.length; i++) {
        let title = 'Invalid Attribute';
        let pointer = `/data/attributes/${key}`;
        if (key === PRIMARY_ATTRIBUTE_KEY) {
          title = 'Invalid Document';
          pointer = `/data`;
        }
        out.push({
          title: title,
          detail: messages[i],
          source: {
            pointer: pointer
          }
        });
      }
    });
  }
  return out;
}
function normalizeProperties(store, identifier, properties, isForV1) {
  if (isForV1 === void 0) {
    isForV1 = false;
  }
  // assert here
  if (properties !== undefined) {
    const {
      type
    } = identifier;

    // convert relationship Records to RecordDatas before passing to RecordData
    let defs = store.getSchemaDefinitionService().relationshipsDefinitionFor({
      type
    });
    if (defs !== null) {
      let keys = Object.keys(properties);
      let relationshipValue;
      for (let i = 0; i < keys.length; i++) {
        let prop = keys[i];
        let def = defs[prop];
        if (def !== undefined) {
          if (def.kind === 'hasMany') {
            if (macroCondition(isDevelopingApp())) {
              assertRecordsPassedToHasMany(properties[prop]);
            }
            relationshipValue = extractIdentifiersFromRecords(properties[prop], isForV1);
          } else {
            relationshipValue = extractIdentifierFromRecord(properties[prop], isForV1);
          }
          properties[prop] = relationshipValue;
        }
      }
    }
  }
  return properties;
}
function assertRecordsPassedToHasMany(records) {}
function extractIdentifiersFromRecords(records, isForV1) {
  if (isForV1 === void 0) {
    isForV1 = false;
  }
  return records.map(record => extractIdentifierFromRecord(record, isForV1));
}
function extractIdentifierFromRecord(recordOrPromiseRecord, isForV1) {
  if (isForV1 === void 0) {
    isForV1 = false;
  }
  if (!recordOrPromiseRecord) {
    return null;
  }
  const extract = isForV1 ? recordDataFor : recordIdentifierFor;
  if (macroCondition(getOwnConfig().deprecations.DEPRECATE_PROMISE_PROXIES)) {
    if (isPromiseRecord(recordOrPromiseRecord)) {
      let content = recordOrPromiseRecord.content;
      return content ? extract(content) : null;
    }
  }
  return extract(recordOrPromiseRecord);
}
function isPromiseRecord(record) {
  return !!record.then;
}
function secretInit(record, recordData, identifier, store) {
  setRecordIdentifier(record, identifier);
  StoreMap.set(record, store);
  setRecordDataFor(record, recordData);
}
function normalizeModelName(modelName) {
  if (macroCondition(getOwnConfig().deprecations.DEPRECATE_HELPERS)) {
    return normalizeModelName$1(modelName);
  }
}
export { Collection as AdapterPopulatedRecordArray, IDENTIFIER_ARRAY_TAG, IdentifierArray, MUTATE, IdentifierArray as RecordArray, RecordArrayManager, SOURCE, Snapshot, SnapshotRecordArray, Store, _clearCaches, coerceId, fastPush, isStableIdentifier, normalizeModelName, notifyArray, recordDataFor, recordIdentifierFor, removeRecordDataFor, setIdentifierForgetMethod, setIdentifierGenerationMethod, setIdentifierResetMethod, setIdentifierUpdateMethod, storeFor };